import { LitElement, css, html, svg } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { SseClient } from '../services/sse-client';

/** Markdown → HTML SEGURO pras respostas do Conect. Escapa o HTML do texto ANTES
 * de inserir as tags de formatação, então conteúdo do LLM/dados nunca injeta tags
 * (sem XSS). Suporta: títulos (#), negrito (**), itálico (*), código (`), listas. */
function _escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function _mdInline(s: string): string {
  return s
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/`([^`]+?)`/g, '<code>$1</code>');
}

function mdToHtml(raw: string): string {
  const lines = _escHtml(raw).split('\n');
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  const closeList = (): void => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };
  for (const line of lines) {
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    const h = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (ul) {
      if (list !== 'ul') {
        closeList();
        out.push('<ul>');
        list = 'ul';
      }
      out.push(`<li>${_mdInline(ul[1])}</li>`);
    } else if (ol) {
      if (list !== 'ol') {
        closeList();
        out.push('<ol>');
        list = 'ol';
      }
      out.push(`<li>${_mdInline(ol[1])}</li>`);
    } else if (h) {
      closeList();
      out.push(`<div class="gmd-h">${_mdInline(h[1])}</div>`);
    } else if (line.trim() === '') {
      closeList();
    } else {
      closeList();
      out.push(`<div>${_mdInline(line)}</div>`);
    }
  }
  closeList();
  return out.join('');
}

// White-label: cards reordenáveis/ocultáveis + fontes permitidas (key→família CSS).
// Ordem canônica AGRUPADA (espelha CARD_KEYS do backend/theme.py): Engajamento →
// Consumo Pedagógico → Tutoria IA → Segurança.
const CARD_KEYS = [
  'kpis',
  'estudantes_ativos',
  'materias',
  'tematicas',
  'serie_tematicas',
  'emergentes',
  'classificacao',
  'serie_temporal',
  'etapa',
  'uso_etapa',
  'periodo_letivo',
  'seguranca',
];
// Cabeçalhos de seção do painel (blocos da proposta OlharSaber). Cada um ancora
// no PRIMEIRO card visível do grupo (via CSS order) — some se o grupo inteiro
// estiver oculto/fora da aba ativa. KPIs+Insights ficam acima, sem cabeçalho.
const CARD_SECTIONS: { label: string; keys: string[] }[] = [
  { label: 'Engajamento', keys: ['estudantes_ativos'] },
  {
    label: 'Aprendizagem e dúvidas',
    keys: ['materias', 'tematicas', 'serie_tematicas', 'emergentes', 'classificacao'],
  },
  {
    label: 'Uso da Tutoria IA',
    keys: ['serie_temporal', 'etapa', 'uso_etapa', 'periodo_letivo'],
  },
  { label: 'Governança e Segurança Educacional', keys: ['seguranca'] },
];
// Agrupamento do editor de Abas (Personalizar → Abas): espelha os grupos do painel
// pra ficar claro o que está dentro de cada um. "Indicadores" (KPIs) fica no topo
// (no painel é headline sem cabeçalho); o resto segue o CARD_SECTIONS. Cobre os 12
// cards de CARD_KEYS.
const ABAS_GROUPS: { label: string; keys: string[] }[] = [
  { label: 'Indicadores', keys: ['kpis'] },
  ...CARD_SECTIONS,
];

// Legendas explicativas (tooltip `title`) por bloco de card — mostradas no ⓘ do
// cabeçalho. Texto factual e curto: o que o indicador mede e de onde vem.
const CARD_HELP: Record<string, string> = {
  kpis: 'Indicadores-chave do período e do recorte selecionados (instituição, filtros e datas). Clique em cada número para ver a lista por trás dele.',
  estudantes_ativos:
    'Estudantes que usaram a Tutoria IA no período, com a tendência em relação ao período anterior.',
  materias:
    'Disciplinas que os estudantes mais consultaram na Tutoria IA, no período. Classificação feita pela IA a partir das perguntas.',
  tematicas:
    'Assuntos e tópicos recorrentes nas conversas dos estudantes — o que eles mais estão estudando ou tendo dúvida.',
  serie_tematicas:
    'Assuntos que estão em alta no período em comparação ao anterior (temas emergentes).',
  emergentes:
    'Novos assuntos que passaram a aparecer nas conversas e ainda não eram frequentes.',
  classificacao:
    'Como as interações se distribuem: com matéria identificada, sem matéria cadastrada, fora de conteúdo (pessoal/administrativo) ou aguardando processamento da IA.',
  serie_temporal: 'Evolução dos indicadores ao longo do tempo, dentro do período.',
  etapa:
    'Distribuição dos estudantes pelas etapas de ensino. Dados de matrícula vindos da carga do Moodle.',
  uso_etapa: 'Intensidade de uso da Tutoria IA por etapa de ensino, no período.',
  periodo_letivo:
    'Recorte por período letivo configurado (bimestre/trimestre). Defina em Personalizar → Períodos letivos.',
  seguranca:
    'Interações barradas pela Segurança Educacional e o percentual de perguntas fora do escopo de estudo. Clique para ver os casos.',
};

// Legendas dos 6 indicadores do bloco de KPIs (tooltip no card do número).
const STAT_HELP: Record<string, string> = {
  stat_matriculados:
    'Total de estudantes vinculados à instituição pela carga do Moodle. "Pendente de carga" = ainda sem perfil sincronizado.',
  stat_ativos:
    'Estudantes que usaram a Tutoria IA ao menos uma vez no período. A seta compara com o período anterior.',
  stat_inativos:
    'Estudantes vinculados que NÃO usaram a Tutoria IA no período — candidatos a incentivo de uso.',
  stat_sessoes:
    'Conversas de tutoria iniciadas no período. O subtítulo traz tempo médio, tempo total e recorrência por aluno quando disponíveis.',
  stat_perguntas:
    'Mensagens enviadas pelos estudantes à Tutoria IA no período. "por sessão" é a média de perguntas em cada conversa.',
  stat_barrados:
    'Interações barradas pela Segurança Educacional (conteúdo sensível/impróprio). A seta invertida: menos é melhor.',
};
// Rótulos dos blocos no editor de Personalizar — acompanham a nomenclatura do painel.
const CARD_LABELS: Record<string, string> = {
  kpis: 'Indicadores',
  materias: 'Disciplinas mais consultadas',
  tematicas: 'Temas e dúvidas mais recorrentes',
  emergentes: 'Temas em crescimento',
  serie_temporal: 'Evolução ao longo do tempo',
  serie_tematicas: 'Evolução dos temas consultados',
  periodo_letivo: 'Uso por período letivo',
  classificacao: 'Classificação das interações',
  etapa: 'Estudantes por etapa',
  uso_etapa: 'Uso da Tutoria por etapa/ano',
  estudantes_ativos: 'Estudantes com maior utilização',
  seguranca: 'Governança e Segurança Educacional',
};
// Os 6 indicadores do bloco KPIs, cada um com sua própria chave de tema — assim
// dá pra colorir cada card individualmente (sobrepõe o estilo do bloco 'kpis').
const KPI_STATS = [
  { key: 'stat_matriculados', label: 'Estudantes vinculados' },
  { key: 'stat_ativos', label: 'Estudantes ativos' },
  { key: 'stat_inativos', label: 'Estudantes com baixa utilização recente' },
  { key: 'stat_sessoes', label: 'Sessões de tutoria' },
  { key: 'stat_perguntas', label: 'Interações com a Tutoria IA' },
  { key: 'stat_barrados', label: 'Alertas de Segurança Educacional' },
];
const FONT_MAP: Record<string, string> = {
  system: 'system-ui, -apple-system, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, "Courier New", monospace',
  condensed: '"Arial Narrow", "Roboto Condensed", system-ui, sans-serif',
};
const FONT_LABELS: Record<string, string> = {
  system: 'Padrão',
  serif: 'Serifada',
  mono: 'Monoespaçada',
  condensed: 'Condensada',
};
const CHART_TYPE_LABELS: Record<string, string> = {
  bar: 'Barra',
  column: 'Coluna',
  line: 'Linha',
  area: 'Área',
  pie: 'Pizza',
  donut: 'Rosca',
  stacked: 'Empilhada',
  gauge: 'Medidor',
  radar: 'Radar',
};
// Ordem canônica dos tipos (usada no editor e no default liberado).
const ALL_CHART_TYPES = [
  'bar', 'column', 'line', 'area', 'pie', 'donut', 'stacked', 'gauge', 'radar',
];
const CHART_PALETTE = [
  '#378add', '#1d9e75', '#ba7517', '#c0432f', '#7b61ff', '#0ea5a5', '#d6499b',
];
// Seções do relatório do período (keys casam com insights_report.SECTIONS no backend).
// Seções do relatório — MESMOS nomes dos blocos/cards do painel (consistência
// painel↔relatório). Precisa casar com insights_report.SECTIONS (títulos do PDF).
const REPORT_SECTIONS: { key: string; label: string }[] = [
  { key: 'resumo', label: 'Insights' },
  { key: 'indicadores', label: 'Indicadores' },
  { key: 'materias', label: 'Disciplinas mais consultadas' },
  { key: 'uso_etapa', label: 'Uso da Tutoria por etapa/ano' },
  { key: 'classificacao', label: 'Classificação das interações' },
  { key: 'seguranca', label: 'Governança e Segurança Educacional' },
  { key: 'recomendacoes', label: 'Recomendações' },
];
const CHART_MODE_LABELS: Record<string, string> = {
  auto: 'Automático (quando faz sentido)',
  always: 'Sempre que possível',
  off: 'Desligado',
};
const DEFAULT_CHARTS = { mode: 'auto', types: [...ALL_CHART_TYPES] };
// Emojis curados p/ ícone de card (dashboard educacional). Grade do seletor.
const CARD_EMOJIS = [
  '📊', '📈', '📉', '📚', '📖', '🎓', '🏫', '👥', '👩‍🏫', '🧑‍🎓',
  '🛡️', '⚠️', '✅', '🔔', '💬', '🧮', '🔬', '🌍', '✏️', '📝',
  '🎯', '⭐', '🏆', '🚀', '💡', '📌', '🔍', '📅', '⏱️', '🔒',
];

/** Contraste WCAG (aprox.) entre 2 cores hex — < 3 é ruim. */
function contrastRatio(a: string, b: string): number {
  const lum = (c: string): number => {
    const h = c.replace('#', '');
    if (h.length !== 6) return 1;
    const ch = [0, 2, 4].map((i) => {
      const v = parseInt(h.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  };
  const l1 = lum(a);
  const l2 = lum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

interface ThemeData {
  bg: string;
  card: string;
  accent: string;
  text: string;
  muted: string;
  border: string;
  hover: string;
  font: string;
  radius: number;
  pad: number;
  layout: string[];
  hidden: string[];
  logo_url: string;
  nome: string;
  titulo: string;
  subtitulo: string;
  charts: { mode: string; types: string[] };
  cards: Record<string, CardCfg>;
  tabs: TabCfg[];
  // Fase 4 — períodos letivos da instituição (bimestre/semestre/etc), datas YYYY-MM-DD.
  periodos_letivos?: { nome: string; de: string; ate: string }[];
  // Preview do chat do aluno p/ admin: UUID da escola-filha como qual o site admin
  // do Moodle pré-visualiza o <sos-chat>. Só no tema do portal. "" = desligado.
  chat_admin_preview_tenant?: string;
  // Tema PRÓPRIO do chat do aluno (<sos-chat>), independente do painel. Aplicado
  // como CSS vars --sos-chat-* no componente (GET /api/v1/chat/tema).
  chat?: ChatTheme;
}

interface ChatTheme {
  bg: string; // fundo da janela do chat
  text: string; // cor do texto
  accent: string; // botão Enviar + balão do aluno + destaques
  bubble: string; // fundo do balão da IA
  font: string; // chave de FONT_MAP
  radius: number; // cantos (px)
}

// Espelha o DEFAULT_THEME['chat'] do backend (theme.py) — fallback do editor.
const DEFAULT_CHAT_THEME: ChatTheme = {
  bg: '#ffffff',
  text: '#1f1f1f',
  accent: '#1f1f1f',
  bubble: '#f0f0f0',
  font: 'system',
  radius: 12,
};

interface CardCfg {
  color?: string; // destaque (barras + título)
  bg?: string; // cor de fundo do card
  text?: string; // cor do texto do card
  font?: string; // fonte do card (chave de FONT_MAP)
  icon?: string;
  titulo?: string;
}

interface TabCfg {
  id: string;
  nome: string;
  cards: string[];
  // Aba de IA: em vez de cards, mostra a resposta da IA a `ai_prompt` (grounded no
  // snapshot). O resultado gerado vive no backend (ai_dashboard_widgets), não aqui.
  kind?: 'ia';
  ai_prompt?: string;
}

interface AbaIAResult {
  tab_id: string;
  prompt?: string | null;
  result_md?: string | null;
  periodo_de?: string | null;
  periodo_ate?: string | null;
  periodo_label?: string | null;
  generated_at?: string | null;
  can_generate?: boolean;
}

interface ReportChart {
  type: string;
  title: string;
  series: { label: string; value: number }[];
}
interface ReportSection {
  key: string;
  titulo: string;
  texto: string;
  chart: ReportChart | null;
}
interface ReportData {
  escola: string | null;
  periodo_label: string;
  kpis: { label: string; value: number }[];
  sections: ReportSection[];
}

interface GestorMsg {
  role: 'user' | 'assistant';
  content: string;
}

interface GestorConvRow {
  id: string;
  titulo: string | null;
  created_at: string;
  mensagens: number;
}

interface GestorMsgRow {
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface GestorUso {
  consultas_total: number;
  consultas_7d: number;
  ultima_consulta: string | null;
}

const SUGESTOES = [
  'Onde está meu maior gargalo pedagógico?',
  'Que matéria concentra mais dúvidas?',
  'Como está o engajamento dos alunos?',
  'O que a segurança vem barrando?',
];
// Um exemplo por TIPO de gráfico (o gestor pede o tipo; a IA desenha com os dados
// reais). Cobre os 9 tipos suportados pelo painel.
const SUGESTOES_GRAFICOS = [
  'Matérias mais consultadas em barra',
  'Uso do Tutor IA por etapa em colunas',
  'Engajamento (ativos × inativos) em pizza',
  'Composição da matrícula em rosca',
  'Classificação das perguntas em barra empilhada',
  '% de alunos ativos em medidor',
  'Perguntas por etapa em linha',
  'Sessões e perguntas em área',
  'Categorias de segurança em radar',
];

interface EtapaCount {
  etapa: string | null;
  total: number;
}

interface CategoriaBloqueio {
  categoria: string;
  rotulo: string;
  total: number;
}

interface MateriaCount {
  categoria_id: string;
  materia: string;
  total: number;
}

interface EstudanteAtivo {
  user_id: string;
  name: string | null;
  sessoes: number;
  tenant_id?: string | null;
}

interface AssuntoEmergente {
  materia: string;
  atual: number;
  anterior: number;
  growth_pct: number | null; // null = assunto novo (sem base anterior)
}

interface ParceiroData {
  escola: string | null;
  matriculados: number;
  desmatriculados: number;
  pendentes_carga: number;
  ativos_7d: number;
  inativos_7d: number;
  sessoes_tutor_7d: number;
  tempo_medio_sessao_min: number;
  tempo_total_min: number;
  recorrencia_media_dias: number;
  mensagens_alunos_7d: number;
  media_msgs_por_sessao: number;
  bloqueios_total: number;
  // Tendência vs. período anterior (% int; null = sem base). null quando janela longa.
  delta_ativos: number | null;
  prev_ativos?: number | null;
  prev_sessoes?: number | null;
  prev_interacoes?: number | null;
  prev_barrados?: number | null;
  delta_sessoes: number | null;
  delta_interacoes: number | null;
  delta_barrados: number | null;
  por_categoria_bloqueio: CategoriaBloqueio[];
  por_materia: MateriaCount[];
  // Temáticas/tópicos granulares mais consultados (mais fino que a matéria; ex.: "frações").
  por_tematica?: { tematica: string; total: number }[];
  por_etapa: EtapaCount[];
  perguntas_por_etapa?: EtapaCount[];
  // Fase 3b — ranking de estudantes mais ativos + assuntos que mais cresceram.
  estudantes_mais_ativos?: EstudanteAtivo[];
  estudantes_baixo_uso?: EstudanteAtivo[];
  assuntos_emergentes?: AssuntoEmergente[];
  // Fase 3 — série temporal (evolução ao longo do tempo): interações + alertas por bucket.
  serie_temporal?: { data: string; interacoes: number; alertas: number }[];
  // Refinamento — evolução das top temáticas ao longo do tempo (multi-linha).
  serie_tematicas?: { tematica: string; pontos: { data: string; total: number }[] }[];
  // Fase 4 — uso por período letivo nomeado (bimestre/semestre da instituição).
  uso_por_periodo_letivo?: { nome: string; total: number }[];
  perguntas_classificadas: number;
  perguntas_sem_materia: number;
  perguntas_fora_conteudo: number;
  // % fora do escopo educacional calculado no backend (mesma conta do card; usado
  // pelo Conect/relatório). Opcional: payloads antigos caem no cálculo local.
  pct_fora_conteudo?: number;
  perguntas_pendentes: number;
  tutor_nome: string;
  copiloto_intro: string;
  // Divisão do solicitante (do token, assinada) — 'rede'/'diretor' = gestor (pode
  // criar aba de IA e gerar relatório); 'coordenador' = só visualiza.
  division_key?: string | null;
  // Capacidades efetivas (Fase 4) — o coordenador segue a concessão.
  can_view_reports?: boolean;
  can_use_copilot?: boolean;
  allowed_tabs?: string[] | null; // null = todas as abas de IA
}

// ─── Concessão de acesso do coordenador (Fase 4) ─────────────────────────────
interface ProfileRow {
  id: string;
  key: string;
  name: string;
  active: boolean;
  n_institutions: number;
  n_etapas: number;
  n_materias: number;
  can_view_reports: boolean;
}
interface GrantData {
  id: string | null; // null = perfil novo (ainda não salvo)
  key: string | null;
  name: string;
  allowed_tenant_ids: string[];
  allowed_etapas: string[]; // codes do catálogo
  allowed_ano_series: string[];
  allowed_materias: string[]; // nomes de matéria
  can_view_reports: boolean;
  can_use_copilot: boolean;
  allowed_tabs: string[] | null;
  active: boolean;
}
interface GrantOptions {
  institutions: { id: string; nome: string | null }[];
  etapas: { code: string; label: string }[]; // catálogo ai_etapas
  anos_serie: { etapa: string | null; ano_serie: string }[]; // pares p/ agrupar
  materias: string[]; // catálogo ai_categories (nomes)
  tabs: { id: string; nome: string }[];
}
interface CoordinatorDetail {
  profile: GrantData;
  options: GrantOptions;
}

// ─── Coordenador por pessoa (Fase A: associação pessoa→perfil + ajustes) ──────
interface PersonRow {
  id: string;
  moodle_user_id: string;
  name: string | null;
  profile_id: string;
  profile_name: string;
  has_overrides: boolean;
  active: boolean;
}
interface DisponivelRow {
  moodle_user_id: string;
  name: string | null;
  roles: string[];
  ja_associado: boolean;
}
interface PessoaEditorData {
  moodle_user_id: string;
  name: string | null;
  roles: string[];
  association: {
    id: string;
    profile_id: string;
    overrides: Record<string, unknown>;
    active: boolean;
  } | null;
  profiles: { id: string; name: string }[];
  options: GrantOptions;
}
// Estado do editor da pessoa (montado a partir de PessoaEditorData).
type OvDim =
  | 'allowed_tenant_ids'
  | 'allowed_etapas'
  | 'allowed_ano_series'
  | 'allowed_materias';
type OvCap = 'can_view_reports' | 'can_use_copilot';
interface PessoaEditing {
  moodle_user_id: string;
  name: string | null;
  roles: string[];
  assocId: string | null; // null = nova associação
  profileId: string; // '' = nenhum
  active: boolean;
  // Quais dimensões/capacidades estão PERSONALIZADAS (senão herdam do perfil).
  adj: Record<OvDim | OvCap, boolean>;
  lists: Record<OvDim, string[]>; // valores dos overrides de lista ([] = todos)
  caps: Record<OvCap, boolean>;
  profiles: { id: string; name: string }[];
  options: GrantOptions;
}

interface EscopoRegra {
  categoria: string;
  rotulo: string;
  mensagem: string | null;
  tipo: string;
}

interface EscopoData {
  escola: string | null;
  blacklist: EscopoRegra[];
  graylist: EscopoRegra[];
  regras_customizadas: boolean;
}

interface AlunoRow {
  user_id: string;
  name: string | null;
  etapa: string | null;
  ano_serie: string | null;
  enrollment_status: string | null;
  last_seen_at: string | null;
}

interface SessaoRow {
  id: string;
  aluno: string | null;
  user_id: string;
  titulo: string | null;
  componente: string | null;
  mensagens: number;
  created_at: string;
  materias: string[];
  perguntas_sem_materia: number;
}

interface AlunoBuscaRow {
  user_id: string;
  name: string | null;
  etapa: string | null;
  ano_serie: string | null;
}

interface BloqueioRow {
  id: string;
  categoria: string;
  rotulo: string;
  // Sub-motivo (ex.: categoria do anexo fora de escopo). Pode ser null.
  detalhe: string | null;
  severity: string;
  action: string;
  aluno: string | null;
  user_id: string;
  etapa: string | null;
  ano_serie: string | null;
  created_at: string;
}

interface PerguntaRow {
  id: string;
  aluno: string | null;
  user_id: string;
  etapa: string | null;
  ano_serie: string | null;
  materia: string | null;
  materia_status: string;
  pergunta: string;
  created_at: string;
}

type DrillTipo = 'alunos' | 'sessoes' | 'bloqueios' | 'perguntas';

interface DrillState {
  titulo: string;
  tipo: DrillTipo;
  items: AlunoRow[] | SessaoRow[] | BloqueioRow[] | PerguntaRow[];
  loading: boolean;
  error: string;
}

// ─── Acesso (de-para papel do Moodle → divisão), editável no painel (Fase 2) ──
interface AccessDivision {
  id: string;
  key: string;
  label: string;
  active: boolean;
}

interface AccessRoleMapRow {
  id: string;
  moodle_role: string;
  division_id: string;
  division_key: string;
  division_label: string;
  profile_id: string | null; // perfil de coordenador vinculado (ou null)
  profile_name: string | null;
  scope: string; // 'herdado' (global) | 'personalizado' (override do portal)
  active: boolean;
}

interface AccessProfileOption {
  id: string;
  name: string;
}

interface AccessOverview {
  divisions: AccessDivision[];
  profiles: AccessProfileOption[]; // perfis de coordenador = alvos extras do de-para
  role_map: AccessRoleMapRow[];
  can_edit: boolean;
}

interface AccessEditing {
  id: string | null; // null = criar override (POST); preenchido = editar (PUT)
  moodle_role: string;
  division_id: string;
  // Alvo = perfil de coordenador (quando setado, ignora division_id — o backend
  // força a divisão 'coordenador'). null = divisão-base pura.
  profile_id: string | null;
  active: boolean;
}

// O que cada divisão de acesso permite — legenda na aba Acesso. Chaveado pelo
// `key` ESTÁVEL da divisão (o `label` é editável no de-para). Reflete o
// enforcement do backend (escopo em _resolve_scope; capacidades em isManager/
// isNetwork/isGrantor e nas concessões do coordenador).
const DIVISION_HELP: Record<string, string> = {
  rede: 'Vê todas as escolas do portal e alterna entre elas. Edita este de-para, concede acesso a coordenadores, cria abas de IA, gera relatórios e usa o copiloto Conect.',
  diretor:
    'Vê somente a própria escola. Concede acesso a coordenadores, cria abas de IA, gera relatórios e usa o Conect — mas não edita este de-para (só o Gestor de Rede edita).',
  coordenador:
    'Vê somente a própria escola, em leitura. Relatórios, Conect e abas de IA aparecem apenas se um gestor conceder na aba Coordenadores. Não concede acesso nem edita este de-para.',
};

// ─── Filtros granulares (Fase 3): coorte (etapa/ano-série) + matéria ──────────
interface FiltroMateria {
  id: string;
  nome: string;
}
interface FiltroAno {
  etapa: string | null;
  ano_serie: string;
}
interface FiltrosData {
  etapas: string[];
  tem_sem_etapa: boolean;
  anos_serie: FiltroAno[];
  tem_sem_ano: boolean;
  materias: FiltroMateria[];
  materia_disponivel: boolean;
}

interface AutoInsightsData {
  insights_md: string | null;
  periodo_de: string | null;
  periodo_ate: string | null;
  periodo_label: string | null;
  generated_at: string | null;
  can_generate: boolean;
}

/**
 * <sos-dashboard> — painel do PARCEIRO (escola) pro staff (Diretor/Coordenador).
 *
 * Mesmo padrão do <sos-chat>: recebe `api-base` + `token` (JWT com role→divisão
 * e institution→escola, emitido pelo plugin Moodle). Busca GET
 * /api/v1/dashboard/parceiro — o backend escopa tudo pela escola do token e só
 * libera staff (aluno = 403). Empacotado no mesmo bundle (sos-chat.js).
 *
 * Fase A: botãozinho de escopo (o que o tutor barra), cards clicáveis com
 * drill-down (lista por trás de cada total) e métricas de conversa/guardrails.
 */
// Textos do tour por seção nomeada de cards (CARD_SECTIONS[i].label → explicação).
// Fallback genérico se a seção não estiver mapeada.
const TOUR_SECTION_TEXT: Record<string, string> = {
  Engajamento:
    'Quantos estudantes estão ativos e como está a adesão à tutoria no período/filtro selecionado.',
  'Aprendizagem e dúvidas':
    'Disciplinas e temas mais consultados, assuntos emergentes e a classificação das interações.',
  'Uso da Tutoria IA':
    'Volume de uso ao longo do tempo, por etapa/ano-série e por período letivo.',
  'Governança e Segurança Educacional':
    'Interações que a camada de segurança tratou — o que foi redirecionado/bloqueado e por quê.',
};

@customElement('sos-dashboard')
export class SosDashboard extends LitElement {
  /** URL base da API SOS (ex.: https://api.sosalunos.app.br). */
  @property({ attribute: 'api-base' }) apiBase = '';
  /** JWT do staff. */
  @property() token = '';
  /** URL (mesma origem do Moodle) que emite um token NOVO usando a sessão do
   * Moodle — renovação silenciosa quando o JWT embutido na página expira. */
  @property({ attribute: 'token-url' }) tokenUrl = '';
  /** Deep-link do menu: quando setado (página dedicada ?conect=1), abre o copiloto
   * Conect automaticamente assim que os dados carregam e há permissão. */
  @property({ attribute: 'open-conect' }) openConect = '';
  private _conectAutoOpened = false;

  // Renovação em voo (single-flight): N chamadas com 401 simultâneo disparam UMA
  // renovação e todas esperam o mesmo resultado.
  private _tokenRefresh: Promise<boolean> | null = null;

  private async refreshToken(): Promise<boolean> {
    if (!this.tokenUrl) return false;
    if (this._tokenRefresh) return this._tokenRefresh;
    this._tokenRefresh = (async () => {
      try {
        // credentials same-origin: o endpoint valida a SESSÃO do Moodle (cookie).
        const r = await fetch(this.tokenUrl, { credentials: 'same-origin' });
        if (!r.ok) return false;
        const j = (await r.json()) as { token?: string };
        if (!j?.token) return false;
        this.token = j.token;
        return true;
      } catch {
        return false;
      } finally {
        // Libera pro PRÓXIMO 401 (não cacheia falha nem sucesso além do ciclo).
        setTimeout(() => {
          this._tokenRefresh = null;
        }, 0);
      }
    })();
    return this._tokenRefresh;
  }

  /** O token expira em <60s (ou já expirou)? Lido do payload do próprio JWT —
   * permite renovar PROATIVAMENTE (ex.: antes de abrir o stream SSE do Conect). */
  private tokenExpSoon(): boolean {
    try {
      const b64 = this.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(b64)) as { exp?: number };
      return (
        typeof payload.exp === 'number' && Date.now() / 1000 > payload.exp - 60
      );
    } catch {
      return false;
    }
  }

  /** fetch autenticado com renovação automática: em 401, renova o token pela
   * sessão do Moodle (token-url) e repete a chamada UMA vez. Só devolve 401 se a
   * própria sessão do Moodle morreu (aí só o reload/login resolve). */
  private async authFetch(url: string, init?: RequestInit): Promise<Response> {
    if (this.tokenExpSoon()) await this.refreshToken(); // proativo (poupa um 401)
    const doFetch = () =>
      fetch(url, {
        ...init,
        headers: {
          ...(init?.headers || {}),
          Authorization: `Bearer ${this.token}`,
        },
      });
    let r = await doFetch();
    if (r.status === 401 && (await this.refreshToken())) {
      r = await doFetch();
    }
    return r;
  }

  @state() private data?: ParceiroData;
  @state() private error = '';
  @state() private loading = true;
  // White-label: tema EFETIVO do escopo (default ← portal ← escola).
  @state() private tema?: ThemeData;
  // Tela "Configurações" (abas Personalizar / Acesso / Coordenadores).
  @state() private showConfig = false;
  @state() private configTab:
    | 'personalizar'
    | 'acesso'
    | 'coordenadores'
    | 'pessoas' = 'personalizar';
  // Seção ativa DENTRO do Personalizar (o formulário é grande — navegar por
  // seções evita o scroll único gigante).
  @state() private pzSection:
    | 'aparencia'
    | 'blocos'
    | 'abas'
    | 'marca'
    | 'chat'
    | 'graficos'
    | 'periodos' = 'aparencia';
  @state() private temaSalvando = false;
  @state() private temaMsg = '';
  // Estado do auto-save do editor (feedback bem visível no topo do modal).
  @state() private temaSaveState: 'idle' | 'dirty' | 'saving' | 'saved' | 'error' =
    'idle';
  private _saveTimer: number | undefined;
  // Tentativas consecutivas de save que falharam (auto-retry transitório).
  private _saveRetries = 0;
  @state() private logoUploading = false;
  @state() private logoDragging = false;
  @state() private activeTab = '';
  @state() private emojiOpenFor = '';
  @state() private temaPresets: { nome: string; tema: Partial<ThemeData> }[] = [];

  // Botãozinho (escopo / blacklist).
  @state() private showInfo = false;
  @state() private escopo?: EscopoData;
  @state() private escopoLoading = false;
  @state() private escopoError = '';

  // Drill-down (lista por trás de um total).
  @state() private drill?: DrillState;

  // Visão de Rede: o Gestor de Rede seleciona a instituição (vazio = não é rede).
  @state() private escolas: { id: string; nome: string | null }[] = [];
  @state() private escolaSel = 'todas';

  // Período das MÉTRICAS de atividade (default: mês atual). `periodoMes` (YYYY-MM),
  // quando setado, tem precedência (mês específico escolhido no seletor de mês).
  @state() private periodo = 'mes_atual';
  @state() private periodoMes = '';

  // Filtros granulares (Fase 3): coorte (etapa/ano-série) + matéria. '' = todos;
  // '__sem__' na etapa/ano = "sem valor". Matéria some na visão 'Todas' (por-tenant).
  @state() private filtroEtapa = '';
  @state() private filtroAnoSerie = '';
  @state() private filtroMateria = '';
  @state() private filtros?: FiltrosData;
  // Filtro de ALUNO: escopa TODO o painel a um aluno. filtroAluno = user_id (''=todos);
  // filtroAlunoNome = rótulo exibido no chip. Busca por nome no dropdown.
  @state() private filtroAluno = '';
  @state() private filtroAlunoNome = '';
  // v2 §2.3 — detalhes sob demanda: cards de série COLAPSADOS por padrão; o gestor
  // expande quando quer investigar. Guarda as chaves abertas na sessão do painel.
  @state() private cardsAbertos = new Set<string>();
  @state() private alunoBusca: AlunoBuscaRow[] = [];
  @state() private alunoBuscaAberta = false;
  private _alunoBuscaTimer?: number;

  // Copiloto do Gestor (chat de analytics grounded no snapshot).
  @state() private showGestor = false;
  @state() private gestorMsgs: GestorMsg[] = [];
  @state() private gestorStreamingText = '';
  @state() private gestorStreaming = false;
  @state() private gestorInput = '';
  @state() private gestorError = '';
  @state() private gestorUso?: GestorUso;
  private gestorSessionId: string | null = null;
  // Gerenciamento de conversas do copiloto (histórico/renomear/excluir).
  @state() private gestorSessions: GestorConvRow[] = [];
  @state() private gestorShowList = false;
  @state() private gestorRenamingId: string | null = null;
  @state() private gestorRenameText = '';
  @state() private gestorLastQ = ''; // última pergunta (pro "Tentar de novo")

  // Abas de IA: resultado por tab_id (cache local; limpo ao trocar escola/período).
  @state() private abaIA: Record<string, AbaIAResult | null> = {};
  @state() private abaIALoading = ''; // tab_id em geração/carregamento
  @state() private abaIAError = '';
  // Relatório do período (gerado sob demanda; PDF baixado do backend).
  @state() private showRelatorio = false;
  @state() private relatorioLoading = false;
  @state() private relatorioError = '';
  @state() private relatorio?: { report_id: string; report: ReportData };
  @state() private relatorioPdfLoading = false;
  // Seções escolhidas do relatório ([] = todas). O gestor gera 1 seção ou todas.
  @state() private relatorioSecoes: string[] = [];

  // Bloco "Insights da IA" (interpretação automática, inline no topo do painel).
  @state() private autoInsights?: AutoInsightsData;
  @state() private autoInsightsLoading = false;
  @state() private autoInsightsErr = '';
  // Tour guiado (tutorial): índice do passo atual (-1 = inativo) e retângulo do
  // alvo destacado (viewport-relative). tourSteps = passos EFETIVOS (filtra os que
  // não existem pra este papel/estado).
  @state() private tourStep = -1;
  @state() private tourRect: { top: number; left: number; width: number; height: number } | null =
    null;
  private tourSteps: { key: string | null; title: string; text: string }[] = [];
  // Última (escola|de|até) para a qual TENTAMOS auto-gerar — evita re-POST em loop se
  // a geração falhar (a tentativa marca a chave; só re-tenta em outro escopo/período
  // ou no clique manual em Atualizar).
  private _aiTriedKey = '';

  // Acesso (de-para papel→divisão) editável no painel (Fase 2). Só gestor edita.
  @state() private acesso?: AccessOverview;
  @state() private acessoLoading = false;
  @state() private acessoError = '';
  @state() private acessoSaving = false;
  @state() private acessoEditing?: AccessEditing;

  // Perfis de coordenador (Rede/Diretor gerenciam). Substituem o grant por-pessoa.
  @state() private coordList?: ProfileRow[];
  @state() private coordLoading = false;
  @state() private coordError = '';
  @state() private coordSaving = false;
  @state() private coordEditing?: CoordinatorDetail; // grant em edição + opções

  // Coordenador por PESSOA (Fase A): associação pessoa→perfil + ajustes.
  @state() private pessoasList?: PersonRow[]; // associações existentes
  @state() private disponiveis?: DisponivelRow[]; // diretório (carga do Moodle)
  @state() private pessoaLoading = false;
  @state() private pessoaError = '';
  @state() private pessoaSaving = false;
  @state() private pessoaEditing?: PessoaEditing;

  /** Gestor (Rede/Diretor) pode criar aba de IA e gerar relatório; coordenador não. */
  private get isManager(): boolean {
    const dk = this.data?.division_key;
    return dk === 'rede' || dk === 'diretor';
  }

  /** Gestor de REDE (ou site-admin, que resolve p/ 'rede'): edita o de-para do portal. */
  private get isNetwork(): boolean {
    return this.data?.division_key === 'rede';
  }

  /** Capacidade efetiva de relatório (gestor sempre; coordenador conforme concessão). */
  private get canReports(): boolean {
    return this.isManager || this.data?.can_view_reports === true;
  }

  /** Capacidade efetiva de copiloto (padrão true; coordenador pode ter desabilitado). */
  private get canCopilot(): boolean {
    return this.data?.can_use_copilot !== false;
  }

  static styles = css`
    :host {
      display: block;
      position: relative; /* âncora dos overlays (info/drill) */
      /* White-label: as vars --sos-* vêm do tema (applyTheme no host); com
         fallback nos defaults. Remapeamos --blue/--muted/--border pro tema, então
         o CSS existente já tematiza sem tocar cada regra. */
      font-family: var(--sos-font, system-ui, -apple-system, sans-serif);
      color: var(--sos-text, #1a1a1a);
      --green: #1d9e75;
      --blue: var(--sos-accent, #378add);
      --amber: #ba7517;
      --red: #c0432f;
      --muted: var(--sos-muted, #5f5e5a);
      --border: var(--sos-border, rgba(0, 0, 0, 0.12));
      /* Superfícies derivadas (translúcidas → funcionam em tema claro E escuro):
         painéis/botões usam --sos-card; hover e trilhas usam cinza translúcido. */
      --panel-bg: var(--sos-card, #fff);
      /* Destaque ao passar o mouse: cor do tema (--sos-hover) aplicada em BAIXA
         opacidade (color-mix) → é sempre um leve véu, nunca cobre o texto, então
         não briga com a cor da fonte em nenhum tema. Fallback rgba p/ browsers
         sem color-mix. Padrão = cinza neutro (#808080). */
      --hover-bg: rgba(127, 127, 127, 0.14);
      --hover-bg: color-mix(in srgb, var(--sos-hover, #808080) 16%, transparent);
      --track-bg: rgba(127, 127, 127, 0.22);
      --track-bg: color-mix(in srgb, var(--sos-hover, #808080) 24%, transparent);
    }
    /* White-label: o conteúdo vive num wrap com fundo+padding+cantos do tema
       (os overlays ficam FORA, absolutos sobre o host). Padding = respiro. */
    .wrap {
      background: var(--sos-bg, #fff);
      padding: var(--sos-pad, 16px);
      border-radius: var(--sos-radius, 12px);
      box-sizing: border-box;
      max-width: 100%;
      overflow-wrap: break-word;
      /* O editor "Personalizar" é um overlay ABSOLUTO ancorado no host (inset:0),
         então a altura dele acompanha o conteúdo do .wrap. Uma aba de IA ativa
         (placeholder curto) encolheria o host e o overlay junto — por isso um
         piso de altura garante que o editor nunca colapse. Só age quando o
         conteúdo é curto; painel cheio é bem mais alto. */
      min-height: 480px;
    }
    .pheading {
      display: inline-block;
      font-size: 20px;
      font-weight: 700;
      color: var(--sos-text);
      padding-bottom: 6px;
      margin-bottom: 14px;
      border-bottom: 3px solid var(--sos-accent, #378add);
    }
    .head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
    }
    .head-l {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px 10px;
      min-width: 0;
    }
    .brand-logo {
      height: 34px;
      width: auto;
      max-width: 140px;
      object-fit: contain;
      border-radius: 6px;
      margin-right: 4px;
      flex: 0 0 auto;
    }
    .title {
      font-size: 18px;
      font-weight: 600;
    }
    .tag {
      font-size: 13px;
      color: var(--muted);
    }
    .ibtn {
      flex: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 30px;
      padding: 0 11px;
      border: 0.5px solid var(--border);
      border-radius: 999px;
      background: var(--panel-bg);
      color: var(--muted);
      font-size: 12px;
      cursor: pointer;
    }
    .ibtn:hover {
      border-color: var(--blue);
      color: var(--blue);
    }
    .ibtn svg {
      width: 16px;
      height: 16px;
    }
    .sub {
      font-size: 13px;
      color: var(--muted);
      margin: 4px 0 16px;
    }
    /* Linha de ESCOPO (v2 §3/§4): diz na hora se a análise é institucional ou
       individual, com breadcrumb e volta. */
    .scope-line {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin: 6px 0 2px;
      font-size: 13px;
    }
    .scope-kind {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--blue);
      background: color-mix(in srgb, var(--blue) 12%, transparent);
      padding: 3px 8px;
      border-radius: 999px;
    }
    .scope-line.indiv .scope-kind {
      color: var(--amber);
      background: color-mix(in srgb, var(--amber) 14%, transparent);
    }
    .scope-path {
      color: var(--text, inherit);
    }
    .scope-back {
      border: 0.5px solid var(--border);
      background: var(--card-bg, var(--sos-card, #fff));
      border-radius: 999px;
      padding: 3px 10px;
      font-size: 12px;
      cursor: pointer;
      color: inherit;
    }
    .scope-back:hover {
      border-color: var(--blue);
    }
    /* v2 §2.1 — "O que merece atenção" (resumo executivo). Sem faixa lateral —
       usa o card padrão (borda/raio normais). */
    .atn-row {
      display: flex;
      gap: 8px;
      font-size: 13px;
      margin: 6px 0;
    }
    .atn-ic {
      color: var(--amber);
      flex: 0 0 auto;
    }
    .atn-cta {
      margin-top: 8px;
    }
    /* v2 §2.3 — detalhe sob demanda: card de série colapsado por padrão. */
    .hd-fold {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      user-select: none;
    }
    .fold-arr {
      color: var(--muted);
      font-size: 12px;
    }
    .fold-note {
      cursor: pointer;
    }
    /* Abas customizáveis (pílulas). */
    .tabbar {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: 0 0 16px;
    }
    .tab {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      color: var(--muted);
      border-radius: 999px;
      padding: 7px 14px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }
    .tab:hover {
      border-color: var(--blue);
    }
    .tab.active {
      background: var(--blue);
      color: #fff;
      border-color: var(--blue);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      /* espaçamento abaixo vem do gap do .cards (grid pai) */
      margin-bottom: 0;
    }
    /* White-label: container dos cards reordenáveis (order/hide via cardStyle).
       Grid de 2 colunas: por padrão todo filho ocupa a linha inteira; cards
       COMPACTOS (classe .half) dividem a linha quando o container é largo o
       bastante (container query — funciona em qualquer largura de embed).
       Navegador sem @container mantém tudo full-width (fallback gracioso). */
    .cards {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      container-type: inline-size;
    }
    .cards > * {
      grid-column: 1 / -1;
      min-width: 0;
    }
    .cards > .card {
      margin-bottom: 0;
    }
    @container (min-width: 860px) {
      .cards > .half {
        grid-column: auto;
      }
    }
    /* Cabeçalho de seção (Engajamento / Consumo Pedagógico / …): linha inteira,
       discreto, ancorado no 1º card do grupo via CSS order. */
    .sec-hd {
      grid-column: 1 / -1;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.07em;
      text-transform: uppercase;
      color: var(--muted);
      margin: 10px 2px -6px;
    }
    .stat {
      border: 0.5px solid var(--border);
      border-radius: var(--sos-radius, 12px);
      padding: 12px 14px;
      background: var(--card-bg, var(--sos-card, #fff));
      color: var(--card-text, inherit);
      font-family: var(--card-font, inherit);
      position: relative;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    .stat.clk {
      cursor: pointer;
      transition: border-color 0.12s, box-shadow 0.12s;
    }
    .stat.clk:hover {
      border-color: var(--blue);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    }
    .stat.clk::after {
      content: '›';
      position: absolute;
      top: 12px;
      right: 14px;
      color: var(--muted);
      font-size: 18px;
      line-height: 1;
    }
    .slabel {
      font-size: 12px;
      color: var(--muted);
      display: flex;
      align-items: center;
      gap: 7px;
    }
    .dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      display: inline-block;
    }
    /* Emoji do indicador (white-label) no lugar da bolinha. */
    .sico {
      font-size: 14px;
      line-height: 1;
      display: inline-block;
    }
    .sval {
      font-size: 26px;
      font-weight: 600;
      margin: 4px 0 2px;
    }
    .ssub {
      font-size: 12px;
      color: var(--muted);
    }
    .card {
      border: 0.5px solid var(--border);
      border-radius: var(--sos-radius, 12px);
      padding: 14px 16px;
      background: var(--card-bg, var(--sos-card, #fff));
      color: var(--card-text, inherit);
      font-family: var(--card-font, inherit);
      margin-bottom: 14px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    .kpis-block {
      font-family: var(--card-font, inherit);
    }
    .hd {
      font-size: 15px;
      font-weight: 600;
      margin: 0 0 12px;
      color: var(--card-accent, inherit);
    }
    .trow {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 9px;
      font-size: 13px;
      border-radius: 6px;
    }
    .trow.clk {
      cursor: pointer;
    }
    .trow.clk:hover {
      background: var(--hover-bg);
    }
    .tname {
      width: 130px;
    }
    /* Rótulo com inicial maiúscula (os tópicos vêm minúsculos do classificador). */
    .tname::first-letter {
      text-transform: uppercase;
    }
    /* Temáticas (dimensão fina): rótulos longos em 1 linha com reticências —
       o texto completo fica no title (tooltip). Evita linhas de altura dupla. */
    .t-fine .tname {
      width: 200px;
      flex: 0 0 auto;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ttrack {
      flex: 1;
      height: 8px;
      background: var(--track-bg);
      border-radius: 4px;
      overflow: hidden;
    }
    .tbar {
      /* span é INLINE por padrão — width/min-width não se aplicam e o fill
         renderizava com largura 0 (só o trilho aparecia). block conserta. */
      display: block;
      height: 100%;
      background: var(--card-accent, var(--blue));
      border-radius: 4px;
      /* contagens pequenas ainda aparecem (nunca fill invisível) */
      min-width: 4px;
    }
    .tbar.red {
      background: var(--red);
    }
    .hd-ico {
      font-size: 1.05em;
      line-height: 1;
    }
    /* Selo de ajuda (ⓘ) no cabeçalho dos cards — legenda ao passar o mouse. */
    .hd-help {
      display: inline-block;
      margin-left: 6px;
      font-size: 0.82em;
      font-weight: 400;
      color: var(--muted);
      opacity: 0.65;
      cursor: help;
      vertical-align: baseline;
      transition: opacity 0.15s;
    }
    .hd-help:hover {
      opacity: 1;
    }
    /* ─── Tour guiado (tutorial): overlay + spotlight + balão ─── */
    .tour-layer {
      position: fixed;
      inset: 0;
      z-index: 99998;
    }
    .tour-dim {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
    }
    .tour-spot {
      position: fixed;
      border-radius: 8px;
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
      outline: 2px solid #2563eb;
      outline-offset: 0;
      pointer-events: none;
      transition: top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease;
    }
    .tour-card {
      position: fixed;
      width: 320px;
      max-width: calc(100vw - 24px);
      background: #fff;
      color: #1f1f1f;
      border-radius: 10px;
      padding: 14px 16px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.32);
      z-index: 100000;
      font-size: 13px;
      line-height: 1.5;
    }
    .tour-hd {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
    }
    .tour-count {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.3px;
    }
    .tour-x {
      background: none;
      border: 0;
      cursor: pointer;
      color: #999;
      font-size: 14px;
      line-height: 1;
      padding: 0;
    }
    .tour-title {
      font-size: 15px;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .tour-text {
      color: #444;
    }
    .tour-nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;
      gap: 8px;
    }
    .tour-navr {
      display: flex;
      gap: 8px;
    }
    .tour-skip {
      background: none;
      border: 0;
      color: #888;
      cursor: pointer;
      font-size: 12px;
    }
    .tour-prev {
      background: #eef0f2;
      border: 0;
      border-radius: 6px;
      padding: 7px 12px;
      cursor: pointer;
      font-weight: 600;
      font-size: 12px;
    }
    .tour-next {
      background: #2563eb;
      color: #fff;
      border: 0;
      border-radius: 6px;
      padding: 7px 14px;
      cursor: pointer;
      font-weight: 600;
      font-size: 12px;
    }
    .kpis-block > .hd {
      margin-bottom: 12px;
    }
    .tnum {
      width: 40px;
      text-align: right;
      color: var(--muted);
    }
    /* Temas em crescimento (Fase 3b): badge de crescimento + par anterior→atual. */
    .emg-badge {
      flex: 0 0 auto;
      font-size: 12px;
      font-weight: 600;
      color: var(--green);
      background: color-mix(in srgb, var(--green) 14%, transparent);
      border-radius: 6px;
      padding: 1px 7px;
      margin-left: auto;
      white-space: nowrap;
    }
    /* Menor utilização (bottom-N dentro do card de estudantes ativos). */
    .bu-sep {
      margin: 10px 0 4px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--muted);
      border-top: 0.5px solid var(--border);
      padding-top: 8px;
    }
    .bu-row {
      opacity: 0.85;
    }
    .bu-num {
      margin-left: auto;
      color: var(--muted);
      font-size: 12px;
      white-space: nowrap;
    }
    .note {
      font-size: 12px;
      color: var(--muted);
      margin-top: 8px;
    }
    .serie-leg {
      display: flex;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
      font-size: 12px;
      color: var(--muted);
      margin-top: 6px;
    }
    .serie-leg i {
      display: inline-block;
      width: 12px;
      height: 3px;
      border-radius: 2px;
      margin-right: 5px;
      vertical-align: middle;
    }
    .serie-range {
      margin-left: auto;
    }
    .err {
      color: #e5675a;
      padding: 16px;
    }
    .loading {
      color: var(--muted);
      padding: 16px;
    }
    /* Atualização DISCRETA: em vez de trocar o painel inteiro por "Carregando…" a
       cada clique, o conteúdo atual continua visível e o refresh é sinalizado só
       por uma barrinha fina no topo (ancorada no :host, que é position:relative). */
    .refreshbar {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      overflow: hidden;
      border-top-left-radius: var(--sos-radius, 12px);
      border-top-right-radius: var(--sos-radius, 12px);
      background: color-mix(in srgb, var(--blue) 16%, transparent);
      z-index: 15;
      pointer-events: none;
    }
    .refreshbar::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 35%;
      background: var(--blue);
      animation: sos-refresh 1s ease-in-out infinite;
    }
    @keyframes sos-refresh {
      0% {
        transform: translateX(-120%);
      }
      100% {
        transform: translateX(400%);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .refreshbar::before {
        animation-duration: 2.4s;
      }
    }
    /* Cursor de "ocupado" durante o refresh — pista sutil sem esconder o conteúdo. */
    .wrap.refreshing {
      cursor: progress;
    }
    /* Falha num refresh: banner inline (o painel segue mostrando os dados anteriores)
       em vez de apagar tudo com a tela de erro. */
    .refresherr {
      margin: 0 0 10px;
      padding: 8px 12px;
      font-size: 12.5px;
      color: var(--red);
      background: color-mix(in srgb, var(--red) 10%, transparent);
      border: 0.5px solid color-mix(in srgb, var(--red) 35%, transparent);
      border-radius: 8px;
    }

    /* ── Overlays (info + drill), padrão do <sos-chat> ─────────────── */
    .panel {
      position: absolute;
      inset: 0;
      background: var(--panel-bg);
      z-index: 20;
      border-radius: 12px;
      overflow-y: auto;
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
    }
    /* Conect = modal ancorado na VIEWPORT (não no host, que é alto): header no
       topo, mensagens rolam no meio, campo fixo embaixo — igual chat de IA. */
    .panel.gpanel {
      position: fixed;
      inset: 0;
      /* Acima da navbar/toasts fixos do host (Moodle Boost/Lambda usam
         z-index ~1030-1052). O modal precisa cobrir o chrome do host, então
         vai bem alto de propósito — senão o topo fica atrás do header. */
      z-index: 2147483000;
      background: rgba(0, 0, 0, 0.45);
      overflow: visible;
      border-radius: 0;
      box-shadow: none;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      box-sizing: border-box;
    }
    .gcard {
      width: min(1040px, 94vw);
      height: min(88vh, 780px);
      background: var(--panel-bg);
      border-radius: 14px;
      box-shadow: 0 16px 50px rgba(0, 0, 0, 0.35);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .panel-head {
      position: sticky;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 16px;
      background: var(--panel-bg);
      border-bottom: 0.5px solid var(--border);
    }
    .panel-title {
      font-size: 15px;
      font-weight: 600;
    }
    .panel-sub {
      display: block;
      margin-top: 2px;
      font-size: 11.5px;
      font-weight: 400;
      color: var(--muted);
      line-height: 1.3;
    }
    .xbtn {
      flex: none;
      width: 30px;
      height: 30px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0.5px solid var(--border);
      border-radius: 8px;
      background: var(--panel-bg);
      color: var(--muted);
      cursor: pointer;
    }
    .xbtn:hover {
      color: var(--sos-text);
      border-color: var(--border);
    }
    .panel-body {
      padding: 14px 16px;
    }
    /* Abas da tela Configurações (Personalizar/Acesso/Coordenadores) */
    .cfg-tabs {
      display: flex;
      gap: 2px;
      padding: 6px 12px 0;
      border-bottom: 0.5px solid var(--border);
      background: var(--panel-bg);
      flex: none;
      flex-wrap: wrap;
    }
    .cfg-scroll {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }
    /* Sub-navegação do Personalizar (pills por seção) — fica visível ao rolar. */
    .pz-nav {
      position: sticky;
      top: 0;
      z-index: 4;
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      padding: 12px 16px 10px;
      background: var(--panel-bg);
      border-bottom: 0.5px solid var(--border);
    }
    .pz-navbtn {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      color: var(--muted);
      border-radius: 999px;
      padding: 6px 14px;
      font-size: 12.5px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
    }
    .pz-navbtn:hover {
      color: var(--sos-text);
      border-color: var(--blue);
    }
    .pz-navbtn.on {
      background: color-mix(in srgb, var(--blue) 14%, transparent);
      border-color: var(--blue);
      color: var(--blue);
    }
    /* Previews dos tipos de gráfico (Personalizar → Gráficos) */
    .pz-chart-prevs {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 10px;
      width: 100%;
      margin-top: 10px;
    }
    .pz-chart-prev {
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 10px 12px;
      background: var(--panel-bg);
      min-width: 0;
    }
    .pz-chart-prev .gchart {
      margin: 0;
    }
    .cfg-tab {
      border: none;
      background: none;
      color: var(--muted);
      font-family: inherit;
      font-size: 13.5px;
      font-weight: 600;
      padding: 8px 14px;
      border-bottom: 2px solid transparent;
      margin-bottom: -0.5px;
      cursor: pointer;
    }
    .cfg-tab.active {
      color: var(--blue);
      border-bottom-color: var(--blue);
    }
    .cfg-tab:hover {
      color: var(--sos-text);
    }
    .esec-h {
      font-size: 13px;
      font-weight: 600;
      margin: 16px 0 8px;
      display: flex;
      align-items: center;
      gap: 7px;
    }
    .esec-h:first-child {
      margin-top: 0;
    }
    .erow {
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 10px 12px;
      margin-bottom: 8px;
    }
    .erow-t {
      font-size: 13px;
      font-weight: 600;
    }
    .erow-m {
      font-size: 12px;
      color: var(--muted);
      margin-top: 3px;
    }
    .lrow {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 4px;
      border-bottom: 0.5px solid var(--border);
      font-size: 13px;
    }
    .lrow:last-child {
      border-bottom: 0;
    }
    .lrow.clk {
      cursor: pointer;
      border-radius: 6px;
    }
    .lrow.clk:hover {
      background: var(--hover-bg);
    }
    .ltags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 5px;
    }
    .mtag {
      font-size: 11px;
      padding: 1px 7px;
      border-radius: 999px;
      background: var(--hover-bg);
      color: var(--sos-text);
      white-space: nowrap;
    }
    .mtag.sem {
      background: rgba(186, 117, 23, 0.15);
      color: var(--amber);
    }
    .mtag.pend {
      background: var(--hover-bg);
      color: var(--muted);
    }
    .lmain {
      flex: 1;
      min-width: 0;
    }
    .lname {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .lsub {
      font-size: 12px;
      color: var(--muted);
      margin-top: 2px;
    }
    .lright {
      flex: none;
      text-align: right;
      color: var(--muted);
      font-size: 12px;
    }
    .badge {
      flex: none;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 999px;
      font-weight: 600;
    }
    .badge.ok {
      background: rgba(29, 158, 117, 0.12);
      color: var(--green);
    }
    .badge.warn {
      background: rgba(186, 117, 23, 0.13);
      color: var(--amber);
    }
    .badge.bad {
      background: rgba(192, 67, 47, 0.12);
      color: var(--red);
    }
    .empty {
      color: var(--muted);
      padding: 20px 4px;
      text-align: center;
      font-size: 13px;
    }
    /* Badge de tendência vs. período anterior nos KPIs. */
    .delta {
      font-size: 11px;
      font-weight: 600;
      vertical-align: middle;
      white-space: nowrap;
    }
    .delta.good {
      color: var(--green);
    }
    .delta.bad {
      color: var(--red);
    }
    /* % de interações fora do escopo (bloco de Governança). */
    .gov-pct {
      display: flex;
      align-items: baseline;
      gap: 8px;
      padding: 2px 0 12px;
      border-bottom: 0.5px solid var(--border);
      margin-bottom: 10px;
    }
    .gov-pct-v {
      font-size: 22px;
      font-weight: 700;
      color: var(--red);
    }
    .gov-pct-l {
      font-size: 12px;
      color: var(--muted);
      line-height: 1.4;
    }
    /* Bloco "Insights da IA" (interpretação automática). */
    .ai-insights .hd {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .ai-refresh {
      font: inherit;
      font-size: 11.5px;
      color: var(--muted);
      background: transparent;
      border: 0.5px solid var(--border);
      border-radius: 8px;
      padding: 4px 9px;
      cursor: pointer;
    }
    .ai-refresh:hover:not(:disabled) {
      color: var(--sos-text);
      border-color: var(--blue);
    }
    .ai-refresh:disabled {
      opacity: 0.6;
      cursor: default;
    }
    .ai-md {
      font-size: 13.5px;
      line-height: 1.6;
    }
    .ai-md ul {
      margin: 4px 0;
      padding-left: 18px;
    }
    .ai-md li {
      margin: 5px 0;
    }
    .ai-gen {
      margin-top: 6px;
    }
    .ai-stale {
      margin-top: 8px;
      font-size: 11.5px;
      color: var(--amber, #b45309);
      background: color-mix(in srgb, var(--amber, #b45309) 10%, transparent);
      border-radius: 8px;
      padding: 6px 10px;
    }

    /* ── Seletor de instituição (Gestor de Rede) ──────────────────── */
    .scope {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0 0 16px;
    }
    .scope-l {
      font-size: 12px;
      color: var(--muted);
      font-weight: 600;
    }
    .scope-sel {
      flex: 1;
      max-width: 320px;
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 9px 12px;
      font-size: 14px;
      font-family: inherit;
      background: var(--panel-bg);
      color: var(--sos-text);
      cursor: pointer;
      outline: none;
    }
    .scope-sel:focus {
      border-color: var(--blue);
    }
    /* Combos legíveis em QUALQUER tema (inclusive escuros como Grafite): a lista de
       <option> nativa não herda as cores do tema sozinha — força fundo do card +
       texto do tema em toda <option> do componente. */
    select option {
      background: var(--panel-bg);
      color: var(--sos-text);
    }
    /* Barra de filtros granulares (Fase 3) */
    .filtros {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin: -6px 0 16px;
    }
    .fl-sel {
      flex: 0 1 auto;
      min-width: 130px;
      max-width: 220px;
      padding: 7px 10px;
      font-size: 13px;
    }
    .fl-clear {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      color: var(--muted);
      border-radius: 8px;
      padding: 6px 11px;
      font-size: 12.5px;
      font-family: inherit;
      cursor: pointer;
    }
    .fl-clear:hover {
      color: var(--sos-text);
      border-color: var(--blue);
    }
    /* Filtro de aluno: busca com dropdown + chip quando selecionado. */
    .fl-aluno {
      position: relative;
      display: inline-block;
    }
    .fl-aluno-in {
      min-width: 160px;
    }
    .fl-aluno-menu {
      position: absolute;
      z-index: 30;
      top: calc(100% + 4px);
      left: 0;
      min-width: 240px;
      max-height: 280px;
      overflow-y: auto;
      background: var(--card, var(--panel-bg));
      border: 0.5px solid var(--border);
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
      padding: 4px;
    }
    .fl-aluno-opt {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1px;
      width: 100%;
      text-align: left;
      background: none;
      border: 0;
      border-radius: 7px;
      padding: 7px 9px;
      font-family: inherit;
      cursor: pointer;
      color: var(--sos-text);
    }
    .fl-aluno-opt:hover {
      background: color-mix(in srgb, var(--blue) 12%, transparent);
    }
    .fl-aluno-nome {
      font-size: 13px;
    }
    .fl-aluno-meta {
      font-size: 11px;
      color: var(--muted);
    }
    .fl-aluno-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: color-mix(in srgb, var(--blue) 14%, transparent);
      color: var(--blue);
      border-radius: 999px;
      padding: 5px 6px 5px 11px;
      font-size: 12.5px;
      font-weight: 600;
    }
    .fl-hint {
      margin: -8px 0 14px;
      font-size: 12px;
      line-height: 1.5;
      color: var(--muted);
      background: color-mix(in srgb, var(--blue) 7%, transparent);
      border: 0.5px solid color-mix(in srgb, var(--blue) 25%, transparent);
      border-radius: 8px;
      padding: 8px 11px;
    }
    .fl-aluno-x {
      border: 0;
      background: none;
      color: inherit;
      cursor: pointer;
      font-size: 15px;
      line-height: 1;
      padding: 0 3px;
    }
    /* Sub-motivo do bloqueio (ex.: categoria do anexo barrado). */
    .blk-tag {
      display: inline-block;
      margin-left: 8px;
      font-size: 10.5px;
      font-weight: 600;
      padding: 1px 7px;
      border-radius: 999px;
      background: color-mix(in srgb, var(--danger, #d9534f) 15%, transparent);
      color: var(--danger, #d9534f);
      vertical-align: middle;
    }

    /* ── Botões do cabeçalho + copiloto do Gestor ─────────────────── */
    .head-btns {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
    /* Filtro de período: discreto, alinhado com os botões do canto sup. direito. */
    .head-period {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .hp-sel,
    .hp-mes {
      font: inherit;
      font-size: 12px;
      color: var(--muted);
      /* Fundo OPAQUE do tema (não 'transparent') — senão a lista de <option>
         aberta herda um fundo do sistema e fica ilegível em temas escuros. */
      background: var(--panel-bg);
      border: 0.5px solid var(--border);
      border-radius: 8px;
      padding: 5px 8px;
      cursor: pointer;
      max-width: 160px;
    }
    .hp-sel:hover,
    .hp-mes:hover {
      color: var(--sos-text);
      border-color: var(--sos-text);
    }
    .hp-mes {
      cursor: text;
    }
    .ibtn.primary {
      border-color: var(--blue);
      color: #fff;
      background: var(--blue);
    }
    .ibtn.primary:hover {
      filter: brightness(0.95);
      color: #fff;
    }
    /* Mobile / bloco estreito: empilha o cabeçalho, botões viram largura cheia,
       o seletor de instituição quebra pra linha própria. Evita o overflow
       horizontal que cortava o conteúdo. */
    @media (max-width: 560px) {
      .head {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
      }
      .head-l {
        align-items: baseline;
      }
      .title {
        font-size: 17px;
      }
      .head-btns {
        gap: 8px;
      }
      .head-btns .ibtn {
        flex: 1 1 auto;
        justify-content: center;
      }
      .scope {
        flex-wrap: wrap;
      }
      .scope .scope-sel,
      .scope select {
        flex: 1 1 100%;
        width: 100%;
      }
    }
    .gtoolbar {
      display: flex;
      gap: 8px;
      padding: 8px 16px;
      border-bottom: 0.5px solid var(--border);
    }
    .gtbtn {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      border-radius: 999px;
      padding: 4px 12px;
      font-size: 12px;
      cursor: pointer;
      color: var(--muted);
    }
    .gtbtn:hover {
      background: var(--hover-bg);
    }
    .gconvlist {
      max-height: 220px;
      overflow-y: auto;
      padding: 6px 16px;
      border-bottom: 0.5px solid var(--border);
      background: var(--hover-bg);
    }
    .gconvrow {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 2px;
      border-bottom: 0.5px solid var(--border);
      font-size: 13px;
    }
    .gconvrow:last-child {
      border-bottom: 0;
    }
    .gconvrow.ativa {
      /* Linha ativa: leve véu na cor de destaque do tema (fallback neutro pra
         browsers sem color-mix). Distingue a ativa sem cobrir o texto. */
      background: var(--track-bg);
      background: color-mix(in srgb, var(--sos-accent, #378add) 15%, transparent);
      border-radius: 6px;
    }
    .gconvname {
      flex: 1;
      min-width: 0;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .gconvname:hover {
      color: var(--blue);
    }
    .gconvmeta {
      color: var(--muted);
      font-size: 11px;
      margin-left: 6px;
    }
    .glink {
      border: 0;
      background: none;
      color: var(--blue);
      font-size: 12px;
      cursor: pointer;
      padding: 2px 4px;
      flex: none;
    }
    .glink.del {
      color: var(--red);
    }
    .glink:hover {
      text-decoration: underline;
    }
    .grename {
      flex: 1;
      min-width: 0;
      border: 0.5px solid var(--border);
      border-radius: 6px;
      padding: 4px 8px;
      font-size: 13px;
    }
    .gchat {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      padding: 14px 16px;
    }
    .gmsgs {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .gintro {
      font-size: 13px;
      color: var(--muted);
      background: var(--hover-bg);
      border-radius: 10px;
      padding: 12px 14px;
    }
    .gsug {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .gsug-h {
      margin-top: 6px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: var(--muted);
    }
    /* Chips de tipo de gráfico: visual mais neutro pra distinguir das perguntas. */
    .gchip-chart {
      color: var(--muted);
      font-size: 11.5px;
      padding: 6px 10px;
    }
    .gchip {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      border-radius: 999px;
      padding: 7px 12px;
      font-size: 12px;
      color: var(--blue);
      cursor: pointer;
      text-align: left;
    }
    .gchip:hover {
      border-color: var(--blue);
      background: var(--hover-bg);
    }
    .gbubble {
      max-width: 88%;
      padding: 10px 13px;
      border-radius: 12px;
      font-size: 14px;
      line-height: 1.45;
      white-space: pre-wrap;
    }
    .gbubble.user {
      align-self: flex-end;
      background: var(--blue);
      color: #fff;
      border-bottom-right-radius: 4px;
    }
    .gbubble.assistant {
      align-self: flex-start;
      background: var(--hover-bg);
      color: var(--sos-text);
      border-bottom-left-radius: 4px;
    }
    /* "Digitando…" antes do 1º token. */
    .gtyping {
      display: inline-flex;
      gap: 4px;
      align-items: center;
    }
    .gtyping span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--muted);
      animation: gtdot 1.2s infinite ease-in-out;
    }
    .gtyping span:nth-child(2) {
      animation-delay: 0.2s;
    }
    .gtyping span:nth-child(3) {
      animation-delay: 0.4s;
    }
    @keyframes gtdot {
      0%,
      60%,
      100% {
        opacity: 0.3;
        transform: translateY(0);
      }
      30% {
        opacity: 1;
        transform: translateY(-3px);
      }
    }
    /* Erro do chat + botão de re-tentar. */
    .gerr {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      color: #c0432f;
      padding: 8px 0;
      font-size: 13px;
    }
    .gretry {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      border-radius: 8px;
      padding: 5px 12px;
      font-size: 13px;
      cursor: pointer;
      color: var(--sos-text, #1a1a1a);
    }
    /* Bolha com markdown renderizado: layout por blocos (não pre-wrap). */
    .gmd {
      white-space: normal;
    }
    .gmd > div,
    .gmd > ul,
    .gmd > ol {
      margin: 0 0 6px;
    }
    .gmd > div:last-child,
    .gmd > ul:last-child,
    .gmd > ol:last-child {
      margin-bottom: 0;
    }
    .gmd-h {
      font-weight: 700;
      margin-top: 8px;
    }
    .gmd ul,
    .gmd ol {
      padding-left: 20px;
    }
    .gmd li {
      margin: 2px 0;
    }
    .gmd code {
      background: var(--hover-bg);
      padding: 1px 4px;
      border-radius: 4px;
      font-size: 0.92em;
    }
    .gmd strong {
      font-weight: 700;
    }
    /* Gráficos do Conect (fiéis aos dados do painel). */
    .gchart {
      margin: 10px 0;
      background: var(--panel-bg);
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 10px 12px;
    }
    .gchart-t {
      font-weight: 600;
      font-size: 13px;
      margin-bottom: 8px;
    }
    .gchart-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      margin-bottom: 6px;
    }
    .gchart-l {
      width: 120px;
      flex: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .gchart-track {
      flex: 1;
      height: 10px;
      background: var(--track-bg);
      border-radius: 5px;
      overflow: hidden;
    }
    .gchart-bar {
      display: block;
      height: 100%;
      background: var(--sos-accent, var(--blue));
      border-radius: 5px;
    }
    .gchart-v {
      width: 34px;
      text-align: right;
      color: var(--muted);
      flex: none;
    }
    .gchart-pie {
      display: flex;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
    }
    .gchart-leg {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 12px;
      min-width: 0;
    }
    .gchart-li {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .gchart-dot {
      width: 10px;
      height: 10px;
      border-radius: 3px;
      flex: none;
    }
    /* Porcentagem na legenda de pizza/rosca (composição). */
    .gchart-pct {
      width: 40px;
      text-align: right;
      font-weight: 600;
      color: var(--sos-text, #1a1a1a);
      flex: none;
    }
    .gchart-xaxis {
      display: flex;
      flex-wrap: wrap;
      gap: 4px 12px;
      font-size: 11px;
      color: var(--muted);
      margin-top: 6px;
    }
    .gchart-stack {
      display: flex;
      height: 22px;
      border-radius: 6px;
      overflow: hidden;
      margin: 4px 0 10px;
      background: var(--track-bg);
    }
    .gchart-seg {
      display: block;
      height: 100%;
    }
    /* Editor "Personalizar" (E3). */
    .pz {
      font-size: 13px;
    }
    /* Painel embutido estreito (bloco do Moodle ~300px): empilha os campos de
       texto/select pra não espremer o controle contra o rótulo. */
    @media (max-width: 380px) {
      .pz-field:has(input[type='text']),
      .pz-field:has(select) {
        flex-direction: column;
        align-items: stretch;
        gap: 4px;
      }
      .pz-field input[type='text'],
      .pz-field select {
        width: 100%;
      }
    }
    .pz-sec {
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--muted);
      margin: 14px 0 8px;
    }
    .pz-presets {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .pz-preset {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border: 1px solid;
      border-radius: 999px;
      cursor: pointer;
      font-size: 12px;
    }
    .pz-preset.active {
      outline: 2px solid var(--sos-accent, #378add);
      outline-offset: 1px;
    }
    .pz-pdot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex: none;
    }
    .pz-pname {
      font-weight: 600;
    }
    .pz-warn {
      margin: 2px 0 10px;
      padding: 7px 10px;
      border-radius: 8px;
      font-size: 12px;
      line-height: 1.35;
      /* Aviso: pílula âmbar OPACA (independe do tema) + texto marrom escuro —
         alto contraste (~7:1) tanto no claro quanto no escuro. */
      color: #5c3d00;
      background: #f6d78a;
      border: 0.5px solid #e0a83e;
    }
    .pz-field {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
    }
    .pz-field.col {
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
    }
    .pz-field input[type='color'] {
      width: 44px;
      height: 28px;
      padding: 0;
      border: 0.5px solid var(--border);
      border-radius: 6px;
      background: none;
    }
    .pz-field input[type='text'],
    .pz-field select {
      border: 0.5px solid var(--border);
      border-radius: 8px;
      padding: 7px 10px;
      font-size: 13px;
      font-family: inherit;
    }
    .pz-field input[type='range'] {
      flex: 1;
      max-width: 160px;
    }
    .pz-types {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 14px;
    }
    .pz-type {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      cursor: pointer;
    }
    .pz-card {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0 4px;
    }
    .pz-cardname {
      flex: 1;
    }
    .pz-cardcfg {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 8px;
      padding: 0 0 10px 34px;
      border-bottom: 0.5px solid var(--border);
      margin-bottom: 2px;
    }
    .pz-substats {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin: 0 0 10px 34px;
      padding: 8px 10px 8px 12px;
      border-left: 2px solid var(--border);
      border-bottom: 0.5px solid var(--border);
    }
    .pz-substats-h {
      font-size: 11px;
      font-weight: 600;
      color: var(--muted);
    }
    .pz-substat {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px 8px;
    }
    .pz-substat-name {
      font-size: 12px;
      min-width: 130px;
      font-weight: 500;
    }
    .pz-cc-sw {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      color: var(--muted);
      cursor: pointer;
    }
    .pz-cc-sw input[type='color'] {
      width: 26px;
      height: 24px;
      padding: 0;
      border: 0.5px solid var(--border);
      border-radius: 5px;
      background: none;
    }
    .pz-cc-font {
      border: 0.5px solid var(--border);
      border-radius: 6px;
      padding: 4px 6px;
      font-size: 12px;
      font-family: inherit;
    }
    .pz-emoji-wrap {
      position: relative;
      flex: none;
    }
    .pz-cc-emoji {
      width: 34px;
      height: 28px;
      border: 0.5px solid var(--border);
      border-radius: 6px;
      background: var(--panel-bg);
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
    }
    .pz-cc-emoji.empty {
      opacity: 0.4;
    }
    .pz-emoji-pop {
      position: absolute;
      z-index: 30;
      top: 32px;
      left: 0;
      background: var(--panel-bg);
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 6px;
      box-shadow: 0 8px 26px rgba(0, 0, 0, 0.2);
      display: grid;
      grid-template-columns: repeat(7, 26px);
      gap: 2px;
      width: max-content;
    }
    .pz-emoji-pop button {
      width: 26px;
      height: 26px;
      border: 0;
      background: none;
      border-radius: 6px;
      font-size: 17px;
      line-height: 1;
      cursor: pointer;
    }
    .pz-emoji-pop button:hover {
      background: var(--hover-bg);
    }
    .pz-emoji-none {
      grid-column: 1 / -1;
      width: auto !important;
      height: auto !important;
      padding: 4px !important;
      font-size: 12px !important;
      color: var(--muted);
    }
    .pz-cc-title {
      flex: 1;
      min-width: 0;
      border: 0.5px solid var(--border);
      border-radius: 6px;
      padding: 5px 8px;
      font-size: 13px;
      font-family: inherit;
    }
    .pz-cc-clear {
      flex: none;
      width: 24px;
      height: 24px;
      border: 0.5px solid var(--border);
      border-radius: 6px;
      background: var(--panel-bg);
      color: var(--muted);
      cursor: pointer;
    }
    .pz-tab {
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 10px;
      margin-bottom: 8px;
    }
    .pz-tab-head {
      display: flex;
      gap: 6px;
      margin-bottom: 8px;
    }
    .pz-tab-cards {
      display: flex;
      flex-direction: column;
      gap: 9px;
    }
    /* Grupos do editor de Abas (espelham os grupos do painel) */
    .pz-tab-grouphd {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 3px;
    }
    .pz-tab-grouprow {
      display: flex;
      flex-wrap: wrap;
      gap: 4px 14px;
      padding-left: 6px;
    }
    .pz-tab.ia {
      border-color: color-mix(in srgb, var(--blue) 45%, var(--border));
      background: color-mix(in srgb, var(--blue) 5%, transparent);
    }
    .pz-tab-badge {
      align-self: center;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.4px;
      color: #fff;
      background: var(--blue);
      border-radius: 6px;
      padding: 1px 6px;
    }
    .pz-tab-ia {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .pz-ta {
      width: 100%;
      box-sizing: border-box;
      resize: vertical;
      font: inherit;
      font-size: 12.5px;
      color: var(--sos-text, #1a1a1a);
      background: var(--panel-bg);
      border: 0.5px solid var(--border);
      border-radius: 8px;
      padding: 7px 9px;
    }
    .pz-ta:disabled {
      opacity: 0.6;
    }
    .pz-tab-btns {
      display: flex;
      gap: 8px;
      margin-top: 6px;
    }
    /* Aba de IA (corpo do painel) */
    .aia {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .aia-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
    .aia-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 650;
    }
    .aia-title svg {
      width: 18px;
      height: 18px;
    }
    .aia-gen {
      white-space: nowrap;
    }
    .aia-prompt {
      font-size: 13px;
      color: var(--sos-text, #1a1a1a);
      background: var(--hover-bg);
      border-radius: 8px;
      padding: 8px 10px;
    }
    .aia-prompt.muted {
      color: var(--muted);
    }
    .aia-loading,
    .aia-empty {
      color: var(--muted);
      font-size: 13px;
      padding: 14px 4px;
    }
    .aia-body {
      font-size: 14px;
      line-height: 1.55;
    }
    .aia-meta {
      color: var(--muted);
      font-size: 11px;
    }
    .aia-meta.warn {
      color: var(--amber);
    }
    .aia-updating {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12.5px;
      color: var(--muted);
      background: var(--hover-bg);
      border-radius: 8px;
      padding: 6px 10px;
    }
    /* Estado de carregamento "grande" (troca de período): substitui o conteúdo,
       centralizado e com presença, pra não mostrar a análise do período antigo. */
    .aia-loading-lg {
      justify-content: center;
      padding: 34px 14px;
      font-size: 13.5px;
    }
    .aia-spin {
      width: 12px;
      height: 12px;
      border: 2px solid var(--track-bg);
      border-top-color: var(--blue);
      border-radius: 50%;
      animation: aia-spin 0.7s linear infinite;
    }
    @keyframes aia-spin {
      to {
        transform: rotate(360deg);
      }
    }
    /* Relatório do período */
    .rcard {
      max-width: 820px;
    }
    .rtoolbar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 14px;
      border-bottom: 0.5px solid var(--border);
      flex-wrap: wrap;
    }
    .rsec-pick {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      border-bottom: 0.5px solid var(--border);
      flex-wrap: wrap;
    }
    .rsec-pick-l {
      font-size: 12px;
      font-weight: 600;
      color: var(--muted);
    }
    .rsec-chip {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      color: var(--muted);
      border-radius: 999px;
      padding: 4px 11px;
      font-size: 12px;
      font-family: inherit;
      cursor: pointer;
    }
    .rsec-chip.on {
      background: color-mix(in srgb, var(--blue) 16%, transparent);
      color: var(--blue);
      border-color: color-mix(in srgb, var(--blue) 40%, transparent);
      font-weight: 600;
    }
    .rsec-pick-hint {
      font-size: 11px;
      color: var(--muted);
      margin-left: auto;
    }
    .rperiodo {
      font-size: 12.5px;
      color: var(--muted);
    }
    .rspacer {
      flex: 1;
    }
    .rbody {
      overflow-y: auto;
      padding: 12px 16px 18px;
    }
    .rkpis {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
    }
    .rkpi {
      flex: 1 1 28%;
      min-width: 90px;
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 8px 10px;
      background: var(--panel-bg);
    }
    .rkpi-v {
      font-size: 20px;
      font-weight: 700;
    }
    .rkpi-l {
      font-size: 11.5px;
      color: var(--muted);
    }
    .rsec {
      margin: 12px 0;
      padding-bottom: 10px;
      border-bottom: 0.5px solid var(--border);
    }
    .rsec:last-child {
      border-bottom: 0;
    }
    .rsec-t {
      font-size: 14px;
      font-weight: 650;
      margin-bottom: 6px;
    }
    .rsec-md {
      font-size: 13.5px;
      line-height: 1.55;
    }
    /* Acesso (de-para papel→divisão) editável no painel */
    .acc-intro {
      font-size: 12.5px;
      color: var(--muted);
      line-height: 1.5;
      margin-bottom: 12px;
    }
    .acc-tbl {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    .acc-tbl th {
      text-align: left;
      font-weight: 600;
      font-size: 11.5px;
      color: var(--muted);
      padding: 6px 8px;
      border-bottom: 0.5px solid var(--border);
    }
    .acc-tbl td {
      padding: 8px;
      border-bottom: 0.5px solid var(--border);
      vertical-align: middle;
    }
    .acc-tbl tr.acc-off td {
      opacity: 0.55;
    }
    .acc-role {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12.5px;
    }
    .acc-inactive {
      color: var(--muted);
      font-size: 11.5px;
    }
    .acc-empty {
      color: var(--muted);
      text-align: center;
      padding: 16px 8px;
    }
    .acc-badge {
      display: inline-block;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.06);
      color: var(--muted);
    }
    .acc-badge.own {
      background: color-mix(in srgb, var(--blue) 16%, transparent);
      color: var(--blue);
    }
    .acc-legend {
      margin-top: 14px;
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 12px 14px;
      background: color-mix(in srgb, var(--muted) 5%, transparent);
    }
    .acc-legend-t {
      font-size: 11.5px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--muted);
      margin-bottom: 8px;
    }
    .acc-legend-item {
      display: flex;
      gap: 8px;
      font-size: 12.5px;
      line-height: 1.5;
      padding: 4px 0;
    }
    .acc-legend-item + .acc-legend-item {
      border-top: 0.5px solid var(--border);
    }
    .acc-legend-name {
      flex: 0 0 116px;
      font-weight: 600;
      color: var(--text);
    }
    .acc-legend-desc {
      color: var(--muted);
    }
    .acc-prof {
      font-weight: 600;
    }
    .co-summary {
      color: var(--muted);
      font-size: 12px;
    }
    /* Coordenador por pessoa (Fase A) */
    .pp-roles {
      display: inline-block;
      margin-left: 6px;
      font-size: 11px;
      color: var(--muted);
      background: rgba(127, 127, 127, 0.14);
      border-radius: 6px;
      padding: 1px 6px;
    }
    .pp-muted {
      color: var(--muted);
      font-style: italic;
    }
    .acc-chk.pp-inline {
      margin-left: 16px;
      padding-bottom: 0;
    }
    .co-name-in {
      width: 100%;
      max-width: 340px;
    }
    .acc-actions {
      text-align: right;
      white-space: nowrap;
    }
    /* Botão-ícone (editar/excluir) na linha do perfil. */
    .ic-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      margin-left: 4px;
      border: 0.5px solid var(--border);
      border-radius: 7px;
      background: var(--panel-bg);
      color: var(--muted);
      cursor: pointer;
      vertical-align: middle;
    }
    .ic-btn:hover {
      color: var(--sos-text);
      border-color: var(--muted);
      background: var(--hover-bg);
    }
    .ic-btn.danger:hover {
      color: var(--red);
      border-color: var(--red);
    }
    .co-restore-row {
      color: var(--green);
    }
    /* Barra de ações abaixo da tabela: "+ Novo perfil" à esquerda, "Voltar
       configuração original" separado à direita. */
    .co-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: 12px;
    }
    .gtbtn.ghost {
      background: transparent;
      color: var(--muted);
    }
    .gtbtn.ghost:hover {
      color: var(--sos-text);
      border-color: var(--muted);
    }
    .acc-row-clk {
      cursor: pointer;
    }
    .acc-row-clk:hover td {
      background: rgba(0, 0, 0, 0.035);
    }
    .acc-form {
      margin-top: 14px;
      padding: 12px;
      border: 0.5px solid var(--border);
      border-radius: 10px;
      background: var(--panel-bg);
    }
    .acc-form-t {
      font-size: 13px;
      font-weight: 650;
      margin-bottom: 10px;
    }
    .acc-form-row {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: flex-end;
    }
    .acc-fld {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 11.5px;
      color: var(--muted);
      flex: 1 1 180px;
    }
    .acc-in {
      font-size: 13px;
      padding: 7px 9px;
      border: 0.5px solid var(--border);
      border-radius: 8px;
      background: var(--panel-bg);
      color: var(--sos-text, #1a1a1a);
    }
    .acc-chk {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12.5px;
      padding-bottom: 7px;
    }
    .acc-form-btns {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }
    /* Coordenadores (concessão de acesso) */
    .co-head {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }
    .co-name {
      font-size: 15px;
      font-weight: 650;
    }
    .co-sec {
      margin: 0 0 14px;
      padding-bottom: 12px;
      border-bottom: 0.5px solid var(--border);
    }
    .co-sec-t {
      font-size: 13px;
      font-weight: 650;
      margin-bottom: 3px;
    }
    .co-hint {
      font-size: 11.5px;
      color: var(--muted);
      margin-bottom: 8px;
    }
    .co-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .co-chip {
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      color: var(--muted);
      border-radius: 999px;
      padding: 5px 12px;
      font-size: 12.5px;
      font-family: inherit;
      cursor: pointer;
    }
    .co-chip.on {
      background: color-mix(in srgb, var(--blue) 16%, transparent);
      border-color: var(--blue);
      color: var(--blue);
      font-weight: 600;
    }
    .co-anogrp {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 6px;
      flex-wrap: wrap;
    }
    .co-anogrp-t {
      font-size: 11.5px;
      color: var(--muted);
      min-width: 96px;
      font-weight: 600;
    }
    .pz-mini {
      width: 26px;
      height: 26px;
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      border-radius: 6px;
      cursor: pointer;
    }
    .pz-mini:disabled {
      opacity: 0.35;
      cursor: default;
    }
    .pz-eye {
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--muted);
      font-size: 12px;
    }
    .pz-actions {
      display: flex;
      gap: 8px;
      margin-top: 16px;
    }
    .pz-btn {
      flex: 1;
      border: 0.5px solid var(--border);
      background: var(--panel-bg);
      border-radius: 9px;
      padding: 9px;
      font-size: 13px;
      cursor: pointer;
    }
    .pz-btn.primary {
      background: var(--blue);
      color: #fff;
      border-color: var(--blue);
    }
    .pz-btn:disabled {
      opacity: 0.6;
    }
    /* ── Upload da logo: zona de arrastar-e-soltar ────────────────── */
    .pz-drop {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 104px;
      padding: 16px 14px;
      text-align: center;
      border: 1.5px dashed var(--border);
      border-radius: 12px;
      background: var(--panel-bg);
      color: var(--muted);
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
    }
    .pz-drop:hover {
      border-color: var(--blue);
    }
    .pz-drop.drag {
      border-color: var(--blue);
      background: var(--hover-bg);
    }
    .pz-drop.busy {
      cursor: default;
    }
    .pz-drop-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }
    .pz-drop.busy .pz-drop-input {
      pointer-events: none;
    }
    .pz-drop-ico {
      width: 26px;
      height: 26px;
      color: var(--blue);
    }
    .pz-drop-t {
      font-size: 13px;
      color: var(--sos-text);
    }
    .pz-drop-t b {
      color: var(--blue);
      font-weight: 600;
    }
    .pz-drop-h {
      font-size: 11px;
      color: var(--muted);
    }
    .pz-drop-prev {
      max-height: 48px;
      max-width: 150px;
      object-fit: contain;
      border-radius: 6px;
    }
    .pz-drop-x {
      position: absolute;
      top: 6px;
      right: 6px;
      z-index: 2;
      width: 22px;
      height: 22px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0.5px solid var(--border);
      border-radius: 50%;
      background: var(--panel-bg);
      color: var(--muted);
      font-size: 12px;
      cursor: pointer;
    }
    .pz-drop-x:hover {
      color: var(--red);
      border-color: var(--red);
    }
    .pz-spin {
      width: 24px;
      height: 24px;
      border: 2.5px solid var(--track-bg);
      border-top-color: var(--blue);
      border-radius: 50%;
      animation: pz-spin 0.7s linear infinite;
    }
    @keyframes pz-spin {
      to {
        transform: rotate(360deg);
      }
    }
    .pz-msg {
      margin-top: 10px;
      color: var(--green);
      font-size: 13px;
    }
    /* Indicador de auto-save no topo do editor (bem visível). */
    .pz-savestate {
      font-size: 12px;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 999px;
      white-space: nowrap;
      margin-left: auto;
      margin-right: 10px;
    }
    .pz-savestate.saving,
    .pz-savestate.dirty {
      color: var(--muted);
      background: color-mix(in srgb, var(--muted) 14%, transparent);
    }
    .pz-savestate.saved {
      color: #0b7a44;
      background: color-mix(in srgb, #1d9e75 20%, transparent);
    }
    .pz-savestate.error {
      color: #b4271a;
      background: color-mix(in srgb, #c0432f 18%, transparent);
    }
    .pz-savestate.idle {
      display: none;
    }
    .ginput {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }
    .ginput input {
      flex: 1;
      border: 0.5px solid var(--border);
      border-radius: 10px;
      padding: 11px 13px;
      font-size: 14px;
      font-family: inherit;
      outline: none;
    }
    .ginput input:focus {
      border-color: var(--blue);
    }
    .ginput button {
      border: 0;
      background: var(--blue);
      color: #fff;
      border-radius: 10px;
      padding: 0 18px;
      font-size: 14px;
      cursor: pointer;
    }
    .ginput button:disabled {
      opacity: 0.5;
      cursor: default;
    }
  `;

  firstUpdated(): void {
    void this.load();
    void this.loadEscolas();
  }

  updated(): void {
    // Deep-link do menu (página dedicada ?conect=1): abre o Conect uma vez, quando
    // os dados já chegaram (permissões conhecidas) e o usuário pode usar o copiloto.
    if (this.openConect && this.data && this.canCopilot && !this._conectAutoOpened) {
      this._conectAutoOpened = true;
      void this.openGestor();
    }
    // Aba de IA ativa: acompanha o período/escola selecionado no painel.
    const tabs = this.tema?.tabs ?? [];
    const active = tabs.find((t) => t.id === this.activeTabId());
    if (active?.kind !== 'ia' || this.abaIALoading !== '') return;
    const res = this.abaIA[active.id];
    if (res === undefined) {
      void this.loadAbaIA(active.id); // 1º: busca o cache (rápido, sem IA)
      return;
    }
    // Gestor: se o resultado é de OUTRO período que o selecionado (ou ainda vazio) e
    // a aba tem prompt, regenera pro período atual — a aba "segue" o painel. Um
    // resultado gerado agora fica com o período igual → não re-dispara (converge).
    // res===null (falha de GET) não regenera sozinho — evita loop de erro.
    if (res && this.isManager && (active.ai_prompt || '').trim()) {
      const { from, to } = this.periodRange();
      const stale =
        !res.result_md || res.periodo_de !== from || res.periodo_ate !== to;
      if (stale) void this.generateAbaIA(active.id);
    }
  }

  private async loadEscolas(): Promise<void> {
    // Lista de instituições do portal (vazia = não é Gestor de Rede → sem seletor).
    try {
      this.escolas = await this.fetchJson<{ id: string; nome: string | null }[]>(
        '/parceiro/escolas',
      );
    } catch {
      this.escolas = [];
    }
  }

  private onEscolaChange(e: Event): void {
    this.escolaSel = (e.target as HTMLSelectElement).value;
    // Filtros granulares são por-escola (etapas/matérias diferem) → zera ao trocar;
    // loadFiltros() (via load) repovoa as opções do novo escopo.
    this.filtroEtapa = '';
    this.filtroAnoSerie = '';
    this.filtroMateria = '';
    this.filtroAluno = '';
    this.filtroAlunoNome = '';
    this.alunoBusca = [];
    // Invalida tudo que era escopado à escola anterior (escopo, copiloto, drill).
    this.drill = undefined;
    this.showInfo = false;
    this.escopo = undefined;
    this.escopoError = '';
    this.showGestor = false;
    this.gestorSessionId = null;
    this.gestorMsgs = [];
    this.gestorStreamingText = '';
    this.gestorStreaming = false;
    this.invalidateScopedInsights(); // aba de IA e relatório são por escopo
    void this.load();
  }

  /** Invalida o que é escopado à escola/período (aba de IA + relatório) — evita
   * mostrar dados do escopo anterior ao trocar de instituição/período. */
  private invalidateScopedInsights(): void {
    this.abaIA = {};
    this.relatorio = undefined;
    this.relatorioError = '';
    this.showRelatorio = false;
    // Insights automáticos também são escopados (escola+período) — limpa pra não
    // exibir os da instituição/período anterior enquanto o novo carrega.
    this.autoInsights = undefined;
    this.autoInsightsErr = '';
    this._aiTriedKey = '';
  }

  // ─── Filtros granulares (etapa/ano-série/matéria) ────────────────────────────
  private async loadFiltros(): Promise<void> {
    try {
      const q = this.escolaSel !== 'todas' ? `?escola=${encodeURIComponent(this.escolaSel)}` : '';
      this.filtros = await this.fetchJson<FiltrosData>(`/parceiro/filtros${q}`);
      // Se o valor selecionado não existe mais no escopo atual, limpa (evita filtro
      // "fantasma" que zera os dados). Matéria só vale em escola específica.
      if (
        this.filtroEtapa &&
        this.filtroEtapa !== '__sem__' &&
        !this.filtros.etapas.includes(this.filtroEtapa)
      )
        this.filtroEtapa = '';
      if (
        this.filtroAnoSerie &&
        this.filtroAnoSerie !== '__sem__' &&
        !this.filtros.anos_serie.some((a) => a.ano_serie === this.filtroAnoSerie)
      )
        this.filtroAnoSerie = '';
      if (
        this.filtroMateria &&
        (!this.filtros.materia_disponivel ||
          !this.filtros.materias.some((m) => m.id === this.filtroMateria))
      )
        this.filtroMateria = '';
    } catch {
      /* filtros são opcionais — sem eles, os combos não aparecem */
    }
  }

  /** Aplica a troca de um filtro granular: fecha drills/overlays escopados e recarrega. */
  private applyFiltroChange(): void {
    this.drill = undefined;
    this.showInfo = false;
    this.invalidateScopedInsights();
    void this.load();
  }

  private onEtapaChange(e: Event): void {
    this.filtroEtapa = (e.target as HTMLSelectElement).value;
    // Ano-série depende da etapa: se o ano atual não pertence à nova etapa, limpa.
    if (
      this.filtroAnoSerie &&
      this.filtroAnoSerie !== '__sem__' &&
      this.filtroEtapa &&
      this.filtroEtapa !== '__sem__' &&
      !(this.filtros?.anos_serie ?? []).some(
        (a) => a.ano_serie === this.filtroAnoSerie && a.etapa === this.filtroEtapa,
      )
    )
      this.filtroAnoSerie = '';
    this.applyFiltroChange();
  }
  private onAnoSerieChange(e: Event): void {
    this.filtroAnoSerie = (e.target as HTMLSelectElement).value;
    this.applyFiltroChange();
  }
  private onMateriaChange(e: Event): void {
    this.filtroMateria = (e.target as HTMLSelectElement).value;
    this.applyFiltroChange();
  }

  /** Busca de aluno (debounced) pro dropdown do filtro. */
  private onAlunoSearch(e: Event): void {
    const q = (e.target as HTMLInputElement).value;
    this.alunoBuscaAberta = true;
    clearTimeout(this._alunoBuscaTimer);
    this._alunoBuscaTimer = window.setTimeout(() => {
      void this.fetchAlunoBusca(q);
    }, 250);
  }
  private async fetchAlunoBusca(q: string): Promise<void> {
    try {
      const p = new URLSearchParams();
      if (this.escolaSel !== 'todas') p.set('escola', this.escolaSel);
      if (q.trim()) p.set('q', q.trim());
      this.alunoBusca = await this.fetchJson<AlunoBuscaRow[]>(
        `/parceiro/alunos-busca?${p.toString()}`,
      );
    } catch {
      this.alunoBusca = [];
    }
  }
  private selectAluno(a: AlunoBuscaRow): void {
    this.filtroAluno = a.user_id;
    this.filtroAlunoNome = a.name ?? a.user_id;
    this.alunoBuscaAberta = false;
    this.alunoBusca = [];
    this.applyFiltroChange();
  }
  private clearAluno(): void {
    this.filtroAluno = '';
    this.filtroAlunoNome = '';
    this.alunoBuscaAberta = false;
    this.alunoBusca = [];
    this.applyFiltroChange();
  }

  private onPeriodoChange(e: Event): void {
    const v = (e.target as HTMLSelectElement).value;
    if (v === 'custom') {
      // "Mês específico…": revela o seletor de mês já no mês atual.
      this.periodo = 'custom';
      if (!this.periodoMes) {
        const now = new Date();
        this.periodoMes = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      }
    } else {
      this.periodo = v;
      this.periodoMes = ''; // preset limpa o mês específico
    }
    this.drill = undefined;
    this.showInfo = false;
    this.invalidateScopedInsights(); // aba de IA e relatório dependem do período
    void this.load();
  }

  private onPeriodoMesChange(e: Event): void {
    this.periodoMes = (e.target as HTMLInputElement).value; // YYYY-MM ('' = volta ao preset)
    this.drill = undefined;
    this.showInfo = false;
    this.invalidateScopedInsights();
    void this.load();
  }

  private static _d(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  }

  /** Badge de tendência (vs. período anterior). `invert`=true para métricas em que
   *  SUBIR é ruim (ex.: alertas de segurança): verde quando cai, vermelho quando sobe. */
  /** Minutos → "Xh Ymin" / "X min" / "Xs" (pro tempo total de utilização). */
  private fmtDuracao(min: number): string {
    if (!min) return '0 min';
    if (min < 1) return `${Math.round(min * 60)}s`;
    if (min < 60) return `${Math.round(min)} min`;
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return m ? `${h}h ${m}min` : `${h}h`;
  }

  private deltaBadge(
    v: number | null | undefined,
    invert = false,
    prev?: number | null,
    cur?: number | null,
  ): TemplateResult | '' {
    if (v === null || v === undefined || v === 0) return '';
    const up = v > 0;
    const good = invert ? !up : up;
    // v2 §8: percentual SEMPRE com a base absoluta — "+233%" sozinho engana
    // (pode ser 3→10). O par prev→cur aparece junto e o tooltip explica.
    const abs =
      prev !== null && prev !== undefined && cur !== null && cur !== undefined
        ? ` · ${prev}→${cur}`
        : '';
    const tip =
      prev !== null && prev !== undefined && cur !== null && cur !== undefined
        ? `de ${prev} para ${cur} vs. período anterior (${up ? '+' : '−'}${Math.abs(cur - prev)})`
        : 'vs. período anterior';
    return html`<span class="delta ${good ? 'good' : 'bad'}" title=${tip}
      >${up ? '▲' : '▼'} ${Math.abs(v)}%${abs}</span
    >`;
  }

  /** Intervalo [from, to] (YYYY-MM-DD) do período selecionado, calculado no fuso
   * LOCAL do usuário. "Todo período" manda uma data bem antiga (o backend, sem
   * from/to, cairia no mês corrente). */
  private periodRange(): { from: string; to: string } {
    const D = SosDashboard._d;
    const now = new Date();
    if (this.periodoMes) {
      const [y, m] = this.periodoMes.split('-').map(Number);
      return { from: D(new Date(y, m - 1, 1)), to: D(new Date(y, m, 0)) };
    }
    const mkFrom = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return D(d);
    };
    switch (this.periodo) {
      case 'mes_passado':
        return {
          from: D(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          to: D(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
      case '7d':
        return { from: mkFrom(6), to: D(now) };
      case '30d':
        return { from: mkFrom(29), to: D(now) };
      case '90d':
        return { from: mkFrom(89), to: D(now) };
      case 'ano':
        return { from: D(new Date(now.getFullYear(), 0, 1)), to: D(now) };
      case 'tudo':
        return { from: '2000-01-01', to: D(now) };
      case 'mes_atual':
      default:
        return { from: D(new Date(now.getFullYear(), now.getMonth(), 1)), to: D(now) };
    }
  }

  /** Rótulo curto do período (pros cards e subtítulos). */
  private periodLabel(): string {
    if (this.periodoMes) {
      const [y, m] = this.periodoMes.split('-').map(Number);
      const nome = new Date(y, m - 1, 1).toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
      });
      return nome;
    }
    return (
      {
        mes_atual: 'mês atual',
        mes_passado: 'mês passado',
        '7d': 'últimos 7 dias',
        '30d': 'últimos 30 dias',
        '90d': 'últimos 90 dias',
        ano: 'este ano',
        tudo: 'todo período',
      }[this.periodo] ?? 'mês atual'
    );
  }

  /** Query (?escola&from&to[&etapa&ano_serie&categoria_id]) do escopo+período+filtros
   * granulares pras chamadas de métrica. */
  private scopeQuery(): string {
    const p = new URLSearchParams();
    if (this.escolaSel !== 'todas') p.set('escola', this.escolaSel);
    const { from, to } = this.periodRange();
    p.set('from', from);
    p.set('to', to);
    if (this.filtroEtapa) p.set('etapa', this.filtroEtapa);
    if (this.filtroAnoSerie) p.set('ano_serie', this.filtroAnoSerie);
    // Matéria só vale em escola específica (id da categoria é por-tenant).
    if (this.filtroMateria && this.escolaSel !== 'todas')
      p.set('categoria_id', this.filtroMateria);
    if (this.filtroAluno) p.set('aluno', this.filtroAluno);
    return `?${p.toString()}`;
  }

  private async fetchJson<T>(path: string): Promise<T> {
    const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard${path}`);
    if (!r.ok) {
      throw new Error(
        r.status === 403
          ? 'Acesso restrito ao staff da escola.'
          : `Erro ${r.status} ao carregar.`,
      );
    }
    return (await r.json()) as T;
  }

  private async load(): Promise<void> {
    if (!this.apiBase || !this.token) {
      this.loading = false;
      this.error = 'Configuração ausente (api-base/token).';
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const escolaQ =
        this.escolaSel !== 'todas'
          ? `?escola=${encodeURIComponent(this.escolaSel)}`
          : '';
      this.data = await this.fetchJson<ParceiroData>(`/parceiro${this.scopeQuery()}`);
      void this.loadTema(escolaQ); // tema não depende de período
      void this.loadFiltros(); // opções dos combos (etapa/ano-série/matéria) do escopo
      void this.loadAutoInsights(); // interpretação automática (escola+período)
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  /** Insights automáticos: lê o cache; se o gestor está vendo e está vazio/de outro
   *  período, gera na hora (fica "automático"). Escopo = escola + período (não segue
   *  os filtros granulares — é a leitura institucional, igual ao relatório). */
  /** Chave do escopo+período atual (escola|de|até) — base da staleness e da guarda. */
  /** Há filtro granular ativo (etapa/ano/matéria/aluno)? Muda o comportamento dos
   * Insights da IA: o cache global do escopo não vale pro recorte. */
  private hasFiltroGranular(): boolean {
    return !!(
      this.filtroEtapa ||
      this.filtroAnoSerie ||
      this.filtroMateria ||
      this.filtroAluno
    );
  }

  private aiScopeKey(): string {
    const { from, to } = this.periodRange();
    // Inclui os filtros: cada recorte gera (e guarda em memória) os próprios insights.
    return `${this.escolaSel}|${from}|${to}|${this.filtroEtapa}|${this.filtroAnoSerie}|${this.filtroMateria}|${this.filtroAluno}`;
  }
  /** O cache é de OUTRO período? Compara as DATAS reais [de,até] (o rótulo é fixo em
   *  períodos relativos como "últimos 7 dias" e não detecta a janela deslizando). */
  private autoInsightsStale(res: AutoInsightsData): boolean {
    const { from, to } = this.periodRange();
    return !res.insights_md || res.periodo_de !== from || res.periodo_ate !== to;
  }
  private async loadAutoInsights(): Promise<void> {
    this.autoInsightsErr = '';
    try {
      const q =
        this.escolaSel !== 'todas' ? `?escola=${encodeURIComponent(this.escolaSel)}` : '';
      const res = await this.insightsFetch<AutoInsightsData>(`/insights/auto${q}`);
      // Com FILTRO granular ativo, o cache global do escopo NÃO vale pro recorte
      // (falaria da escola toda no plural) — não exibe; gera do recorte abaixo.
      if (this.hasFiltroGranular()) {
        this.autoInsights = { ...res, insights_md: null, generated_at: null };
        if (
          res.can_generate &&
          !this.autoInsightsLoading &&
          this._aiTriedKey !== this.aiScopeKey()
        ) {
          void this.generateAutoInsights();
        }
        return;
      }
      this.autoInsights = res;
      // Auto-gera (gestor) quando obsoleto — mas só 1× por escopo/período: se falhar,
      // não fica re-tentando a cada reload.
      if (
        res.can_generate &&
        this.autoInsightsStale(res) &&
        !this.autoInsightsLoading &&
        this._aiTriedKey !== this.aiScopeKey()
      ) {
        void this.generateAutoInsights();
      }
    } catch (e) {
      this.autoInsightsErr = (e as Error).message;
    }
  }
  private async generateAutoInsights(): Promise<void> {
    this._aiTriedKey = this.aiScopeKey(); // marca a tentativa (sucesso OU falha)
    this.autoInsightsLoading = true;
    this.autoInsightsErr = '';
    try {
      // Filtros granulares vão junto: o backend gera do snapshot FILTRADO (recorte,
      // singular pra 1 aluno) e não grava o cache global.
      this.autoInsights = await this.insightsFetch<AutoInsightsData>(
        '/insights/auto/gerar',
        {
          method: 'POST',
          body: JSON.stringify({
            ...this.insightsScope(),
            etapa: this.filtroEtapa || undefined,
            ano_serie: this.filtroAnoSerie || undefined,
            categoria_id: this.filtroMateria || undefined,
            aluno: this.filtroAluno || undefined,
          }),
        },
      );
    } catch (e) {
      this.autoInsightsErr = (e as Error).message;
    } finally {
      this.autoInsightsLoading = false;
    }
  }

  private async loadTema(q: string): Promise<void> {
    try {
      const r = await this.fetchJson<{
        resolved: ThemeData;
        presets?: { nome: string; tema: Partial<ThemeData> }[];
      }>(`/parceiro/tema${q}`);
      this.tema = r.resolved;
      if (r.presets) this.temaPresets = r.presets;
      this.applyTheme();
    } catch {
      /* tema é opcional — sem ele, o painel usa os defaults do CSS */
    }
  }

  // ─── Abas de IA + Relatório ──────────────────────────────────────────────
  private insightsScope(): {
    escola: string | null;
    periodo_de: string;
    periodo_ate: string;
    periodo_label: string;
  } {
    const { from, to } = this.periodRange();
    return {
      escola: this.escolaSel !== 'todas' ? this.escolaSel : null,
      periodo_de: from,
      periodo_ate: to,
      periodo_label: this.periodLabel(),
    };
  }

  private async insightsFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard/insights${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers || {}),
      },
    });
    if (!r.ok) {
      let detail = r.status === 403 ? 'Ação restrita a gestores.' : `Erro ${r.status}.`;
      try {
        const j = (await r.json()) as { detail?: string };
        if (j?.detail) detail = j.detail;
      } catch {
        /* corpo não-JSON */
      }
      throw new Error(detail);
    }
    return (await r.json()) as T;
  }

  /** Carrega o resultado CACHEADO de uma aba de IA (não gera nada). */
  private async loadAbaIA(tabId: string): Promise<void> {
    if (this.abaIALoading) return;
    this.abaIALoading = tabId;
    this.abaIAError = '';
    try {
      const q = new URLSearchParams({ tab_id: tabId });
      if (this.escolaSel !== 'todas') q.set('escola', this.escolaSel);
      const res = await this.insightsFetch<AbaIAResult>(`/aba?${q.toString()}`);
      this.abaIA = { ...this.abaIA, [tabId]: res };
    } catch (e) {
      this.abaIAError = (e as Error).message;
      this.abaIA = { ...this.abaIA, [tabId]: null };
    } finally {
      this.abaIALoading = '';
    }
  }

  /** Gera/atualiza o resultado de uma aba de IA (gestor). */
  private async generateAbaIA(tabId: string): Promise<void> {
    this.abaIALoading = tabId;
    this.abaIAError = '';
    try {
      const res = await this.insightsFetch<AbaIAResult>('/aba/gerar', {
        method: 'POST',
        body: JSON.stringify({ tab_id: tabId, ...this.insightsScope() }),
      });
      this.abaIA = { ...this.abaIA, [tabId]: res };
    } catch (e) {
      this.abaIAError = (e as Error).message;
    } finally {
      this.abaIALoading = '';
    }
  }

  private openRelatorio(): void {
    this.showInfo = false;
    this.drill = undefined;
    this.showGestor = false;
    this.showConfig = false;
    this.showRelatorio = true;
    if (!this.relatorio) void this.generateRelatorio();
  }
  private closeRelatorio(): void {
    this.showRelatorio = false;
  }

  private async generateRelatorio(): Promise<void> {
    this.relatorioLoading = true;
    this.relatorioError = '';
    try {
      this.relatorio = await this.insightsFetch<{
        report_id: string;
        report: ReportData;
      }>('/relatorio/gerar', {
        method: 'POST',
        body: JSON.stringify({
          ...this.insightsScope(),
          sections: this.relatorioSecoes, // [] = todas
          // v2 crit. 11: o relatório respeita o contexto exibido (filtros ativos).
          etapa: this.filtroEtapa || undefined,
          ano_serie: this.filtroAnoSerie || undefined,
          categoria_id: this.filtroMateria || undefined,
          aluno: this.filtroAluno || undefined,
        }),
      });
    } catch (e) {
      this.relatorioError = (e as Error).message;
    } finally {
      this.relatorioLoading = false;
    }
  }

  /** Alterna uma seção do relatório. Chip "Todas" = lista vazia (sem restrição);
   *  clicar numa seção com "Todas" ativa passa a restringir SÓ àquela. */
  private toggleRelatorioSecao(key: string): void {
    const cur = this.relatorioSecoes;
    if (cur.length === 0) {
      this.relatorioSecoes = [key];
    } else if (cur.includes(key)) {
      const next = cur.filter((k) => k !== key);
      // Desmarcar a última volta pra "Todas" (nunca fica vazio-sem-sentido).
      this.relatorioSecoes = next;
    } else {
      const next = [...cur, key];
      // Todas marcadas → volta a "Todas" (lista vazia = sem restrição).
      this.relatorioSecoes = next.length >= REPORT_SECTIONS.length ? [] : next;
    }
  }
  private clearRelatorioSecoes(): void {
    this.relatorioSecoes = [];
  }

  private async downloadRelatorioPdf(): Promise<void> {
    if (!this.relatorio) return;
    this.relatorioPdfLoading = true;
    this.relatorioError = '';
    try {
      const r = await this.authFetch(
        `${this.apiBase}/api/v1/dashboard/insights/relatorio/${encodeURIComponent(
          this.relatorio.report_id,
        )}/pdf`,
      );
      if (!r.ok) throw new Error(`Erro ${r.status} ao gerar o PDF.`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'relatorio.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      this.relatorioError = (e as Error).message;
    } finally {
      this.relatorioPdfLoading = false;
    }
  }

  // ─── Acesso (de-para papel→divisão) — editável no painel pelo gestor ─────────
  private async accessFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard/access${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers || {}),
      },
    });
    if (!r.ok) {
      let detail = r.status === 403 ? 'Ação restrita a gestores.' : `Erro ${r.status}.`;
      try {
        const j = (await r.json()) as { detail?: string };
        if (j?.detail) detail = j.detail;
      } catch {
        /* corpo não-JSON */
      }
      throw new Error(detail);
    }
    if (r.status === 204) return undefined as T;
    return (await r.json()) as T;
  }

  private async loadAcesso(): Promise<void> {
    this.acessoLoading = true;
    this.acessoError = '';
    try {
      this.acesso = await this.accessFetch<AccessOverview>('/overview');
    } catch (e) {
      this.acessoError = (e as Error).message;
    } finally {
      this.acessoLoading = false;
    }
  }

  private startNovoPapel(): void {
    const firstDiv = this.acesso?.divisions[0]?.id ?? '';
    this.acessoEditing = {
      id: null,
      moodle_role: '',
      division_id: firstDiv,
      profile_id: null,
      active: true,
    };
    this.acessoError = '';
  }
  private startEditPapel(row: AccessRoleMapRow): void {
    // Herdado (global) → cria override (id=null, prefill). Personalizado → edita.
    this.acessoEditing = {
      id: row.scope === 'personalizado' ? row.id : null,
      moodle_role: row.moodle_role,
      division_id: row.division_id,
      profile_id: row.profile_id,
      active: row.active,
    };
    this.acessoError = '';
  }
  private cancelEditPapel(): void {
    this.acessoEditing = undefined;
    this.acessoError = '';
  }

  /** Interpreta a escolha do dropdown de alvo: `p:<id>` = perfil; `d:<id>` = divisão. */
  private setAccTarget(value: string): void {
    const ed = this.acessoEditing;
    if (!ed) return;
    if (value.startsWith('p:')) {
      this.acessoEditing = { ...ed, profile_id: value.slice(2) };
    } else {
      this.acessoEditing = {
        ...ed,
        profile_id: null,
        division_id: value.startsWith('d:') ? value.slice(2) : value,
      };
    }
  }

  private async saveRoleMap(): Promise<void> {
    const ed = this.acessoEditing;
    if (!ed) return;
    const role = ed.moodle_role.trim().toLowerCase();
    if (!role || (!ed.division_id && !ed.profile_id)) {
      this.acessoError = 'Informe o papel e a divisão (ou perfil).';
      return;
    }
    this.acessoSaving = true;
    this.acessoError = '';
    try {
      // Perfil setado → manda profile_id (backend força a divisão 'coordenador');
      // senão, a divisão-base escolhida.
      const body = JSON.stringify(
        ed.profile_id
          ? { moodle_role: role, profile_id: ed.profile_id, division_id: ed.division_id, active: ed.active }
          : { moodle_role: role, division_id: ed.division_id, profile_id: null, active: ed.active },
      );
      if (ed.id) {
        await this.accessFetch(`/role-map/${encodeURIComponent(ed.id)}`, {
          method: 'PUT',
          body,
        });
      } else {
        await this.accessFetch('/role-map', { method: 'POST', body });
      }
      this.acessoEditing = undefined;
      await this.loadAcesso();
    } catch (e) {
      this.acessoError = (e as Error).message;
    } finally {
      this.acessoSaving = false;
    }
  }

  private async deleteRoleMap(row: AccessRoleMapRow): Promise<void> {
    if (row.scope !== 'personalizado') return;
    this.acessoSaving = true;
    this.acessoError = '';
    try {
      await this.accessFetch(`/role-map/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
      });
      await this.loadAcesso();
    } catch (e) {
      this.acessoError = (e as Error).message;
    } finally {
      this.acessoSaving = false;
    }
  }

  // ─── Perfis de coordenador (substituem o grant por-pessoa) ───────────────────
  private backToCoordList(): void {
    this.coordEditing = undefined;
    this.coordError = '';
  }

  private async loadCoordList(): Promise<void> {
    this.coordLoading = true;
    this.coordError = '';
    try {
      this.coordList = await this.accessFetch<ProfileRow[]>('/perfis');
    } catch (e) {
      this.coordError = (e as Error).message;
    } finally {
      this.coordLoading = false;
    }
  }

  /** Excluir = inativar (soft, reversível) / Restaurar = reativar um perfil.
   * Inativo derruba o coordenador pro genérico (load_grant exige perfil ativo). */
  private async setCoordActive(c: ProfileRow, active: boolean): Promise<void> {
    if (
      !active &&
      !window.confirm(
        `Inativar o perfil "${c.name}"? Coordenadores com este perfil voltam ao ` +
          `acesso genérico até você restaurar. Nada é apagado.`,
      )
    )
      return;
    this.coordLoading = true;
    this.coordError = '';
    try {
      await this.accessFetch(`/perfil/${encodeURIComponent(c.id)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      });
      await this.loadCoordList();
    } catch (e) {
      this.coordError = (e as Error).message;
      this.coordLoading = false;
    }
  }

  /** Voltar configuração original: recria (ou reseta) o perfil-base "Coordenador"
   * com acesso total — o "coordenador inicial" de fábrica. */
  private async restoreOriginalCoord(): Promise<void> {
    if (
      !window.confirm(
        'Voltar à configuração original? Isso recria (ou reseta) o perfil ' +
          '"Coordenador" com acesso total: todas as instituições, etapas, matérias ' +
          'e recursos, Ativo.',
      )
    )
      return;
    this.coordLoading = true;
    this.coordError = '';
    try {
      await this.accessFetch('/perfil/restaurar-original', { method: 'POST' });
      await this.loadCoordList();
    } catch (e) {
      this.coordError = (e as Error).message;
      this.coordLoading = false;
    }
  }

  /** Abre o editor de um perfil existente (id) ou um novo (id undefined). */
  private async openCoordGrant(profileId?: string): Promise<void> {
    this.coordLoading = true;
    this.coordError = '';
    try {
      this.coordEditing = await this.accessFetch<CoordinatorDetail>(
        profileId ? `/perfil/${encodeURIComponent(profileId)}` : '/perfil/novo',
      );
    } catch (e) {
      this.coordError = (e as Error).message;
    } finally {
      this.coordLoading = false;
    }
  }

  private setGrantName(value: string): void {
    const ed = this.coordEditing;
    if (!ed) return;
    this.coordEditing = { ...ed, profile: { ...ed.profile, name: value } };
  }

  private toggleGrantList(
    field:
      | 'allowed_tenant_ids'
      | 'allowed_etapas'
      | 'allowed_ano_series'
      | 'allowed_materias',
    value: string,
  ): void {
    const ed = this.coordEditing;
    if (!ed) return;
    const cur = ed.profile[field];
    const next = cur.includes(value)
      ? cur.filter((v) => v !== value)
      : [...cur, value];
    this.coordEditing = { ...ed, profile: { ...ed.profile, [field]: next } };
  }
  private toggleGrantTab(tabId: string): void {
    const ed = this.coordEditing;
    if (!ed) return;
    const all = ed.options.tabs.map((t) => t.id);
    const cur = ed.profile.allowed_tabs; // null = todas (sem restrição)
    let next: string[] | null;
    if (cur === null) {
      // "Todas" ativa → clicar numa aba passa a restringir SÓ àquela (mesmo
      // padrão das demais dimensões: seleção explícita).
      next = [tabId];
    } else {
      next = cur.includes(tabId)
        ? cur.filter((v) => v !== tabId)
        : [...cur, tabId];
      if (next.length >= all.length) next = null; // todas marcadas → sem restrição
    }
    this.coordEditing = { ...ed, profile: { ...ed.profile, allowed_tabs: next } };
  }
  private setGrantFlag(
    field: 'can_view_reports' | 'can_use_copilot' | 'active',
    value: boolean,
  ): void {
    const ed = this.coordEditing;
    if (!ed) return;
    this.coordEditing = { ...ed, profile: { ...ed.profile, [field]: value } };
  }

  /** Volta a dimensão pro "Todos" (lista vazia = sem restrição). */
  private clearGrantList(
    field:
      | 'allowed_tenant_ids'
      | 'allowed_etapas'
      | 'allowed_ano_series'
      | 'allowed_materias',
  ): void {
    const ed = this.coordEditing;
    if (!ed) return;
    this.coordEditing = { ...ed, profile: { ...ed.profile, [field]: [] } };
  }

  private async saveGrant(): Promise<void> {
    const ed = this.coordEditing;
    if (!ed) return;
    const g = ed.profile;
    if (!g.name.trim()) {
      this.coordError = 'Dê um nome ao perfil (ex.: "Coordenador EF2").';
      return;
    }
    this.coordSaving = true;
    this.coordError = '';
    try {
      const o = ed.options;
      // Reconcilia com as opções ATUAIS antes de salvar: valores "órfãos" (matéria
      // desativada/renomeada, instituição fora do escopo) não têm chip visível pra
      // remover e o backend os rejeitaria — travando o save inteiro. Descarta-os.
      const instOk = new Set(o.institutions.map((i) => i.id));
      const materiaOk = new Set(o.materias);
      const etapaOk = new Set(o.etapas.map((e) => e.code));
      const body = JSON.stringify({
        name: g.name.trim(),
        allowed_tenant_ids: g.allowed_tenant_ids.filter((t) => instOk.has(t)),
        // Etapas: só reconcilia se há catálogo (sem catálogo, preserva o salvo).
        allowed_etapas: etapaOk.size
          ? g.allowed_etapas.filter((e) => etapaOk.has(e))
          : g.allowed_etapas,
        allowed_ano_series: g.allowed_ano_series,
        allowed_materias: g.allowed_materias.filter((m) => materiaOk.has(m)),
        can_view_reports: g.can_view_reports,
        can_use_copilot: g.can_use_copilot,
        allowed_tabs: g.allowed_tabs,
        active: g.active,
      });
      // id null = criar (POST); senão atualizar (PUT).
      await this.accessFetch(
        g.id ? `/perfil/${encodeURIComponent(g.id)}` : '/perfil',
        { method: g.id ? 'PUT' : 'POST', body },
      );
      this.coordEditing = undefined;
      await this.loadCoordList();
    } catch (e) {
      this.coordError = (e as Error).message;
    } finally {
      this.coordSaving = false;
    }
  }
  private async deleteGrant(): Promise<void> {
    const ed = this.coordEditing;
    if (!ed || !ed.profile.id) {
      this.coordEditing = undefined;
      return;
    }
    this.coordSaving = true;
    this.coordError = '';
    try {
      await this.accessFetch(`/perfil/${encodeURIComponent(ed.profile.id)}`, {
        method: 'DELETE',
      });
      this.coordEditing = undefined;
      await this.loadCoordList();
    } catch (e) {
      this.coordError = (e as Error).message;
    } finally {
      this.coordSaving = false;
    }
  }

  /** Aplica um tema pronto (só as cores; preserva fonte/cantos/layout/marca). */
  private applyPreset(p: { tema: Partial<ThemeData> }): void {
    if (!this.tema) return;
    this.tema = { ...this.tema, ...p.tema };
    this.applyTheme();
    this.temaMsg = '';
  }

  private async uploadLogo(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = '';
    if (f) await this.uploadLogoFile(f);
  }

  private onLogoDragOver(e: DragEvent): void {
    e.preventDefault();
    if (!this.logoUploading) this.logoDragging = true;
  }

  private onLogoDragLeave(e: DragEvent): void {
    e.preventDefault();
    this.logoDragging = false;
  }

  private onLogoDrop(e: DragEvent): void {
    e.preventDefault();
    this.logoDragging = false;
    const f = e.dataTransfer?.files?.[0];
    if (f) void this.uploadLogoFile(f);
  }

  /** Limpa só a logo (o ✕ na prévia); não mexe no resto do tema. */
  private clearLogo(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    this.setTemaField('logo_url', '');
    this.temaMsg = 'Logo removida.';
  }

  /** Envia o arquivo (do clique OU do drag-drop) com validação e estado de loading. */
  private async uploadLogoFile(f: File): Promise<void> {
    if (this.logoUploading) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      this.temaMsg = 'Formato inválido — use PNG, JPG ou WEBP.';
      return;
    }
    if (f.size > 1_000_000) {
      this.temaMsg = 'Imagem grande demais (máx. 1 MB).';
      return;
    }
    this.logoUploading = true;
    this.temaMsg = 'Enviando imagem…';
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard/parceiro/tema/logo`, {
        method: 'POST',
        body: fd,
      });
      const data = (await r.json()) as { url?: string; detail?: string };
      if (!r.ok || !data.url) throw new Error(data.detail || 'Falha no upload.');
      this.setTemaField('logo_url', data.url);
      this.temaMsg = 'Logo enviada.';
    } catch (err) {
      this.temaMsg = (err as Error).message || 'Falha no upload.';
    } finally {
      this.logoUploading = false;
    }
  }

  /** Aplica o tema no host via variáveis CSS (valores já sanitizados no backend). */
  private applyTheme(): void {
    const t = this.tema;
    if (!t) return;
    const s = this.style;
    s.setProperty('--sos-bg', t.bg);
    s.setProperty('--sos-card', t.card);
    s.setProperty('--sos-accent', t.accent);
    s.setProperty('--sos-text', t.text);
    s.setProperty('--sos-muted', t.muted);
    s.setProperty('--sos-border', t.border);
    s.setProperty('--sos-hover', t.hover || '#808080');
    s.setProperty('--sos-font', FONT_MAP[t.font] ?? FONT_MAP.system);
    s.setProperty('--sos-radius', `${t.radius}px`);
    s.setProperty('--sos-pad', `${t.pad ?? 16}px`);
  }

  /** Ordem (layout) + visibilidade (hidden) de um card, via CSS order/display. */
  private cardStyle(key: string): string {
    const layout = this.tema?.layout ?? CARD_KEYS;
    const hidden = this.tema?.hidden ?? [];
    const order = layout.indexOf(key);
    // Estilo por card (via vars com escopo no próprio card): destaque (barras +
    // título), fundo, texto e fonte. Sem valor → herda o tema global.
    const cfg = this.tema?.cards?.[key] ?? {};
    const cardVars =
      (cfg.color ? `--card-accent:${cfg.color};` : '') +
      (cfg.bg ? `--card-bg:${cfg.bg};` : '') +
      (cfg.text ? `--card-text:${cfg.text};` : '') +
      (cfg.font ? `--card-font:${FONT_MAP[cfg.font] ?? FONT_MAP.system};` : '');
    // Abas: se há abas, um card só some quando está atribuído a ALGUMA aba e não
    // à ativa. Card sem aba nenhuma aparece sempre.
    let tabHide = '';
    const tabs = this.tema?.tabs ?? [];
    if (tabs.length) {
      const active = this.activeTabId();
      const assigned = tabs.some((tb) => tb.cards.includes(key));
      const inActive = tabs.find((tb) => tb.id === active)?.cards.includes(key);
      if (assigned && !inActive) tabHide = 'display:none;';
    }
    return (
      `order:${order < 0 ? 99 : order};` +
      (hidden.includes(key) || tabHide ? 'display:none;' : '') +
      cardVars
    );
  }

  /** Posição do cabeçalho de SEÇÃO: mesmo `order` do 1º card visível do grupo
   * (o cabeçalho vem antes no DOM, então desempata na frente do card). Some
   * quando o grupo inteiro está oculto (hidden/aba ativa sem os cards). */
  private sectionStyle(keys: string[]): string {
    const layout = this.tema?.layout ?? CARD_KEYS;
    const hidden = this.tema?.hidden ?? [];
    const tabs = this.tema?.tabs ?? [];
    const active = tabs.length ? this.activeTabId() : null;
    const vis = keys.filter((k) => {
      if (hidden.includes(k)) return false;
      if (tabs.length) {
        const assigned = tabs.some((tb) => tb.cards.includes(k));
        const inActive = tabs.find((tb) => tb.id === active)?.cards.includes(k);
        if (assigned && !inActive) return false;
      }
      return true;
    });
    if (!vis.length) return 'display:none;';
    const orders = vis.map((k) => layout.indexOf(k)).map((o) => (o < 0 ? 99 : o));
    return `order:${Math.min(...orders)};`;
  }

  /** Vars de cor/fonte de UM indicador (stat_*) pra colorir cada card do bloco
   * KPIs individualmente. Sem config → herda o bloco 'kpis'/o tema global. */
  private statStyle(key: string): string {
    const cfg = this.tema?.cards?.[key];
    if (!cfg) return '';
    return (
      (cfg.color ? `--card-accent:${cfg.color};` : '') +
      (cfg.bg ? `--card-bg:${cfg.bg};` : '') +
      (cfg.text ? `--card-text:${cfg.text};` : '') +
      (cfg.font ? `--card-font:${FONT_MAP[cfg.font] ?? FONT_MAP.system};` : '')
    );
  }

  /** Aba ativa efetiva (cai na 1ª aba se a guardada não existir mais). */
  /** Abas VISÍVEIS ao solicitante: coordenador com allowed_tabs restrito não vê as
   * abas de IA fora da concessão (abas normais sempre aparecem). Gestor vê todas. */
  private visibleTabs(): TabCfg[] {
    const tabs = this.tema?.tabs ?? [];
    const allowed = this.data?.allowed_tabs;
    if (allowed == null) return tabs; // null = sem restrição
    return tabs.filter((t) => t.kind !== 'ia' || allowed.includes(t.id));
  }

  private activeTabId(): string {
    const tabs = this.visibleTabs();
    if (!tabs.length) return '';
    return tabs.some((t) => t.id === this.activeTab) ? this.activeTab : tabs[0].id;
  }

  private addTab(): void {
    if (!this.tema) return;
    const tabs = [...(this.tema.tabs ?? [])];
    if (tabs.length >= 8) {
      this.temaMsg = 'Máximo de 8 abas.';
      return;
    }
    const id = (crypto.randomUUID?.() ?? `t${tabs.length}${Date.now()}`).slice(0, 8);
    tabs.push({ id, nome: `Aba ${tabs.length + 1}`, cards: [] });
    this.activeTab = id;
    this.setTemaField('tabs', tabs);
  }

  private renameTab(id: string, nome: string): void {
    if (!this.tema) return;
    const tabs = (this.tema.tabs ?? []).map((t) => (t.id === id ? { ...t, nome } : t));
    this.setTemaField('tabs', tabs);
  }

  private removeTab(id: string): void {
    if (!this.tema) return;
    const tabs = (this.tema.tabs ?? []).filter((t) => t.id !== id);
    this.setTemaField('tabs', tabs);
  }

  private toggleTabCard(id: string, key: string): void {
    if (!this.tema) return;
    const tabs = (this.tema.tabs ?? []).map((t) => {
      if (t.id !== id) return t;
      const cards = t.cards.includes(key)
        ? t.cards.filter((c) => c !== key)
        : [...t.cards, key];
      return { ...t, cards };
    });
    this.setTemaField('tabs', tabs);
  }

  /** Cria uma ABA DE IA (só gestor): o resultado é a resposta da IA a um prompt. */
  private addTabIA(): void {
    if (!this.tema) return;
    const tabs = [...(this.tema.tabs ?? [])];
    if (tabs.length >= 8) {
      this.temaMsg = 'Máximo de 8 abas.';
      return;
    }
    const id = (crypto.randomUUID?.() ?? `t${tabs.length}${Date.now()}`).slice(0, 8);
    tabs.push({
      id,
      nome: `Análise IA ${tabs.length + 1}`,
      cards: [],
      kind: 'ia',
      ai_prompt: '',
    });
    this.activeTab = id;
    this.setTemaField('tabs', tabs);
  }

  private setTabPrompt(id: string, ai_prompt: string): void {
    if (!this.tema) return;
    const tabs = (this.tema.tabs ?? []).map((t) =>
      t.id === id ? { ...t, ai_prompt } : t,
    );
    this.setTemaField('tabs', tabs);
  }

  /** Cabeçalho de um card: ícone/emoji + título (custom do white-label ou padrão). */
  private cardHead(key: string, def: string): TemplateResult {
    const cfg = this.tema?.cards?.[key];
    const icon = (cfg?.icon || '').trim();
    const titulo = (cfg?.titulo || '').trim() || def;
    const help = CARD_HELP[key];
    return html`${icon ? html`<span class="hd-ico">${icon}</span> ` : ''}${titulo}${help
      ? html`<span class="hd-help" title=${help} aria-label=${help} role="img">ⓘ</span>`
      : ''}`;
  }

  /** Linha de personalização de UM indicador no editor (fundo/destaque/texto). */
  private renderStatCfg(key: string, label: string): TemplateResult {
    const t = this.tema;
    if (!t) return html``;
    const cc = t.cards?.[key] ?? {};
    return html`<div class="pz-substat">
      <span class="pz-substat-name">${label}</span>
      <label class="pz-cc-sw" title="Cor de fundo">
        <span>Fundo</span>
        <input
          type="color"
          .value=${this.hexOf(cc.bg || t.card)}
          @input=${(e: Event) =>
            this.setCardCfg(key, 'bg', (e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="pz-cc-sw" title="Destaque (número/barra)">
        <span>Destaque</span>
        <input
          type="color"
          .value=${this.hexOf(cc.color || t.accent)}
          @input=${(e: Event) =>
            this.setCardCfg(key, 'color', (e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="pz-cc-sw" title="Cor do texto">
        <span>Texto</span>
        <input
          type="color"
          .value=${this.hexOf(cc.text || t.text)}
          @input=${(e: Event) =>
            this.setCardCfg(key, 'text', (e.target as HTMLInputElement).value)}
        />
      </label>
      <div class="pz-emoji-wrap">
        <button
          class="pz-cc-emoji ${cc.icon ? '' : 'empty'}"
          title="Ícone do indicador"
          @click=${(e: Event) => {
            e.preventDefault();
            this.emojiOpenFor = this.emojiOpenFor === key ? '' : key;
          }}
        >
          ${cc.icon || '🙂'}
        </button>
        ${this.emojiOpenFor === key
          ? html`<div class="pz-emoji-pop">
              ${CARD_EMOJIS.map(
                (em) => html`<button
                  type="button"
                  @click=${() => {
                    this.setCardCfg(key, 'icon', em);
                    this.emojiOpenFor = '';
                  }}
                >
                  ${em}
                </button>`,
              )}
              <button
                type="button"
                class="pz-emoji-none"
                @click=${() => {
                  this.setCardCfg(key, 'icon', '');
                  this.emojiOpenFor = '';
                }}
              >
                Sem ícone
              </button>
            </div>`
          : ''}
      </div>
      ${cc.bg || cc.color || cc.text || cc.icon
        ? html`<button
            class="pz-cc-clear"
            title="Limpar estilo deste indicador"
            @click=${() => this.clearCard(key)}
          >
            ✕
          </button>`
        : ''}
    </div>`;
  }

  /** Marcador de um indicador: emoji do white-label (se houver) ou a bolinha de cor. */
  private statDot(key: string, color: string): TemplateResult {
    const icon = (this.tema?.cards?.[key]?.icon || '').trim();
    return icon
      ? html`<span class="sico">${icon}</span>`
      : html`<span class="dot" style="background:${color}"></span>`;
  }

  // ─── Editor inline "Personalizar" (E3) ──────────────────────────────────────
  private openConfig(
    tab: 'personalizar' | 'acesso' | 'coordenadores' = 'personalizar',
  ): void {
    this.showInfo = false;
    this.drill = undefined;
    this.showGestor = false;
    this.showRelatorio = false;
    this.temaMsg = '';
    this.temaSaveState = 'idle';
    this.showConfig = true;
    this.setConfigTab(tab);
  }

  private setConfigTab(
    tab: 'personalizar' | 'acesso' | 'coordenadores' | 'pessoas',
  ): void {
    this.configTab = tab;
    if (tab === 'acesso') {
      this.acessoEditing = undefined;
      if (!this.acesso) void this.loadAcesso();
    } else if (tab === 'coordenadores') {
      this.coordEditing = undefined;
      void this.loadCoordList();
    } else if (tab === 'pessoas') {
      this.pessoaEditing = undefined;
      void this.loadPessoas();
    }
  }

  /** Atualiza um campo do tema, aplica AO VIVO (preview) e agenda o auto-save. */
  private setPeriodo(i: number, key: 'nome' | 'de' | 'ate', value: string): void {
    const list = [...(this.tema?.periodos_letivos ?? [])];
    if (!list[i]) return;
    list[i] = { ...list[i], [key]: value };
    this.setTemaField('periodos_letivos', list);
  }

  private addPeriodo(): void {
    const list = [
      ...(this.tema?.periodos_letivos ?? []),
      { nome: '', de: '', ate: '' },
    ];
    this.setTemaField('periodos_letivos', list);
  }

  private removePeriodo(i: number): void {
    const list = (this.tema?.periodos_letivos ?? []).filter((_, j) => j !== i);
    this.setTemaField('periodos_letivos', list);
  }

  private setTemaField(key: keyof ThemeData, value: unknown): void {
    if (!this.tema) return;
    this.tema = { ...this.tema, [key]: value } as ThemeData;
    this.applyTheme();
    this.temaMsg = '';
    this.scheduleSave();
  }

  /** Edita um campo do tema PRÓPRIO do chat (t.chat), preenchendo os defaults
   *  quando ainda não há tema de chat salvo. */
  private setChatField(key: keyof ChatTheme, value: string | number): void {
    if (!this.tema) return;
    const cur = { ...DEFAULT_CHAT_THEME, ...(this.tema.chat ?? {}) };
    this.setTemaField('chat', { ...cur, [key]: value });
  }

  /** Cor do texto sobre o accent do chat: branco OU escuro (#1f1f1f), o de MAIOR
   *  contraste WCAG. Espelha EXATAMENTE o contrastOn do <sos-chat> pra a prévia
   *  bater com o resultado real (tons médios ficam melhores com texto escuro). */
  private chatFg(hex: string): string {
    let s = hex.trim().replace(/^#/, '');
    if (s.length === 3)
      s = s
        .split('')
        .map((c) => c + c)
        .join('');
    if (!/^[0-9a-fA-F]{6}$/.test(s)) return '#fff';
    const n = parseInt(s, 16);
    const lin = (v: number) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const lum = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    return contrast(lum, 1) >= contrast(lum, 0.0137) ? '#fff' : '#1f1f1f';
  }

  /** Mistura duas cores hex por um fator (0..1): 0 = a, 1 = b. Fallback: devolve a. */
  private mixHex(a: string, b: string, ratio: number): string {
    const rgb = (h: string): [number, number, number] | null => {
      const m = /^#?([0-9a-fA-F]{6})$/.exec(h.trim());
      if (!m) return null;
      const n = parseInt(m[1], 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    };
    const ca = rgb(a);
    const cb = rgb(b);
    if (!ca || !cb) return a;
    const mixed = ca.map((v, i) => Math.round(v + (cb[i] - v) * ratio));
    return '#' + mixed.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
  }

  /** Mapeia um tema nomeado DO PAINEL para as 4 cores do chat: bg/text/accent
   *  diretos; bubble = superfície sutil entre fundo e texto, pra o balão da IA ler
   *  bem tanto em temas claros quanto escuros (Grafite/Escuro). */
  private presetToChat(t: Partial<ThemeData>): { bg: string; text: string; accent: string; bubble: string } {
    const bg = (t.bg ?? DEFAULT_CHAT_THEME.bg) as string;
    const text = (t.text ?? DEFAULT_CHAT_THEME.text) as string;
    const accent = (t.accent ?? DEFAULT_CHAT_THEME.accent) as string;
    return { bg, text, accent, bubble: this.mixHex(bg, text, 0.08) };
  }

  /** Seção "Chat do aluno" do Personalizar: tema PRÓPRIO do <sos-chat> (cores,
   *  fonte, cantos) + presets + prévia ao vivo. Salva em t.chat via setChatField. */
  private renderChatTema(t: ThemeData): TemplateResult {
    const c = { ...DEFAULT_CHAT_THEME, ...(t.chat ?? {}) };
    const fg = this.chatFg(c.accent);
    const corChat = (key: keyof ChatTheme, label: string) => html`
      <label class="pz-field">
        <span>${label}</span>
        <input
          type="color"
          .value=${this.hexOf(String(c[key]))}
          @input=${(e: Event) => this.setChatField(key, (e.target as HTMLInputElement).value)}
        />
      </label>
    `;
    // MESMOS temas nomeados do painel (Claro, Neve, Escuro, Azul, Verde, Roxo,
    // Areia, Grafite — de theme_svc.PRESETS via this.temaPresets), mapeados pras 4
    // cores do chat. "Padrão" (1º) volta ao visual neutro original do <sos-chat>.
    const presets: { nome: string; t: { bg: string; text: string; accent: string; bubble: string } }[] = [
      {
        nome: 'Padrão',
        t: {
          bg: DEFAULT_CHAT_THEME.bg,
          text: DEFAULT_CHAT_THEME.text,
          accent: DEFAULT_CHAT_THEME.accent,
          bubble: DEFAULT_CHAT_THEME.bubble,
        },
      },
      ...this.temaPresets.map((p) => ({ nome: p.nome, t: this.presetToChat(p.tema) })),
    ];
    const isActive = (p: { t: { bg: string; text: string; accent: string; bubble: string } }) =>
      (['bg', 'text', 'accent', 'bubble'] as const).every(
        (k) => (p.t[k] ?? '').toLowerCase() === String(c[k]).toLowerCase(),
      );
    return html`
      <div class="pz-sec">Aparência do chat do aluno</div>
      <small style="color:var(--muted);font-size:12px;line-height:1.4;display:block;margin:-4px 0 8px">
        Personaliza só o chat do aluno (o widget de conversa), separado do painel. Vale para
        a instituição selecionada; as escolas herdam o do portal se não tiverem o próprio.
      </small>
      <div class="pz-presets">
        ${presets.map(
          (p) => html`<button
            class="pz-preset ${isActive(p) ? 'active' : ''}"
            title=${p.nome}
            style="background:${p.t.bg};border-color:rgba(0,0,0,.1)"
            @click=${() => this.setTemaField('chat', { ...c, ...p.t })}
          >
            <span class="pz-pdot" style="background:${p.t.accent}"></span>
            <span class="pz-pname" style="color:${p.t.text}">${p.nome}</span>
          </button>`,
        )}
      </div>
      <div class="pz-sec">Cores</div>
      ${corChat('bg', 'Fundo')} ${corChat('bubble', 'Balão da IA')}
      ${corChat('accent', 'Destaque (botão e balão do aluno)')} ${corChat('text', 'Texto')}
      <div class="pz-sec">Tipografia & cantos</div>
      <label class="pz-field">
        <span>Fonte</span>
        <select @change=${(e: Event) => this.setChatField('font', (e.target as HTMLSelectElement).value)}>
          ${['system', 'serif', 'mono', 'condensed'].map(
            (f) => html`<option value=${f} ?selected=${c.font === f}>${FONT_LABELS[f] ?? f}</option>`,
          )}
        </select>
      </label>
      <label class="pz-field">
        <span>Cantos (${c.radius}px)</span>
        <input
          type="range"
          min="0"
          max="28"
          .value=${String(c.radius)}
          @input=${(e: Event) => this.setChatField('radius', Number((e.target as HTMLInputElement).value))}
        />
      </label>
      <div class="pz-sec">Prévia</div>
      <div
        style="background:${c.bg};border:1px solid var(--border);border-radius:${c.radius}px;padding:14px;display:flex;flex-direction:column;gap:8px;font-family:${FONT_MAP[
          c.font
        ] ?? FONT_MAP.system}"
      >
        <div style="align-self:flex-start;max-width:82%;background:${c.bubble};color:${c.text};padding:8px 12px;border-radius:12px;font-size:13px;line-height:1.4">
          Oi! Sou seu tutor 👋 Em que posso ajudar hoje?
        </div>
        <div style="align-self:flex-end;max-width:82%;background:${c.accent};color:${fg};padding:8px 12px;border-radius:12px;font-size:13px;line-height:1.4">
          Me explica frações, por favor
        </div>
        <div style="display:flex;gap:8px;margin-top:4px">
          <div style="flex:1;border:1px solid #ddd;border-radius:8px;padding:8px 10px;color:#9aa0a6;font-size:13px;background:#fff">
            Escreva sua mensagem…
          </div>
          <div style="background:${c.accent};color:${fg};padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600">
            Enviar
          </div>
        </div>
      </div>
    `;
  }

  /** Auto-save com debounce: toda edição aplica na hora e salva sozinha ~0,7s
   *  depois da última mudança. O status aparece no topo do editor. */
  private scheduleSave(): void {
    this.temaSaveState = 'dirty';
    clearTimeout(this._saveTimer);
    this._saveTimer = window.setTimeout(() => void this.saveTema(), 700);
  }

  /** Fecha Configurações garantindo que qualquer mudança pendente do Personalizar
   * seja salva antes. */
  private closeConfig(): void {
    clearTimeout(this._saveTimer);
    if (this.temaSaveState === 'dirty') void this.saveTema();
    this.showConfig = false;
    this.acessoEditing = undefined;
    this.coordEditing = undefined;
  }

  private setChartMode(mode: string): void {
    if (!this.tema) return;
    const c = this.tema.charts ?? { mode: 'auto', types: [...ALL_CHART_TYPES] };
    this.setTemaField('charts', { ...c, mode });
  }

  private toggleChartType(t: string): void {
    if (!this.tema) return;
    const c = this.tema.charts ?? { mode: 'auto', types: [...ALL_CHART_TYPES] };
    const has = c.types.includes(t);
    // Lista vazia é permitida: nenhum tipo marcado = Conect responde sem gráficos.
    const types = has ? c.types.filter((x) => x !== t) : [...c.types, t];
    this.setTemaField('charts', { ...c, types });
  }

  /** Config por card (cor/ícone/título). Guarda só o que foi preenchido e remove
   *  a entrada do card quando tudo é limpo (mantém o tema enxuto p/ o diff-save). */
  private setCardCfg(key: string, field: keyof CardCfg, value: string): void {
    if (!this.tema) return;
    const cards: Record<string, CardCfg> = { ...(this.tema.cards || {}) };
    const cur: CardCfg = { ...(cards[key] || {}) };
    if (value) cur[field] = value;
    else delete cur[field];
    if (Object.keys(cur).length) cards[key] = cur;
    else delete cards[key];
    this.setTemaField('cards', cards);
  }

  private clearCard(key: string): void {
    if (!this.tema) return;
    const cards = { ...(this.tema.cards || {}) };
    delete cards[key];
    this.setTemaField('cards', cards);
  }

  private moveCard(key: string, dir: -1 | 1): void {
    if (!this.tema) return;
    const layout = [...this.tema.layout];
    const i = layout.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= layout.length) return;
    [layout[i], layout[j]] = [layout[j], layout[i]];
    this.setTemaField('layout', layout);
  }

  private toggleCardHidden(key: string): void {
    if (!this.tema) return;
    const has = this.tema.hidden.includes(key);
    // Não deixa ocultar o ÚLTIMO card visível (painel vazio não faz sentido).
    if (!has && this.tema.hidden.length >= CARD_KEYS.length - 1) {
      this.temaMsg = 'Deixe ao menos um bloco visível.';
      return;
    }
    const hidden = has
      ? this.tema.hidden.filter((k) => k !== key)
      : [...this.tema.hidden, key];
    this.setTemaField('hidden', hidden);
  }

  /** Preset "ativo" = cujas cores batem com o tema atual (pro destaque visual). */
  private isPresetActive(p: { tema: Partial<ThemeData> }): boolean {
    const t = this.tema;
    if (!t) return false;
    return (['bg', 'card', 'accent', 'text', 'muted', 'border'] as const).every(
      (k) => (p.tema[k] ?? '').toLowerCase() === (t[k] ?? '').toLowerCase(),
    );
  }

  /** Avisa (não bloqueia) quando texto×card ou texto×fundo têm contraste baixo. */
  private contrastWarn(): TemplateResult | '' {
    const t = this.tema;
    if (!t) return '';
    const txt = this.hexOf(t.text);
    const vsCard = contrastRatio(txt, this.hexOf(t.card));
    const vsBg = contrastRatio(txt, this.hexOf(t.bg));
    if (vsCard >= 3 && vsBg >= 3) return '';
    return html`<div class="pz-warn">
      ⚠ Contraste baixo entre o texto e o ${vsCard < vsBg ? 'card' : 'fundo'}. Ajuste a
      cor do texto ou do ${vsCard < vsBg ? 'card' : 'fundo'} pra melhorar a leitura.
    </div>`;
  }

  /** Garante hex pro <input type=color> (rgba/inválido → branco). */
  private hexOf(c: string): string {
    return /^#[0-9a-fA-F]{6}$/.test(c) ? c : '#ffffff';
  }

  private async saveTema(reset = false): Promise<void> {
    // Serializa: com um save em voo, reagenda em vez de disparar outro PATCH
    // concorrente (dois PATCHes podem chegar fora de ordem no servidor e o velho
    // vencer o novo).
    if (this.temaSalvando) {
      clearTimeout(this._saveTimer);
      this._saveTimer = window.setTimeout(() => void this.saveTema(reset), 250);
      return;
    }
    clearTimeout(this._saveTimer); // salvando agora → cancela auto-save pendente
    this.temaSalvando = true;
    this.temaSaveState = 'saving';
    // (não limpa temaMsg aqui — senão avisos como o da guarda "deixe ao menos um
    // tipo" somem antes do usuário ler; o resultado do save já ajusta a mensagem)
    // Guarda de CORRIDA: toda edição troca a REFERÊNCIA de this.tema (setTemaField
    // e afins fazem spread). Se o usuário editar enquanto este save está em voo,
    // a resposta NÃO pode sobrescrever o estado local (reverteria os cliques e o
    // próximo auto-save salvaria o estado velho — checkboxes "voltando sozinhos").
    const snapshot = this.tema;
    try {
      const q =
        this.escolaSel !== 'todas'
          ? `?escola=${encodeURIComponent(this.escolaSel)}`
          : '';
      // RESTAURAR reseta a ESTÉTICA (cores/fontes/layout/marca) mas PRESERVA o que
      // é DADO/estrutura da escola: períodos letivos (calendário) e abas — perder o
      // calendário num reset de aparência já causou "perdi tudo" 2× (14 e 17/07).
      const resetBody = {
        periodos_letivos: snapshot?.periodos_letivos ?? [],
        // Abas: o padrão é UMA "Visão geral" com TODAS as seções. Preserva as abas
        // de IA (kind='ia' — guardam prompt/conteúdo do gestor, não são estética) e
        // DESCARTA abas normais avulsas (ex.: "Aba 2" de teste). Reconstrói do zero,
        // então funciona mesmo com o navegador em cache antigo (não re-salva lixo).
        tabs: [
          { id: 'geral', nome: 'Visão geral', cards: [...CARD_KEYS] },
          ...(snapshot?.tabs ?? []).filter((t) => t.kind === 'ia'),
        ],
        // Config de rede (não é estética): preserva no "Restaurar padrão".
        chat_admin_preview_tenant: snapshot?.chat_admin_preview_tenant ?? '',
      };
      const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard/parceiro/tema${q}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reset ? resetBody : snapshot),
      });
      if (r.status === 401) {
        // authFetch já tentou renovar o token pela sessão do Moodle e falhou —
        // a sessão do Moodle morreu; só o reload/login resolve.
        this.temaSaveState = 'error';
        this.temaMsg =
          'Sessão expirada — recarregue a página para voltar a salvar.';
        return;
      }
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      const data = (await r.json()) as { resolved: ThemeData };
      this._saveRetries = 0;
      if (reset || this.tema === snapshot) {
        // Nada mudou durante o voo → aplica o resolvido/sanitizado do servidor.
        this.tema = data.resolved;
        this.applyTheme();
        this.temaSaveState = 'saved';
        this.temaMsg = reset ? 'Restaurado para o padrão.' : '';
      } else {
        // Houve edições novas (estado 'dirty' e um auto-save já agendado) —
        // mantém o local; a versão mais nova será salva pelo próximo save.
        this.temaMsg = '';
      }
    } catch {
      // Erro transitório (rede/5xx): tenta de novo sozinho algumas vezes antes de
      // desistir — senão a última edição fica sem salvar silenciosamente.
      this._saveRetries += 1;
      this.temaSaveState = 'error';
      if (this._saveRetries <= 3) {
        this.temaMsg = 'Não consegui salvar — tentando de novo…';
        clearTimeout(this._saveTimer);
        this._saveTimer = window.setTimeout(() => void this.saveTema(reset), 2500);
      } else {
        this.temaMsg = 'Não consegui salvar. Verifique a conexão e tente de novo.';
      }
    } finally {
      this.temaSalvando = false;
    }
  }

  // ─── Configurações: tela única com abas (Personalizar/Acesso/Coordenadores) ──
  private renderConfig(): TemplateResult {
    // Defensivo: se a aba salva não é permitida pro solicitante, cai em Personalizar.
    const tab =
      ((this.configTab === 'acesso' ||
        this.configTab === 'coordenadores' ||
        this.configTab === 'pessoas') &&
        !this.isNetwork)
        ? 'personalizar'
        : this.configTab;
    const ss = this.temaSaveState;
    return html`
      <div
        class="panel gpanel"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeConfig();
        }}
      >
        <div class="gcard">
          <div class="panel-head">
            <span class="panel-title">Configurações</span>
            ${tab === 'personalizar'
              ? html`<span class="pz-savestate ${ss}">
                  ${ss === 'saved'
                    ? '✓ Tudo salvo'
                    : ss === 'error'
                      ? '⚠ Erro ao salvar'
                      : ss === 'saving' || ss === 'dirty'
                        ? 'Salvando…'
                        : ''}
                </span>`
              : ''}
            <button class="xbtn" @click=${() => this.closeConfig()}>
              ${this.iconClose}
            </button>
          </div>
          <div class="cfg-tabs" role="tablist">
            <button
              class="cfg-tab ${tab === 'personalizar' ? 'active' : ''}"
              role="tab"
              @click=${() => this.setConfigTab('personalizar')}
            >
              Personalizar
            </button>
            ${this.isNetwork
              ? html`<button
                  class="cfg-tab ${tab === 'acesso' ? 'active' : ''}"
                  role="tab"
                  @click=${() => this.setConfigTab('acesso')}
                >
                  Acesso
                </button>`
              : ''}
            ${this.isNetwork
              ? html`<button
                  class="cfg-tab ${tab === 'coordenadores' ? 'active' : ''}"
                  role="tab"
                  @click=${() => this.setConfigTab('coordenadores')}
                >
                  Perfis
                </button>`
              : ''}
            ${this.isNetwork
              ? html`<button
                  class="cfg-tab ${tab === 'pessoas' ? 'active' : ''}"
                  role="tab"
                  @click=${() => this.setConfigTab('pessoas')}
                >
                  Coordenadores
                </button>`
              : ''}
          </div>
          <div class="cfg-scroll">
            ${tab === 'personalizar'
              ? this.renderPersonalizarBody()
              : tab === 'acesso'
                ? this.renderAcessoBody()
                : tab === 'pessoas'
                  ? this.renderPessoasBody()
                  : this.renderCoordBody()}
          </div>
        </div>
      </div>
    `;
  }

  private renderPersonalizarBody(): TemplateResult {
    const t = this.tema;
    if (!t) return html``;
    const chartsCfg = t.charts ?? DEFAULT_CHARTS;
    const cor = (key: 'bg' | 'card' | 'accent' | 'text' | 'hover', label: string) => html`
      <label class="pz-field">
        <span>${label}</span>
        <input
          type="color"
          .value=${this.hexOf(t[key])}
          @input=${(e: Event) =>
            this.setTemaField(key, (e.target as HTMLInputElement).value)}
        />
      </label>
    `;
    const sec = this.pzSection;
    const secs: [
      'aparencia' | 'blocos' | 'abas' | 'marca' | 'chat' | 'graficos' | 'periodos',
      string,
    ][] = [
      ['aparencia', 'Aparência'],
      ['blocos', 'Blocos'],
      ['abas', 'Abas'],
      ['marca', 'Marca'],
      ['chat', 'Chat do aluno'],
      ['graficos', 'Gráficos'],
      ['periodos', 'Períodos letivos'],
    ];
    return html`
        <div class="pz-nav" role="tablist">
          ${secs.map(
            ([id, label]) => html`<button
              class="pz-navbtn ${sec === id ? 'on' : ''}"
              role="tab"
              @click=${() => (this.pzSection = id)}
            >
              ${label}
            </button>`,
          )}
        </div>
        <div class="panel-body pz">
          ${sec !== 'aparencia'
            ? ''
            : html`${this.temaPresets.length
            ? html`<div class="pz-sec">Temas prontos</div>
                <div class="pz-presets">
                  ${this.temaPresets.map(
                    (p) => html`<button
                      class="pz-preset ${this.isPresetActive(p) ? 'active' : ''}"
                      title=${p.nome}
                      style="background:${p.tema.bg};border-color:${p.tema.border ||
                      'rgba(0,0,0,.1)'}"
                      @click=${() => this.applyPreset(p)}
                    >
                      <span class="pz-pdot" style="background:${p.tema.accent}"></span>
                      <span class="pz-pname" style="color:${p.tema.text}">${p.nome}</span>
                    </button>`,
                  )}
                </div>`
            : ''}
          <div class="pz-sec">Cores</div>
          ${cor('bg', 'Fundo')} ${cor('card', 'Cards')} ${cor('accent', 'Destaque')}
          ${cor('text', 'Texto')} ${cor('hover', 'Destaque ao passar o mouse')}
          ${this.contrastWarn()}
          <div class="pz-sec">Tipografia & cantos</div>
          <label class="pz-field">
            <span>Fonte</span>
            <select
              .value=${t.font}
              @change=${(e: Event) =>
                this.setTemaField('font', (e.target as HTMLSelectElement).value)}
            >
              ${['system', 'serif', 'mono', 'condensed'].map(
                (f) => html`<option value=${f} ?selected=${t.font === f}>
                  ${FONT_LABELS[f] ?? f}
                </option>`,
              )}
            </select>
          </label>
          <label class="pz-field">
            <span>Cantos (${t.radius}px)</span>
            <input
              type="range"
              min="0"
              max="28"
              .value=${String(t.radius)}
              @input=${(e: Event) =>
                this.setTemaField('radius', Number((e.target as HTMLInputElement).value))}
            />
          </label>
          <label class="pz-field">
            <span>Espaçamento (${t.pad}px)</span>
            <input
              type="range"
              min="0"
              max="40"
              .value=${String(t.pad)}
              @input=${(e: Event) =>
                this.setTemaField('pad', Number((e.target as HTMLInputElement).value))}
            />
          </label>`}
          ${sec !== 'blocos'
            ? ''
            : html`<div class="pz-sec">Blocos do painel (ordem, visibilidade e estilo)</div>
          ${t.layout.map((k, i) => {
            const cc = t.cards?.[k] ?? {};
            return html`<div class="pz-card">
                <button
                  class="pz-mini"
                  ?disabled=${i === 0}
                  @click=${() => this.moveCard(k, -1)}
                  title="Subir"
                >
                  ↑
                </button>
                <button
                  class="pz-mini"
                  ?disabled=${i === t.layout.length - 1}
                  @click=${() => this.moveCard(k, 1)}
                  title="Descer"
                >
                  ↓
                </button>
                <span class="pz-cardname">${CARD_LABELS[k] ?? k}</span>
                <label class="pz-eye">
                  <input
                    type="checkbox"
                    ?checked=${!t.hidden.includes(k)}
                    @change=${() => this.toggleCardHidden(k)}
                  />
                  visível
                </label>
              </div>
              <div class="pz-cardcfg">
                <label class="pz-cc-sw" title="Cor de fundo do card">
                  <span>Fundo</span>
                  <input
                    type="color"
                    .value=${this.hexOf(cc.bg || t.card)}
                    @input=${(e: Event) =>
                      this.setCardCfg(k, 'bg', (e.target as HTMLInputElement).value)}
                  />
                </label>
                <label class="pz-cc-sw" title="Destaque (barras e título)">
                  <span>Destaque</span>
                  <input
                    type="color"
                    .value=${this.hexOf(cc.color || t.accent)}
                    @input=${(e: Event) =>
                      this.setCardCfg(k, 'color', (e.target as HTMLInputElement).value)}
                  />
                </label>
                <label class="pz-cc-sw" title="Cor do texto do card">
                  <span>Texto</span>
                  <input
                    type="color"
                    .value=${this.hexOf(cc.text || t.text)}
                    @input=${(e: Event) =>
                      this.setCardCfg(k, 'text', (e.target as HTMLInputElement).value)}
                  />
                </label>
                <select
                  class="pz-cc-font"
                  title="Fonte do card"
                  @change=${(e: Event) =>
                    this.setCardCfg(k, 'font', (e.target as HTMLSelectElement).value)}
                >
                  <option value="" ?selected=${!cc.font}>Fonte padrão</option>
                  ${['system', 'serif', 'mono', 'condensed'].map(
                    (f) => html`<option value=${f} ?selected=${cc.font === f}>
                      ${FONT_LABELS[f] ?? f}
                    </option>`,
                  )}
                </select>
                <div class="pz-emoji-wrap">
                  <button
                    class="pz-cc-emoji ${cc.icon ? '' : 'empty'}"
                    title="Escolher ícone"
                    @click=${(e: Event) => {
                      e.preventDefault();
                      this.emojiOpenFor = this.emojiOpenFor === k ? '' : k;
                    }}
                  >
                    ${cc.icon || '🙂'}
                  </button>
                  ${this.emojiOpenFor === k
                    ? html`<div class="pz-emoji-pop">
                        ${CARD_EMOJIS.map(
                          (em) => html`<button
                            type="button"
                            @click=${() => {
                              this.setCardCfg(k, 'icon', em);
                              this.emojiOpenFor = '';
                            }}
                          >
                            ${em}
                          </button>`,
                        )}
                        <button
                          type="button"
                          class="pz-emoji-none"
                          @click=${() => {
                            this.setCardCfg(k, 'icon', '');
                            this.emojiOpenFor = '';
                          }}
                        >
                          Sem ícone
                        </button>
                      </div>`
                    : ''}
                </div>
                <input
                  class="pz-cc-title"
                  type="text"
                  placeholder=${CARD_LABELS[k] ?? k}
                  title="Renomear o card"
                  .value=${cc.titulo ?? ''}
                  @input=${(e: Event) =>
                    this.setCardCfg(k, 'titulo', (e.target as HTMLInputElement).value)}
                />
                ${cc.color || cc.bg || cc.text || cc.font || cc.icon || cc.titulo
                  ? html`<button
                      class="pz-cc-clear"
                      title="Limpar estilo do card"
                      @click=${() => this.clearCard(k)}
                    >
                      ✕
                    </button>`
                  : ''}
              </div>
              ${k === 'kpis'
                ? html`<div class="pz-substats">
                    <div class="pz-substats-h">
                      Cor de cada indicador (sobrepõe o bloco acima)
                    </div>
                    ${KPI_STATS.map((s) => this.renderStatCfg(s.key, s.label))}
                  </div>`
                : ''}`;
          })}`}
          ${sec !== 'abas'
            ? ''
            : html`<div class="pz-sec">Abas</div>
          <div class="note">
            Sem abas = rolagem única. Crie abas e marque quais blocos aparecem em
            cada uma; bloco sem aba aparece sempre.
          </div>
          ${(t.tabs ?? []).map(
            (tb) => html`<div class="pz-tab ${tb.kind === 'ia' ? 'ia' : ''}">
              <div class="pz-tab-head">
                <input
                  class="pz-cc-title"
                  type="text"
                  placeholder="Nome da aba"
                  .value=${tb.nome}
                  @input=${(e: Event) =>
                    this.renameTab(tb.id, (e.target as HTMLInputElement).value)}
                />
                ${tb.kind === 'ia' ? html`<span class="pz-tab-badge">IA</span>` : ''}
                ${tb.kind !== 'ia' || this.isManager
                  ? html`<button
                      class="pz-cc-clear"
                      title="Excluir aba"
                      @click=${() => this.removeTab(tb.id)}
                    >
                      ✕
                    </button>`
                  : ''}
              </div>
              ${tb.kind === 'ia'
                ? html`<div class="pz-tab-ia">
                    <textarea
                      class="pz-ta"
                      rows="3"
                      maxlength="600"
                      placeholder="Ex.: Compare o engajamento por etapa e aponte onde a escola deve focar este mês."
                      .value=${tb.ai_prompt ?? ''}
                      ?disabled=${!this.isManager}
                      @input=${(e: Event) =>
                        this.setTabPrompt(tb.id, (e.target as HTMLTextAreaElement).value)}
                    ></textarea>
                    <div class="note">
                      A IA responde a esta pergunta com os dados do período (texto +
                      gráficos), numa aba própria.
                      ${this.isManager ? '' : 'Só um gestor pode editar.'}
                    </div>
                  </div>`
                : html`<div class="pz-tab-cards">
                    ${ABAS_GROUPS.map(
                      (g) => html`<div class="pz-tab-group">
                        <div class="pz-tab-grouphd">${g.label}</div>
                        <div class="pz-tab-grouprow">
                          ${g.keys.map(
                            (ck) => html`<label class="pz-type">
                              <input
                                type="checkbox"
                                ?checked=${tb.cards.includes(ck)}
                                @change=${() => this.toggleTabCard(tb.id, ck)}
                              />
                              ${CARD_LABELS[ck] ?? ck}
                            </label>`,
                          )}
                        </div>
                      </div>`,
                    )}
                  </div>`}
            </div>`,
          )}
          <div class="pz-tab-btns">
            <button class="pz-btn" @click=${this.addTab}>+ Nova aba</button>
            ${this.isManager
              ? html`<button class="pz-btn" @click=${this.addTabIA}>
                  + Nova aba de IA
                </button>`
              : ''}
          </div>`}
          ${sec !== 'marca'
            ? ''
            : html`<div class="pz-sec">Marca</div>
          <label class="pz-field col">
            <span>Logo (URL https)</span>
            <input
              type="text"
              placeholder="https://…/logo.png"
              .value=${t.logo_url}
              @input=${(e: Event) =>
                this.setTemaField('logo_url', (e.target as HTMLInputElement).value)}
            />
          </label>
          <div class="pz-field col">
            <span>Ou envie uma imagem</span>
            <label
              class="pz-drop ${this.logoDragging ? 'drag' : ''} ${this.logoUploading
                ? 'busy'
                : ''}"
              @dragover=${this.onLogoDragOver}
              @dragleave=${this.onLogoDragLeave}
              @drop=${this.onLogoDrop}
            >
              <input
                class="pz-drop-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                ?disabled=${this.logoUploading}
                @change=${this.uploadLogo}
              />
              ${this.logoUploading
                ? html`<span class="pz-spin"></span
                    ><span class="pz-drop-t">Enviando imagem…</span>`
                : t.logo_url
                  ? html`<button
                        class="pz-drop-x"
                        title="Remover logo"
                        @click=${this.clearLogo}
                      >
                        ✕
                      </button>
                      <img class="pz-drop-prev" src=${t.logo_url} alt="" />
                      <span class="pz-drop-t">Trocar imagem</span>`
                  : html`<svg
                        class="pz-drop-ico"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M12 16V6M8 10l4-4 4 4" />
                        <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                      </svg>
                      <span class="pz-drop-t">Arraste uma imagem ou <b>clique</b></span>
                      <span class="pz-drop-h">PNG, JPG ou WEBP · até 1 MB</span>`}
            </label>
          </div>
          <label class="pz-field col">
            <span>Nome (opcional)</span>
            <input
              type="text"
              placeholder="Nome da escola/rede"
              .value=${t.nome}
              @input=${(e: Event) =>
                this.setTemaField('nome', (e.target as HTMLInputElement).value)}
            />
          </label>
          <div class="pz-sec">Cabeçalho</div>
          <label class="pz-field col">
            <span>Título no topo (opcional)</span>
            <input
              type="text"
              placeholder="Ex.: Painel do Parceiro SOS"
              .value=${t.titulo ?? ''}
              @input=${(e: Event) =>
                this.setTemaField('titulo', (e.target as HTMLInputElement).value)}
            />
          </label>
          <label class="pz-field col">
            <span>Subtítulo (ao lado do nome)</span>
            <input
              type="text"
              placeholder="Painel do parceiro"
              .value=${t.subtitulo ?? ''}
              @input=${(e: Event) =>
                this.setTemaField('subtitulo', (e.target as HTMLInputElement).value)}
            />
          </label>
          ${this.isNetwork && this.escolaSel === 'todas'
            ? html`<div class="pz-sec">Preview do chat do aluno (equipe)</div>
                <label class="pz-field col">
                  <span>Pré-visualizar o chat como aluno de…</span>
                  <select
                    @change=${(e: Event) =>
                      this.setTemaField(
                        'chat_admin_preview_tenant',
                        (e.target as HTMLSelectElement).value,
                      )}
                  >
                    <option value="" ?selected=${!(t.chat_admin_preview_tenant ?? '')}>
                      Desligado (admin não vê o chat)
                    </option>
                    ${this.escolas
                      .filter((e) => !(e.nome ?? '').includes('(portal)'))
                      .map(
                        (e) => html`<option
                          value=${e.id}
                          ?selected=${t.chat_admin_preview_tenant === e.id}
                        >
                          ${e.nome ?? e.id}
                        </option>`,
                      )}
                  </select>
                  <small style="color:var(--muted);font-size:12px;line-height:1.4;margin-top:2px">
                    Administrador, gestor de rede, diretor e coordenador do Moodle passam a
                    ver e usar o chat do aluno como um estudante da escola escolhida (preview).
                    Vale para toda a rede.
                  </small>
                </label>`
            : ''}
          `}
          ${sec !== 'chat' ? '' : this.renderChatTema(t)}
          ${sec !== 'graficos'
            ? ''
            : html`<div class="pz-sec">Gráficos do Conect</div>
          <label class="pz-field">
            <span>Quando gerar</span>
            <select
              @change=${(e: Event) =>
                this.setChartMode((e.target as HTMLSelectElement).value)}
            >
              ${['auto', 'always', 'off'].map(
                (m) => html`<option value=${m} ?selected=${chartsCfg.mode === m}>
                  ${CHART_MODE_LABELS[m]}
                </option>`,
              )}
            </select>
          </label>
          ${chartsCfg.mode !== 'off'
            ? html`<div class="pz-field col">
                <span>Tipos permitidos</span>
                <div class="pz-types">
                  ${ALL_CHART_TYPES.map(
                    (ct) => html`<label class="pz-type">
                      <input
                        type="checkbox"
                        .checked=${live(chartsCfg.types.includes(ct))}
                        @change=${() => this.toggleChartType(ct)}
                      />
                      ${CHART_TYPE_LABELS[ct]}
                    </label>`,
                  )}
                </div>
                ${chartsCfg.types.length
                  ? html`<div class="pz-chart-prevs">
                      ${ALL_CHART_TYPES.filter((ct) =>
                        chartsCfg.types.includes(ct),
                      ).map(
                        (ct) => html`<div class="pz-chart-prev">
                          ${this.chartExample(ct)}
                        </div>`,
                      )}
                    </div>
                    <div class="note">
                      Exemplos com dados fictícios — é assim que cada tipo marcado
                      aparece nas respostas do Conect e nas abas de IA.
                    </div>`
                  : html`<div class="note">
                      Nenhum tipo marcado — o Conect e as abas de IA respondem só
                      em texto, sem gráficos.
                    </div>`}
              </div>`
            : ''}`}
          ${sec !== 'periodos'
            ? ''
            : html`<div class="pz-sec">Períodos letivos da instituição</div>
          <div class="note" style="margin:0 0 10px">
            Defina o calendário da sua escola (bimestre, semestre, trimestre…). Alimenta
            o card "Uso por período letivo" no painel. Deixe vazio para não usar.
          </div>
          ${(t.periodos_letivos ?? []).map(
            (pl, i) => html`<div
              class="pz-field"
              style="gap:6px;align-items:center;flex-wrap:wrap"
            >
              <input
                type="text"
                .value=${pl.nome}
                placeholder="Ex.: 1º Bimestre"
                style="flex:1;min-width:120px"
                @input=${(e: Event) =>
                  this.setPeriodo(i, 'nome', (e.target as HTMLInputElement).value)}
              />
              <input
                type="date"
                .value=${pl.de}
                @input=${(e: Event) =>
                  this.setPeriodo(i, 'de', (e.target as HTMLInputElement).value)}
              />
              <input
                type="date"
                .value=${pl.ate}
                @input=${(e: Event) =>
                  this.setPeriodo(i, 'ate', (e.target as HTMLInputElement).value)}
              />
              <button class="pz-mini" title="Remover" @click=${() => this.removePeriodo(i)}>
                ✕
              </button>
            </div>`,
          )}
          <button class="gtbtn ghost" style="margin-top:8px" @click=${() => this.addPeriodo()}>
            + Adicionar período
          </button>`}
          ${this.temaMsg ? html`<div class="pz-msg">${this.temaMsg}</div>` : ''}
          <div class="pz-actions">
            <button
              class="pz-btn"
              ?disabled=${this.temaSalvando}
              @click=${() => this.saveTema(true)}
            >
              Restaurar padrão
            </button>
          </div>
          <div class="note">
            As mudanças aplicam na hora e <strong>salvam automaticamente</strong> — o
            status aparece no topo.
            ${this.escolaSel !== 'todas' && this.escolas.length
              ? 'Salva na instituição selecionada.'
              : ''}
          </div>
        </div>
    `;
  }

  private async openInfo(): Promise<void> {
    this.drill = undefined; // só um overlay por vez
    this.showConfig = false;
    this.showInfo = true;
    if (this.escopo || this.escopoLoading) return;
    this.escopoLoading = true;
    this.escopoError = '';
    try {
      const q =
        this.escolaSel !== 'todas'
          ? `?escola=${encodeURIComponent(this.escolaSel)}`
          : '';
      this.escopo = await this.fetchJson<EscopoData>(`/parceiro/escopo${q}`);
    } catch (e) {
      this.escopoError = (e as Error).message;
    } finally {
      this.escopoLoading = false;
    }
  }

  private async openDrill(
    titulo: string,
    tipo: DrillTipo,
    path: string,
  ): Promise<void> {
    this.showInfo = false; // só um overlay por vez
    this.showConfig = false;
    // Escopo + período + filtros granulares nos detalhes (igual aos cards). "Todas"
    // = sem escola → backend agrega portal+filhas; from/to recorta a janela.
    const [base, qs] = path.split('?');
    const p = new URLSearchParams(qs || '');
    if (this.escolaSel !== 'todas' && !p.has('escola'))
      p.set('escola', this.escolaSel);
    const { from, to } = this.periodRange();
    if (!p.has('from')) p.set('from', from);
    if (!p.has('to')) p.set('to', to);
    // Filtros granulares globais — só se o drill não os especifica (o card de etapa
    // já manda `etapa`, o de matéria já manda `categoria_id`) e não é detalhe de
    // sessão (aí quero a conversa inteira, sem recorte de coorte/matéria).
    if (!p.has('session_id')) {
      if (this.filtroEtapa && !p.has('etapa')) p.set('etapa', this.filtroEtapa);
      if (this.filtroAnoSerie && !p.has('ano_serie'))
        p.set('ano_serie', this.filtroAnoSerie);
      if (this.filtroMateria && this.escolaSel !== 'todas' && !p.has('categoria_id'))
        p.set('categoria_id', this.filtroMateria);
      if (this.filtroAluno && !p.has('aluno')) p.set('aluno', this.filtroAluno);
    }
    const full = `${base}?${p.toString()}`;
    this.drill = { titulo, tipo, items: [], loading: true, error: '' };
    try {
      const items = await this.fetchJson<
        AlunoRow[] | SessaoRow[] | BloqueioRow[] | PerguntaRow[]
      >(full);
      this.drill = { titulo, tipo, items, loading: false, error: '' };
    } catch (e) {
      this.drill = {
        titulo,
        tipo,
        items: [],
        loading: false,
        error: (e as Error).message,
      };
    }
  }

  private async openGestor(): Promise<void> {
    this.showInfo = false;
    this.drill = undefined; // só um overlay por vez
    this.showConfig = false;
    this.showGestor = true;
    this.gestorError = '';
    // NÃO cria sessão aqui — só abrir o painel não deve gerar uma AISession vazia.
    // A sessão nasce no PRIMEIRO envio (ensureGestorSession).
    void this.loadGestorUso();
    void this.loadGestorSessions();
  }

  // ─── Conversas do copiloto (histórico / renomear / excluir) ─────────────────
  private async loadGestorSessions(): Promise<void> {
    try {
      this.gestorSessions = await this.fetchJson<GestorConvRow[]>('/gestor/sessions');
    } catch {
      /* lista é opcional — não bloqueia o chat */
    }
  }

  private novaGestorConversa(): void {
    this.gestorSessionId = null;
    this.gestorMsgs = [];
    this.gestorStreamingText = '';
    this.gestorError = '';
    this.gestorShowList = false;
  }

  /** Exporta a conversa ATUAL do Conect como Markdown (.md) — client-side, sem
   * backend. Cabeçalho com contexto (instituição/período/recorte) + cada turno;
   * blocos ```chart``` viram uma linha legível (os números já estão no texto). */
  private exportGestorConversa(): void {
    if (!this.gestorMsgs.length) return;
    const nome = this.data?.tutor_nome || 'Analista';
    const escola = this.data?.escola || 'Instituição';
    const dt = new Date();
    const linhas: string[] = [
      `# Conversa com o ${nome}`,
      '',
      `- Instituição: ${escola}`,
      `- Período analisado: ${this.periodLabel()}`,
    ];
    if (this.filtroAluno) {
      linhas.push(`- Recorte: estudante ${this.filtroAlunoNome || this.filtroAluno}`);
    }
    linhas.push(`- Exportado em: ${dt.toLocaleString('pt-BR')}`, '', '---', '');
    for (const m of this.gestorMsgs) {
      const autor = m.role === 'user' ? 'Você' : nome;
      const txt = m.content
        .replace(/```chart\s*(\{[\s\S]*?\})\s*```/g, (_all, j) => {
          try {
            const o = JSON.parse(j);
            return `_[gráfico: ${o.title || o.data || ''} — ${o.type || ''}]_`;
          } catch {
            return '';
          }
        })
        .trim();
      linhas.push(`**${autor}:**`, '', txt, '');
    }
    const blob = new Blob([linhas.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug =
      escola
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40) || 'conect';
    a.download = `conversa-${slug}-${dt.toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private async loadGestorConversa(id: string): Promise<void> {
    this.gestorShowList = false;
    this.gestorError = '';
    try {
      const msgs = await this.fetchJson<GestorMsgRow[]>(
        `/gestor/sessions/${encodeURIComponent(id)}/messages`,
      );
      this.gestorMsgs = msgs.map((m) => ({ role: m.role, content: m.content }));
      this.gestorSessionId = id;
    } catch {
      this.gestorError = 'Não consegui carregar essa conversa.';
    }
  }

  private startRename(id: string, titulo: string): void {
    this.gestorRenamingId = id;
    this.gestorRenameText = titulo;
  }

  private async confirmRename(id: string): Promise<void> {
    const titulo = this.gestorRenameText.trim();
    if (!titulo) {
      this.gestorRenamingId = null;
      return;
    }
    try {
      const r = await this.authFetch(
        `${this.apiBase}/api/v1/dashboard/gestor/sessions/${encodeURIComponent(id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ titulo }),
        },
      );
      if (!r.ok) throw new Error(`Erro ${r.status}`);
    } catch {
      this.gestorError = 'Não consegui renomear.';
    }
    this.gestorRenamingId = null;
    void this.loadGestorSessions();
  }

  private async excluirConversa(id: string): Promise<void> {
    if (!window.confirm('Excluir esta conversa e seu histórico? Não dá pra desfazer.')) {
      return;
    }
    try {
      const r = await this.authFetch(
        `${this.apiBase}/api/v1/dashboard/gestor/sessions/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
      if (!r.ok && r.status !== 204) throw new Error(`Erro ${r.status}`);
      if (id === this.gestorSessionId) this.novaGestorConversa();
    } catch {
      this.gestorError = 'Não consegui excluir.';
    }
    void this.loadGestorSessions();
  }

  /** Cria a sessão do gestor sob demanda (1ª pergunta). Evita sessão vazia só por
   * abrir o painel. Retorna o id ou null (com erro setado). */
  private async ensureGestorSession(): Promise<string | null> {
    if (this.gestorSessionId) return this.gestorSessionId;
    try {
      const r = await this.authFetch(`${this.apiBase}/api/v1/dashboard/gestor/session`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      this.gestorSessionId = ((await r.json()) as { id: string }).id;
      return this.gestorSessionId;
    } catch {
      this.gestorError = 'Não consegui abrir a consulta. Tente de novo.';
      return null;
    }
  }

  private async loadGestorUso(): Promise<void> {
    try {
      this.gestorUso = await this.fetchJson<GestorUso>('/gestor/uso');
    } catch {
      /* uso é opcional — não bloqueia o chat */
    }
  }

  private onGestorInput(e: Event): void {
    this.gestorInput = (e.target as HTMLInputElement).value;
  }

  private askGestor(q: string): void {
    if (this.gestorStreaming) return;
    this.gestorInput = q;
    void this.sendGestor();
  }

  private async sendGestor(): Promise<void> {
    const q = this.gestorInput.trim();
    if (!q || this.gestorStreaming) return;
    // Trava cedo (antes do await da criação da sessão) pra não duplicar envio/sessão.
    this.gestorStreaming = true;
    this.gestorError = '';
    const sessionId = await this.ensureGestorSession();
    if (!sessionId) {
      this.gestorStreaming = false; // erro já setado; preserva o texto digitado
      return;
    }
    this.gestorInput = '';
    this.gestorLastQ = q;
    this.gestorMsgs = [...this.gestorMsgs, { role: 'user', content: q }];
    this.gestorStreamingText = '';
    this.scrollGestorSoon();
    // O SSE recebe o token na construção — renova proativamente se está vencendo
    // (o authFetch não intercepta o stream).
    if (this.tokenExpSoon()) await this.refreshToken();
    const client = new SseClient({
      url: `${this.apiBase}/api/v1/dashboard/gestor/stream`,
      token: this.token,
      body: {
        session_id: sessionId,
        content: q,
        escola: this.escolaSel,
        periodo_de: this.periodRange().from,
        periodo_ate: this.periodRange().to,
        periodo_label: this.periodLabel(),
        // v2 crit. 10: o Conect recebe os filtros ATIVOS do painel — a análise
        // fala do mesmo recorte que o gestor está vendo.
        etapa: this.filtroEtapa || undefined,
        ano_serie: this.filtroAnoSerie || undefined,
        categoria_id: this.filtroMateria || undefined,
        aluno: this.filtroAluno || undefined,
      },
    });
    client.on('token', (d: { delta?: string }) => {
      this.gestorStreamingText += d.delta ?? '';
      this.scrollGestorSoon();
    });
    client.on('done', (d: { saved?: boolean }) => {
      if (this.gestorStreamingText) {
        this.gestorMsgs = [
          ...this.gestorMsgs,
          { role: 'assistant', content: this.gestorStreamingText },
        ];
      }
      this.gestorStreamingText = '';
      this.gestorStreaming = false;
      if (d && d.saved === false) {
        this.gestorError = 'Resposta exibida, mas não foi salva no histórico.';
      }
      this.scrollGestorSoon();
      void this.loadGestorUso();
      void this.loadGestorSessions(); // atualiza o histórico (auto-título da 1ª pergunta)
    });
    client.on('error', (d: { detail?: string }) => {
      this.gestorStreaming = false;
      this.gestorStreamingText = '';
      // A pergunta fica na conversa; o render mostra "Tentar de novo".
      this.gestorError = d.detail ?? 'Erro ao responder.';
    });
    void client.start();
  }

  /** Reenvia a última pergunta após um erro (sem duplicar a bolha do usuário). */
  private retryGestor(): void {
    if (this.gestorStreaming || !this.gestorLastQ) return;
    const msgs = [...this.gestorMsgs];
    if (msgs.length && msgs[msgs.length - 1].role === 'user') msgs.pop();
    this.gestorMsgs = msgs;
    this.gestorError = '';
    this.gestorInput = this.gestorLastQ;
    void this.sendGestor();
  }

  /** Rola a lista de mensagens pro fim (após render). */
  private scrollGestorSoon(): void {
    void this.updateComplete.then(() => {
      const box = this.shadowRoot?.querySelector('.gmsgs');
      if (box) box.scrollTop = box.scrollHeight;
    });
  }

  private get iconSpark(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><path
        d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"
      /></svg>`;
  }

  private fmtDateTime(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  private fmtDate(iso?: string | null): string {
    if (!iso) return 'nunca';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'nunca';
    return d.toLocaleDateString('pt-BR');
  }

  /** Formata "YYYY-MM-DD" (data-only) como "DD/MM/YYYY" SEM passar por Date —
   * evita o off-by-one de fuso (new Date('2026-07-01') vira UTC → dia anterior). */
  private fmtDMY(ymd?: string | null): string {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-');
    return d && m && y ? `${d}/${m}/${y}` : ymd;
  }

  private statusBadge(s: string | null): TemplateResult {
    if (s === 'inativo')
      return html`<span class="badge bad">desmatriculado</span>`;
    if (s === null || s === undefined)
      return html`<span class="badge warn">pendente</span>`;
    return html`<span class="badge ok">matriculado</span>`;
  }

  private get iconInfo(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round"
      ><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><line
        x1="12"
        y1="7.5"
        x2="12"
        y2="7.6"
      /></svg>`;
  }

  private get iconClose(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" width="16" height="16" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round"
      ><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18"
    /></svg>`;
  }

  private get iconGear(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><circle cx="12" cy="12" r="3" /><path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      /></svg>`;
  }

  private get iconEdit(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" width="15" height="15" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><path d="M12 20h9" /><path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
      /></svg>`;
  }

  private get iconTrash(): TemplateResult {
    return html`<svg viewBox="0 0 24 24" width="15" height="15" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><polyline points="3 6 5 6 21 6" /><path
        d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
      /></svg>`;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    // Se o componente sair da página durante o tour, remove os listeners globais.
    if (this.tourStep >= 0) this.tourExit();
  }

  // ─── Tour guiado (tutorial) ───────────────────────────────────────────────
  private startTour = (): void => {
    const steps = this.buildTourSteps();
    if (steps.length <= 2) return; // só intro + fim → nada visível pra mostrar
    this.tourSteps = steps;
    this.tourStep = 0;
    window.addEventListener('resize', this._tourReposition);
    window.addEventListener('scroll', this._tourReposition, true);
    window.addEventListener('keydown', this._tourKey);
    this.positionTour();
  };

  /** Passos do tour na ordem visual, incluindo APENAS os alvos VISÍVEIS: seções
   *  ocultas (display:none via sectionStyle) e botões sem permissão são pulados.
   *  Intro e encerramento são balões centrais (key null). */
  private buildTourSteps(): { key: string | null; title: string; text: string }[] {
    const visible = (key: string): boolean => {
      const el = this.shadowRoot?.querySelector(`[data-tour="${key}"]`) as HTMLElement | null;
      return !!el && el.offsetWidth > 0 && el.offsetHeight > 0;
    };
    const steps: { key: string | null; title: string; text: string }[] = [
      {
        key: null,
        title: 'Bem-vindo ao Painel do Parceiro 👋',
        text: 'Em poucos passos eu mostro o básico. Use “Próximo” para avançar ou “Sair” quando quiser.',
      },
    ];
    const add = (key: string, title: string, text: string): void => {
      if (visible(key)) steps.push({ key, title, text });
    };
    add(
      'periodo',
      'Período',
      'Escolha a janela de tempo. Todos os indicadores e a análise da IA passam a refletir esse período.',
    );
    add(
      'escola',
      'Escola e visão',
      'Alterne entre uma escola específica e o agregado da rede (todas as instituições).',
    );
    add(
      'filtros',
      'Filtros',
      'Refine por estudante, etapa, ano/série e matéria — o painel inteiro respeita o filtro.',
    );
    add(
      'cards',
      'Indicadores',
      'Os números-chave do período: matriculados, ativos, consultas e mais.',
    );
    // Uma parada por SEÇÃO de cards VISÍVEL (as ocultas têm display:none e são puladas).
    CARD_SECTIONS.forEach((s, i) =>
      add(
        `sec-${i}`,
        s.label,
        TOUR_SECTION_TEXT[s.label] ?? 'Indicadores desta seção no período/filtro selecionado.',
      ),
    );
    add(
      'insights',
      'Leitura da IA',
      'Uma síntese automática do período, escrita pela IA a partir desses mesmos números. “Atualizar análise” regera.',
    );
    add(
      'conect',
      'Copiloto',
      'Converse com a IA sobre os dados: pergunte o porquê de um número, peça comparações e recomendações.',
    );
    add(
      'relatorio',
      'Relatório em PDF',
      'Gere um relatório do período, com a marca da escola, e baixe em PDF para compartilhar.',
    );
    add(
      'config',
      'Configurações',
      'Personalize a aparência, defina acessos de coordenadores e crie abas de IA.',
    );
    steps.push({
      key: null,
      title: 'Pronto! 🎉',
      text: 'Isso é o essencial. Explore à vontade — o botão “Tutorial” fica sempre no topo se quiser rever.',
    });
    return steps;
  }

  private _tourReposition = (): void => this.positionTour();
  private _tourKey = (e: KeyboardEvent): void => {
    if (this.tourStep < 0) return;
    if (e.key === 'Escape') this.tourExit();
    else if (e.key === 'ArrowRight') this.tourNext();
    else if (e.key === 'ArrowLeft') this.tourPrev();
  };

  private positionTour(): void {
    const step = this.tourSteps[this.tourStep];
    if (!step || !step.key) {
      this.tourRect = null;
      return;
    }
    const el = this.shadowRoot?.querySelector(`[data-tour="${step.key}"]`) as HTMLElement | null;
    if (!el) {
      this.tourRect = null;
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'auto' });
    requestAnimationFrame(() => {
      if (this.tourStep < 0) return;
      const r = el.getBoundingClientRect();
      this.tourRect = { top: r.top, left: r.left, width: r.width, height: r.height };
    });
  }

  private tourNext = (): void => {
    if (this.tourStep >= this.tourSteps.length - 1) {
      this.tourExit();
      return;
    }
    this.tourStep += 1;
    this.positionTour();
  };
  private tourPrev = (): void => {
    if (this.tourStep <= 0) return;
    this.tourStep -= 1;
    this.positionTour();
  };
  private tourExit = (): void => {
    this.tourStep = -1;
    this.tourRect = null;
    window.removeEventListener('resize', this._tourReposition);
    window.removeEventListener('scroll', this._tourReposition, true);
    window.removeEventListener('keydown', this._tourKey);
  };

  private renderTour(): TemplateResult {
    if (this.tourStep < 0) return html``;
    const step = this.tourSteps[this.tourStep];
    if (!step) return html``;
    const total = this.tourSteps.length;
    const accent = this.tema?.accent || '#2563eb';
    const rect = this.tourRect;
    const pad = 6;
    const spot = rect
      ? html`<div
          class="tour-spot"
          style="top:${rect.top - pad}px;left:${rect.left - pad}px;width:${rect.width +
          pad * 2}px;height:${rect.height + pad * 2}px;outline-color:${accent}"
        ></div>`
      : html`<div class="tour-dim"></div>`;
    // Balão: abaixo do alvo se couber, senão acima; centralizado quando não há alvo.
    let cardStyle = 'left:50%;top:50%;transform:translate(-50%,-50%)';
    if (rect) {
      const cardW = 320;
      const left = Math.min(Math.max(12, rect.left), window.innerWidth - cardW - 12);
      const below = rect.top + rect.height + 14;
      if (window.innerHeight - below > 190) {
        cardStyle = `top:${below}px;left:${left}px`;
      } else {
        cardStyle = `top:${Math.max(12, rect.top - 14)}px;left:${left}px;transform:translateY(-100%)`;
      }
    }
    const last = this.tourStep >= total - 1;
    return html`
      <div class="tour-layer">
        ${spot}
        <div class="tour-card" style=${cardStyle}>
          <div class="tour-hd">
            <span class="tour-count" style="color:${accent}">${this.tourStep + 1}/${total}</span>
            <button
              class="tour-x"
              @click=${this.tourExit}
              title="Sair do tutorial"
              aria-label="Sair do tutorial"
            >
              ✕
            </button>
          </div>
          <div class="tour-title">${step.title}</div>
          <div class="tour-text">${step.text}</div>
          <div class="tour-nav">
            <button class="tour-skip" @click=${this.tourExit}>Sair</button>
            <div class="tour-navr">
              ${this.tourStep > 0
                ? html`<button class="tour-prev" @click=${this.tourPrev}>Anterior</button>`
                : ''}
              <button class="tour-next" style="background:${accent}" @click=${this.tourNext}>
                ${last ? 'Concluir' : 'Próximo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    // 1ª carga (ainda SEM dados): placeholder/erro em tela cheia. Nas ATUALIZAÇÕES
    // (já há dados na tela) NÃO troca o painel por um placeholder — mantém o conteúdo
    // atual e sinaliza só com uma barrinha discreta no topo. Isso evita o painel
    // "sumir e reaparecer" a cada clique (filtro/período/escola/aluno chamam load()).
    if (!this.data) {
      if (this.loading) return html`<div class="loading">Carregando o painel…</div>`;
      if (this.error) return html`<div class="err">${this.error}</div>`;
      return html``;
    }
    const d = this.data;
    const maxEtapa = Math.max(1, ...d.por_etapa.map((e) => e.total));
    const maxCat = Math.max(1, ...d.por_categoria_bloqueio.map((c) => c.total));
    const maxMateria = Math.max(1, ...d.por_materia.map((m) => m.total));
    const tematicas = d.por_tematica ?? [];
    const maxTematica = Math.max(1, ...tematicas.map((t) => t.total));
    const subtitulo = this.tema?.subtitulo ?? 'Painel do parceiro';
    // Aba de IA ativa → o corpo mostra a análise da IA em vez dos cards.
    const abaIA =
      this.visibleTabs().find(
        (t) => t.id === this.activeTabId() && t.kind === 'ia',
      ) ?? null;
    return html`
      <div class="wrap ${this.loading ? 'refreshing' : ''}">
      ${this.renderTour()}
      ${this.loading
        ? html`<div class="refreshbar" role="progressbar" aria-label="Atualizando o painel"></div>`
        : ''}
      ${this.error
        ? html`<div class="refresherr" role="alert">
            ${this.error} — mostrando os dados anteriores.
          </div>`
        : ''}
      ${this.tema?.titulo
        ? html`<div class="pheading">${this.tema.titulo}</div>`
        : ''}
      <div class="head">
        <div class="head-l">
          ${this.tema?.logo_url && !(this.escolas.length && this.escolaSel === 'todas')
            ? html`<img
                class="brand-logo"
                src=${this.tema.logo_url}
                alt=${this.tema?.nome || ''}
              />`
            : ''}
          ${(() => {
            // Feedback 17/07: logo + nome + tag "Painel do parceiro" ficava
            // amontoado — a TAG saiu (a linha de escopo já dá o contexto). No
            // AGREGADO o título é o subtítulo do tema (a combo diz a seleção);
            // em escola específica, o nome dela (marca do tema vence).
            const agregado = this.escolas.length > 0 && this.escolaSel === 'todas';
            const titleText = agregado
              ? subtitulo || 'Painel do parceiro'
              : this.tema?.nome || d.escola || 'Instituição';
            return html`<span class="title">${titleText}</span>`;
          })()}
        </div>
        <div class="head-btns">
          <div
            class="head-period"
            data-tour="periodo"
            title="Define a janela de tempo de todos os indicadores do painel."
          >
            <select class="hp-sel" @change=${this.onPeriodoChange}>
              <option value="mes_atual" ?selected=${this.periodo === 'mes_atual' && !this.periodoMes}>Mês atual</option>
              <option value="mes_passado" ?selected=${this.periodo === 'mes_passado' && !this.periodoMes}>Mês passado</option>
              <option value="7d" ?selected=${this.periodo === '7d' && !this.periodoMes}>Últimos 7 dias</option>
              <option value="30d" ?selected=${this.periodo === '30d' && !this.periodoMes}>Últimos 30 dias</option>
              <option value="90d" ?selected=${this.periodo === '90d' && !this.periodoMes}>Últimos 90 dias</option>
              <option value="ano" ?selected=${this.periodo === 'ano' && !this.periodoMes}>Este ano</option>
              <option value="tudo" ?selected=${this.periodo === 'tudo' && !this.periodoMes}>Todo período</option>
              <option value="custom" ?selected=${this.periodo === 'custom' || !!this.periodoMes}>Mês específico…</option>
            </select>
            ${this.periodo === 'custom' || this.periodoMes
              ? html`<input
                  class="hp-mes"
                  type="month"
                  .value=${this.periodoMes}
                  @change=${this.onPeriodoMesChange}
                  title="Escolha o mês"
                />`
              : ''}
          </div>
          ${this.canCopilot
            ? html`<button
                class="ibtn primary"
                data-tour="conect"
                @click=${this.openGestor}
                title=${`Analisar com o ${d.tutor_nome} sobre os dados da sua escola`}
              >
                ${this.iconSpark}<span>Analisar com o ${d.tutor_nome}</span>
              </button>`
            : ''}
          <button class="ibtn" @click=${this.openInfo} title="Como o Tutor IA funciona">
            ${this.iconInfo}<span>Como funciona</span>
          </button>
          ${this.canReports
            ? html`<button
                class="ibtn"
                data-tour="relatorio"
                @click=${this.openRelatorio}
                title="Gera um relatório do período com texto interpretado pela IA e gráficos — com opção de baixar em PDF (com a logo da escola)."
              >
                <span>Gerar relatório</span>
              </button>`
            : ''}
          <button
            class="ibtn"
            data-tour="config"
            @click=${() => this.openConfig()}
            title="Configurações do painel (aparência, acesso, coordenadores)"
          >
            ${this.iconGear}<span>Configurações</span>
          </button>
          <button
            class="ibtn"
            @click=${this.startTour}
            title="Tour guiado: mostra e explica o básico do painel."
          >
<span aria-hidden="true">🎓</span><span>Tutorial</span>
          </button>
        </div>
      </div>
      ${this.filtroAluno
        ? html`<div class="scope-line indiv">
            <span
              class="scope-kind"
              title="O painel está mostrando os dados de um único estudante."
              >Análise individual</span
            >
            <span class="scope-path"
              >${this.tema?.nome || d.escola || 'Instituição'} → Estudantes →
              <b>${this.filtroAlunoNome || this.filtroAluno}</b></span
            >
            <button
              class="scope-back"
              @click=${this.voltarVisaoInstitucional}
              title="Sai da análise individual e volta ao painel da instituição."
            >
              ← Voltar à visão institucional
            </button>
          </div>
          <div class="sub">
            Consulte o histórico de utilização, as disciplinas acessadas, os temas
            recorrentes e os registros do estudante no período selecionado.
          </div>`
        : html`<div class="scope-line">
            <span
              class="scope-kind"
              title="O painel está mostrando o conjunto da instituição (ou o agregado da rede)."
              >Visão institucional</span
            >
            ${this.escolas.length
              ? html`<select
                  class="scope-sel"
                  data-tour="escola"
                  title="Escolha uma escola específica ou 'Todas as instituições' para ver o agregado da rede."
                  @change=${this.onEscolaChange}
                >
                  <option value="todas" ?selected=${this.escolaSel === 'todas'}>
                    Todas as instituições
                  </option>
                  ${this.escolas.map(
                    (e) => html`<option value=${e.id} ?selected=${this.escolaSel === e.id}>
                      ${e.nome ?? e.id}
                    </option>`,
                  )}
                </select>`
              : html`<span class="scope-path">${this.tema?.nome || d.escola || 'Instituição'}</span>`}
          </div>
          <div class="sub">
            Acompanhe os principais movimentos de utilização, os temas mais consultados
            e os pontos que merecem atenção no período selecionado.
            ${this.escolas.length ? ' Escolha uma instituição ou veja o agregado.' : ''}
          </div>`}

      ${this.renderFiltros()}

      ${this.visibleTabs().length
        ? html`<div class="tabbar" role="tablist">
            ${this.visibleTabs().map(
              (tb) => html`<button
                class="tab ${this.activeTabId() === tb.id ? 'active' : ''}"
                role="tab"
                @click=${() => (this.activeTab = tb.id)}
              >
                ${tb.nome}
              </button>`,
            )}
          </div>`
        : ''}
      ${abaIA
        ? this.renderAbaIA(abaIA)
        : html`
      <div class="cards">
      ${CARD_SECTIONS.map(
        (s, i) =>
          html`<div class="sec-hd" data-tour="sec-${i}" style=${this.sectionStyle(s.keys)}>
            ${s.label}
          </div>`
      )}
      <div class="kpis-block" data-tour="cards" style=${this.cardStyle('kpis')}>
      ${this.tema?.cards?.kpis?.icon || this.tema?.cards?.kpis?.titulo
        ? html`<div class="hd">${this.cardHead('kpis', 'Indicadores')}</div>`
        : ''}
      <div class="grid">
        ${this.filtroAluno
          ? ''
          : html`<div
          class="stat clk"
          title=${STAT_HELP.stat_matriculados}
          style=${this.statStyle('stat_matriculados')}
          @click=${() =>
            this.openDrill('Estudantes vinculados', 'alunos', '/parceiro/alunos?bucket=matriculados&limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_matriculados', 'var(--green)')}Estudantes vinculados
          </div>
          <div class="sval">${d.matriculados}</div>
          <div class="ssub">
            ${d.pendentes_carga
              ? `${d.pendentes_carga} pendente(s) de carga`
              : 'roster carregado'}
          </div>
        </div>
        <div
          class="stat clk"
          title=${STAT_HELP.stat_ativos}
          style=${this.statStyle('stat_ativos')}
          @click=${() =>
            this.openDrill(`Estudantes ativos · ${this.periodLabel()}`, 'alunos', '/parceiro/alunos?bucket=ativos_7d&limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_ativos', 'var(--green)')}Estudantes ativos
          </div>
          <div class="sval">${d.ativos_7d} ${this.deltaBadge(d.delta_ativos, false, d.prev_ativos, d.ativos_7d)}</div>
          <div class="ssub">usaram a Tutoria IA · ${this.periodLabel()}</div>
        </div>
        <div
          class="stat clk"
          title=${STAT_HELP.stat_inativos}
          style=${this.statStyle('stat_inativos')}
          @click=${() =>
            this.openDrill(`Estudantes com baixa utilização recente · ${this.periodLabel()}`, 'alunos', '/parceiro/alunos?bucket=inativos_7d&limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_inativos', 'var(--amber)')}Estudantes com baixa utilização recente
          </div>
          <div class="sval" style="color:var(--amber)">${d.inativos_7d}</div>
          <div class="ssub">sem uso no período</div>
        </div>`}
        <div
          class="stat clk"
          title=${STAT_HELP.stat_sessoes}
          style=${this.statStyle('stat_sessoes')}
          @click=${() =>
            this.openDrill(`Sessões de tutoria · ${this.periodLabel()}`, 'sessoes', '/parceiro/sessoes?limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_sessoes', 'var(--blue)')}Sessões de tutoria
          </div>
          <div class="sval">${d.sessoes_tutor_7d} ${this.deltaBadge(d.delta_sessoes, false, d.prev_sessoes, d.sessoes_tutor_7d)}</div>
          <div class="ssub">
            sessões${d.tempo_medio_sessao_min
              ? ` · ~${d.tempo_medio_sessao_min} min/sessão`
              : ''}${d.tempo_total_min
              ? ` · ${this.fmtDuracao(d.tempo_total_min)} no total`
              : ''}${d.recorrencia_media_dias
              ? ` · ~${d.recorrencia_media_dias} dias/aluno`
              : ''} · ${this.periodLabel()}
          </div>
        </div>
        <div
          class="stat clk"
          title=${STAT_HELP.stat_perguntas}
          style=${this.statStyle('stat_perguntas')}
          @click=${() =>
            this.openDrill(`Interações com a Tutoria IA · ${this.periodLabel()}`, 'sessoes', '/parceiro/sessoes?limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_perguntas', 'var(--blue)')}Interações com a Tutoria IA
          </div>
          <div class="sval">${d.mensagens_alunos_7d} ${this.deltaBadge(d.delta_interacoes, false, d.prev_interacoes, d.mensagens_alunos_7d)}</div>
          <div class="ssub">~${d.media_msgs_por_sessao} por sessão · ${this.periodLabel()}</div>
        </div>
        <div
          class="stat clk"
          title=${STAT_HELP.stat_barrados}
          style=${this.statStyle('stat_barrados')}
          @click=${() =>
            this.openDrill('Alertas de Segurança Educacional', 'bloqueios', '/parceiro/bloqueios?limit=200')}
        >
          <div class="slabel">
            ${this.statDot('stat_barrados', 'var(--red)')}Alertas de Segurança Educacional
          </div>
          <div class="sval" style="color:var(--red)">
            ${d.bloqueios_total} ${this.deltaBadge(d.delta_barrados, true, d.prev_barrados, d.bloqueios_total)}
          </div>
          <div class="ssub">bloqueios de segurança · ${this.periodLabel()}</div>
        </div>
      </div>
      </div>

      ${(() => {
        // Bloco "Insights" (topo): agrupa "O que merece atenção" + "Síntese
        // inteligente". Só mostra o cabeçalho quando há ao menos um deles.
        const atencao = this.renderAtencao(d);
        const insights = this.renderAutoInsights();
        return atencao || insights
          ? html`<div class="sec-hd">Insights</div>${atencao}${insights}`
          : '';
      })()}

      <div class="card" style=${this.cardStyle('materias')}>
        <div class="hd">${this.cardHead('materias', 'Disciplinas mais consultadas')}</div>
        ${d.por_materia.length
          ? html`
              ${d.por_materia.map((m) => {
                // Em 'Todas' (Rede) a matéria agrega por NOME, mas o id da categoria
                // é de uma só escola — drillar por esse id descasaria total×lista.
                // Então só é clicável em escola específica.
                const drillable = this.escolaSel !== 'todas';
                return html`
                  <div
                    class="trow ${drillable ? 'clk' : ''}"
                    title=${drillable
                      ? ''
                      : 'Selecione uma instituição para ver as perguntas desta matéria'}
                    @click=${drillable
                      ? () =>
                          this.openDrill(
                            `Perguntas · ${m.materia}`,
                            'perguntas',
                            `/parceiro/perguntas?categoria_id=${encodeURIComponent(m.categoria_id)}&limit=200`,
                          )
                      : null}
                  >
                    <span class="tname">${m.materia}</span>
                    <span class="ttrack"
                      ><span
                        class="tbar"
                        style="width:${Math.round((m.total / maxMateria) * 100)}%"
                      ></span
                    ></span>
                    <span class="tnum">${m.total}</span>
                  </div>
                `;
              })}
              <div class="note">Classificação automática por IA das perguntas.</div>
            `
          : html`<div class="empty">Sem perguntas classificadas neste período.</div>`}
      </div>
      ${tematicas.length
        ? html`
      <div class="card" style=${this.cardStyle('tematicas')}>
        <div class="hd">${this.cardHead('tematicas', 'Temas e dúvidas mais recorrentes')}</div>
        ${tematicas.map(
          (t) => html`
              <div class="trow t-fine">
                <span class="tname" title=${t.tematica}>${t.tematica}</span>
                <span class="ttrack"
                  ><span class="tbar" style="width:${Math.round((t.total / maxTematica) * 100)}%"></span
                ></span>
                <span class="tnum">${t.total}</span>
              </div>
            `,
        )}
        <div class="note">
          Assuntos específicos que os alunos mais perguntam (extraídos por IA) — dimensão
          mais fina que a disciplina.
        </div>
      </div>`
        : ''}
      ${this.renderSerieTemporal(d)}
      ${this.renderEvolucaoTematicas(d)}
      ${this.renderPeriodoLetivo(d)}
      ${this.renderEmergentes(d)}
      <div class="card half" style=${this.cardStyle('classificacao')}>
              <div class="hd">${this.cardHead('classificacao', 'Classificação das interações')}</div>
              <div class="trow">
                <span class="tname" style="width:auto;flex:1">Classificadas</span>
                <span class="tnum" style="color:var(--blue)">${d.perguntas_classificadas}</span>
              </div>
              <div
                class="trow ${d.perguntas_sem_materia ? 'clk' : ''}"
                @click=${() =>
                  d.perguntas_sem_materia
                    ? this.openDrill(
                        'Perguntas sem matéria',
                        'perguntas',
                        '/parceiro/perguntas?sem_materia=1&limit=200',
                      )
                    : undefined}
              >
                <span class="tname" style="width:auto;flex:1">Sem matéria (processadas)</span>
                <span class="tnum" style="color:var(--amber)">${d.perguntas_sem_materia}</span>
              </div>
              <div class="trow">
                <span class="tname" style="width:auto;flex:1">Fora de conteúdo</span>
                <span class="tnum">${d.perguntas_fora_conteudo}</span>
              </div>
              <div class="trow">
                <span class="tname" style="width:auto;flex:1">Aguardando processamento</span>
                <span class="tnum">${d.perguntas_pendentes}</span>
              </div>
              <div class="note">
                "Sem matéria" = é conteúdo de estudo, mas sem matéria cadastrada.
                "Fora de conteúdo" = pergunta pessoal/administrativa ou saudação.
                "Aguardando" = ainda não processadas pela IA.
              </div>
            </div>
      ${this.filtroAluno
        ? html`<div class="card half" style=${this.cardStyle('etapa')}>
            <div class="hd">${this.cardHead('etapa', 'Contexto acadêmico do estudante')}</div>
            <div class="trow">
              <span class="tname">Instituição</span>
              <span>${this.tema?.nome || d.escola || '—'}</span>
            </div>
            <div class="trow">
              <span class="tname">Etapa</span>
              <span>${d.por_etapa[0]?.etapa ?? '— sem etapa —'}</span>
            </div>
            <div class="note">Dados de matrícula vindos da carga do Moodle.</div>
          </div>`
        : html`<div class="card half" style=${this.cardStyle('etapa')}>
        <div class="hd">${this.cardHead('etapa', 'Estudantes por etapa')}</div>
        ${d.por_etapa.length
          ? html`
              ${d.por_etapa.map(
                (e) => html`
                  <div
                    class="trow clk"
                    @click=${() =>
                      this.openDrill(
                        `Alunos · ${e.etapa ?? 'sem etapa'}`,
                        'alunos',
                        `/parceiro/alunos?etapa=${encodeURIComponent(e.etapa ?? '__sem__')}&limit=200`,
                      )}
                  >
                    <span class="tname">${e.etapa ?? '— sem etapa —'}</span>
                    <span class="ttrack"
                      ><span
                        class="tbar"
                        style="width:${Math.round((e.total / maxEtapa) * 100)}%"
                      ></span
                    ></span>
                    <span class="tnum">${e.total}</span>
                  </div>
                `,
              )}
            `
          : html`<div class="empty">Sem alunos com etapa definida.</div>`}
      </div>`}
      ${this.renderUsoEtapa(d)}
      ${this.renderEstudantesAtivos(d)}
      <div class="card" style=${this.cardStyle('seguranca')}>
        <div class="hd">${this.cardHead('seguranca', 'Governança e Segurança Educacional')}</div>
        ${(() => {
          // % de interações fora do escopo educacional = perguntas classificadas como
          // "fora de conteúdo" ÷ total de interações processadas (derivado, sem query nova).
          const totInt =
            d.perguntas_classificadas +
            d.perguntas_sem_materia +
            d.perguntas_fora_conteudo +
            d.perguntas_pendentes;
          // Prefere o valor do backend (fonte única do grounding); cai no cálculo
          // local só se o payload não trouxer o campo.
          const pct =
            d.pct_fora_conteudo ??
            (totInt ? Math.round((d.perguntas_fora_conteudo / totInt) * 100) : 0);
          return html`<div class="gov-pct">
            <span class="gov-pct-v">${pct}%</span>
            <span class="gov-pct-l"
              >das interações fora do escopo educacional · ${this.periodLabel()}</span
            >
          </div>`;
        })()}
        ${d.por_categoria_bloqueio.length
          ? html`
              ${d.por_categoria_bloqueio.map(
                (c) => html`
                  <div
                    class="trow clk"
                    @click=${() =>
                      this.openDrill(
                        `Barrados · ${c.rotulo}`,
                        'bloqueios',
                        `/parceiro/bloqueios?categoria=${encodeURIComponent(c.categoria)}&limit=200`,
                      )}
                  >
                    <span class="tname">${c.rotulo}</span>
                    <span class="ttrack"
                      ><span
                        class="tbar red"
                        style="width:${Math.round((c.total / maxCat) * 100)}%"
                      ></span
                    ></span>
                    <span class="tnum">${c.total}</span>
                  </div>
                `,
              )}
            `
          : html`<div class="empty">Nenhuma tentativa barrada neste período. 🎉</div>`}
        </div>
      </div>
      ${d.desmatriculados
        ? html`<div class="note">
            ${d.desmatriculados} aluno(s) desmatriculado(s) não entram nos
            matriculados.
          </div>`
        : ''}
      <div class="note">
        ${this.escolas.length
          ? this.escolaSel === 'todas'
            ? 'Agregado de todas as instituições do seu portal.'
            : 'Dados da instituição selecionada.'
          : 'Você vê apenas os dados da sua instituição.'}
      </div>
      `}
      </div>

      ${this.showInfo ? this.renderInfo() : ''}
      ${this.drill ? this.renderDrill(this.drill) : ''}
      ${this.showGestor ? this.renderGestor() : ''}
      ${this.showRelatorio ? this.renderRelatorio() : ''}
      ${this.showConfig ? this.renderConfig() : ''}
    `;
  }

  private renderGestorList(): TemplateResult {
    return html`
      <div class="gconvlist">
        ${this.gestorSessions.length === 0
          ? html`<div class="note">Nenhuma conversa ainda.</div>`
          : this.gestorSessions.map((c) =>
              this.gestorRenamingId === c.id
                ? html`
                    <div class="gconvrow">
                      <input
                        class="grename"
                        .value=${this.gestorRenameText}
                        @input=${(e: Event) =>
                          (this.gestorRenameText = (e.target as HTMLInputElement).value)}
                        @keydown=${(e: KeyboardEvent) => {
                          if (e.key === 'Enter') void this.confirmRename(c.id);
                          if (e.key === 'Escape') this.gestorRenamingId = null;
                        }}
                      />
                      <button class="glink" @click=${() => this.confirmRename(c.id)}>
                        salvar
                      </button>
                      <button class="glink" @click=${() => (this.gestorRenamingId = null)}>
                        cancelar
                      </button>
                    </div>
                  `
                : html`
                    <div class="gconvrow ${c.id === this.gestorSessionId ? 'ativa' : ''}">
                      <span class="gconvname" @click=${() => this.loadGestorConversa(c.id)}>
                        ${c.titulo || 'Conversa'}
                        <span class="gconvmeta"
                          >${this.fmtDate(c.created_at)} · ${c.mensagens} msg</span
                        >
                      </span>
                      <button class="glink" @click=${() => this.startRename(c.id, c.titulo || '')}>
                        renomear
                      </button>
                      <button class="glink del" @click=${() => this.excluirConversa(c.id)}>
                        excluir
                      </button>
                    </div>
                  `,
            )}
      </div>
    `;
  }

  // ─── Aba de IA (corpo do painel quando a aba ativa é de IA) ─────────────────
  private renderAbaIA(tab: TabCfg): TemplateResult {
    const res = this.abaIA[tab.id];
    const loading = this.abaIALoading === tab.id;
    const canGen = res?.can_generate ?? this.isManager;
    const promptView = (res?.prompt || tab.ai_prompt || '').trim();
    const head = html`<div class="aia-head">
      <div class="aia-title">${this.iconSpark}<span>${tab.nome}</span></div>
      ${canGen
        ? html`<button
            class="ibtn primary aia-gen"
            ?disabled=${loading}
            @click=${() => this.generateAbaIA(tab.id)}
          >
            ${loading ? 'Gerando…' : res?.result_md ? 'Atualizar' : 'Gerar análise'}
          </button>`
        : ''}
    </div>`;
    const promptLine = promptView
      ? html`<div class="aia-prompt">Pergunta: “${promptView}”</div>`
      : html`<div class="aia-prompt muted">
          Esta aba ainda não tem uma pergunta configurada.
          ${canGen
            ? 'Defina em Personalizar → Abas.'
            : 'Peça a um gestor para configurar.'}
        </div>`;
    const { from, to } = this.periodRange();
    // Resultado FRESCO pro período selecionado? (senão é de outro período)
    const freshForCurrent =
      !!res?.result_md && res.periodo_de === from && res.periodo_ate === to;
    // Gerando pra um período DIFERENTE (ou 1ª geração) → NÃO mostra o resultado
    // antigo (evita confundir com o período errado); mostra só o "atualizando".
    const showLoading = loading && !freshForCurrent;

    let body: TemplateResult;
    if (showLoading) {
      body = html`<div class="aia-updating aia-loading-lg">
        <span class="aia-spin"></span>Atualizando para “${this.periodLabel()}”… consultando
        ${this.data?.tutor_nome || 'o analista'}.
      </div>`;
    } else if (this.abaIAError && !res?.result_md) {
      body = html`<div class="err">${this.abaIAError}</div>`;
    } else if (res?.result_md) {
      body = html`<div class="gmd aia-body">${this.renderGestorContent(res.result_md)}</div>`;
    } else {
      body = html`<div class="aia-empty">
        ${canGen
          ? 'Clique em “Gerar análise” para o analista responder com base nos dados do período.'
          : 'Ainda não há uma análise gerada. Um gestor pode gerar.'}
      </div>`;
    }
    // Refresh do MESMO período (botão Atualizar): mantém o conteúdo e só avisa.
    const updatingSame =
      loading && freshForCurrent
        ? html`<div class="aia-updating"><span class="aia-spin"></span>Atualizando…</div>`
        : '';
    const per = (res?.periodo_label || '').trim();
    const range =
      res?.periodo_de && res?.periodo_ate
        ? `${this.fmtDMY(res.periodo_de)} – ${this.fmtDMY(res.periodo_ate)}`
        : '';
    const perTxt = per ? (range ? `${per} (${range})` : per) : range;
    // Meta e aviso de período só quando NÃO está trocando de período (senão
    // mostrariam o período/carimbo do resultado antigo, que está sendo descartado).
    const meta =
      res?.result_md && !showLoading
        ? html`<div class="aia-meta">
            ${res.generated_at
              ? html`Atualizado em ${this.fmtDateTime(res.generated_at)}`
              : ''}${perTxt ? html` · período: ${perTxt}` : ''}
          </div>`
        : '';
    // Coordenador (não gera) vendo um resultado de período diferente do selecionado.
    const staleForViewer =
      !showLoading &&
      !canGen &&
      res?.result_md &&
      (res.periodo_de !== from || res.periodo_ate !== to)
        ? html`<div class="aia-meta warn">
            Este resultado é de outro período. Peça a um gestor para atualizar.
          </div>`
        : '';
    return html`<div class="aia">
      ${head}${promptLine}${updatingSame}${body}${meta}${staleForViewer}
    </div>`;
  }

  // ─── Relatório do período (modal + PDF) ──────────────────────────────────────
  private renderReportChart(chart: ReportChart | null): TemplateResult | '' {
    if (!chart) return '';
    const series = chart.series || [];
    if (!series.length) return '';
    const title = chart.title || '';
    const nonZero = series.filter((s) => s.value > 0);
    const t = chart.type;
    if ((t === 'pie' || t === 'donut') && series.length >= 2 && nonZero.length >= 1)
      return this.chartPie(title, series, t === 'donut');
    if (t === 'stacked' && nonZero.length >= 2) return this.chartStacked(title, series);
    if (t === 'radar' && series.length >= 3) return this.chartRadar(title, series);
    if (t === 'gauge') return this.chartGauge(title, series);
    if (t === 'column') return this.chartColumns(title, series);
    if (t === 'area') return this.chartLine(title, series, true);
    if (t === 'line') return this.chartLine(title, series);
    return this.chartBar(title, series);
  }

  /** Bloco "Insights da IA": interpretação automática (bullets) grounded no snapshot. */
  /** v2 §2.1 — Bloco "O que merece atenção": no MÁXIMO 3 achados objetivos,
   * derivados dos dados já carregados (deltas/baixa utilização/temas em
   * crescimento) — sem chamada extra de IA. Sempre com valores absolutos.
   * Fecha com a chamada pro Conect (CTA de aprofundamento). */
  private renderAtencao(d: ParceiroData): TemplateResult | '' {
    const itens: { icon: string; txt: string }[] = [];
    // 1) Segurança em alta (prioridade máxima).
    if ((d.delta_barrados ?? 0) > 0 && d.prev_barrados !== null && d.prev_barrados !== undefined) {
      itens.push({
        icon: '⚠',
        txt: `Alertas de segurança subiram de ${d.prev_barrados} para ${d.bloqueios_total} no período (+${d.bloqueios_total - (d.prev_barrados ?? 0)}).`,
      });
    }
    // 2) Queda de uso.
    if ((d.delta_sessoes ?? 0) < 0 && d.prev_sessoes !== null && d.prev_sessoes !== undefined) {
      itens.push({
        icon: '▾',
        txt: `Sessões de tutoria caíram de ${d.prev_sessoes} para ${d.sessoes_tutor_7d} no período.`,
      });
    }
    // 3) Baixa utilização (só na visão institucional — população).
    if (!this.filtroAluno && d.inativos_7d > 0) {
      itens.push({
        icon: '◦',
        txt: `${d.inativos_7d} estudante(s) sem uso da Tutoria IA no período.`,
      });
    }
    // 4) Tema em crescimento (contexto pedagógico).
    const em = (d.assuntos_emergentes ?? [])[0];
    if (em) {
      itens.push({
        icon: '▴',
        txt: `${em.materia} em crescimento: de ${em.anterior} para ${em.atual} consultas.`,
      });
    }
    if (!itens.length) return '';
    const top3 = itens.slice(0, 3);
    return html`<div class="card atencao">
      <div class="hd">O que merece atenção</div>
      ${top3.map(
        (i) => html`<div class="atn-row"><span class="atn-ic">${i.icon}</span>${i.txt}</div>`,
      )}
      ${this.canCopilot
        ? html`<button
            class="gtbtn atn-cta"
            @click=${() => this.analisarAtencao(top3)}
            title="Abre o copiloto de IA já perguntando sobre estes pontos de atenção."
          >
            Analisar com o ${this.data?.tutor_nome || 'Conect'} →
          </button>`
        : ''}
    </div>`;
  }

  /** CTA do "O que merece atenção": abre o Conect JÁ perguntando sobre os achados
   * (feedback 17/07 — abrir vazio perdia a referência). O recorte vai junto: os
   * filtros ativos entram no grounding do stream e o texto cita o aluno filtrado. */
  private async analisarAtencao(itens: { icon: string; txt: string }[]): Promise<void> {
    const alvo = this.filtroAluno
      ? ` do estudante ${this.filtroAlunoNome || this.filtroAluno}`
      : '';
    const q =
      `Analise os pontos de atenção${alvo} (${this.periodLabel()}): ` +
      itens.map((i, n) => `${n + 1}) ${i.txt}`).join(' ') +
      ' O que pode explicar cada ponto e o que a gestão deve priorizar?';
    await this.openGestor();
    this.askGestor(q);
  }

  private renderAutoInsights(): TemplateResult | '' {
    const ai = this.autoInsights;
    if (!ai) return '';
    // Nada a mostrar pra quem não pode gerar e ainda não há cache.
    if (!ai.insights_md && !ai.can_generate && !this.autoInsightsLoading) return '';
    return html`
      <div class="card ai-insights" data-tour="insights">
        <div class="hd">
          <span>Síntese inteligente do período</span>
          ${ai.can_generate
            ? html`<button
                class="ai-refresh"
                ?disabled=${this.autoInsightsLoading}
                @click=${() => this.generateAutoInsights()}
                title="Regera a leitura automática da IA com os dados atuais do período/filtro selecionado."
              >
                ${this.autoInsightsLoading ? 'Gerando…' : '↻ Atualizar análise'}
              </button>`
            : ''}
        </div>
        ${this.autoInsightsErr
          ? html`<div class="err">${this.autoInsightsErr}</div>`
          : ''}
        ${this.autoInsightsLoading && !ai.insights_md
          ? html`<div class="empty">Interpretando os indicadores do período…</div>`
          : ai.insights_md
            ? html`<div class="gmd ai-md">
                ${unsafeHTML(mdToHtml(ai.insights_md))}
              </div>`
            : html`<div class="empty">
                Sem insights ainda${ai.can_generate ? ' — clique em Atualizar.' : '.'}
              </div>`}
        ${ai.insights_md && this.autoInsightsStale(ai) && !this.autoInsightsLoading
          ? html`<div class="ai-stale">
              ⚠ Insights de um período anterior${ai.periodo_label
                ? ` (${ai.periodo_label})`
                : ''} — ${ai.can_generate
                ? 'clique em Atualizar para o período atual.'
                : 'peça a um gestor para atualizar.'}
            </div>`
          : ''}
        ${ai.generated_at
          ? html`<div class="note ai-gen">
              Gerado ${this.fmtDateTime(ai.generated_at)}${ai.periodo_label
                ? ` · ${ai.periodo_label}`
                : ''}
              ${this.hasFiltroGranular()
                ? html` · <b>recorte filtrado</b>${this.filtroAlunoNome
                    ? html` (${this.filtroAlunoNome})`
                    : ''} — não salvo no cache da escola`
                : html` · grounded nos dados desta instituição`}
            </div>`
          : ''}
      </div>
    `;
  }

  /** Fase 3b — Temas em crescimento: disciplinas/temáticas que mais cresceram vs. o
   * período anterior (inclui as NOVAS). Vem preenchido só em janelas curtas (≤ ~1 ano);
   * em "Todo período" fica vazio (não há anterior comparável) → some. */
  /** Fase 3 — Evolução ao longo do tempo: linha de interações + alertas por bucket
   * (dia se o período ≤ ~2 meses, senão semana). Cada série normalizada ao próprio
   * máximo (mostra o FORMATO da evolução; alertas costumam ser bem menores). */
  private toggleCard(k: string): void {
    const s = new Set(this.cardsAbertos);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    this.cardsAbertos = s;
  }

  /** Casca COLAPSADA de um card de detalhe (v2 §2.3): título + resumo de 1 linha;
   * o corpo (gráfico/série) só renderiza ao expandir. */
  private foldedCard(key: string, titulo: string, resumo: string): TemplateResult {
    return html`<div class="card" style=${this.cardStyle(key)}>
      <div class="hd hd-fold" @click=${() => this.toggleCard(key)}>
        ${this.cardHead(key, titulo)}<span class="fold-arr">▸ expandir</span>
      </div>
      <div class="note fold-note" @click=${() => this.toggleCard(key)}>${resumo}</div>
    </div>`;
  }

  private renderSerieTemporal(d: ParceiroData): TemplateResult | '' {
    const s = d.serie_temporal ?? [];
    if (s.length < 2) return ''; // uma linha precisa de ≥2 pontos
    if (!this.cardsAbertos.has('serie_temporal')) {
      const tot = s.reduce((a, p) => a + p.interacoes, 0);
      return this.foldedCard(
        'serie_temporal',
        'Evolução ao longo do tempo',
        `${s.length} pontos no período · ${tot} interações — clique para ver o gráfico.`,
      );
    }
    const W = 560;
    const H = 120;
    const padL = 6;
    const padR = 6;
    const padT = 12;
    const padB = 8;
    const n = s.length;
    const maxI = Math.max(1, ...s.map((p) => p.interacoes));
    const maxA = Math.max(1, ...s.map((p) => p.alertas));
    const x = (i: number) => padL + (i / (n - 1)) * (W - padL - padR);
    const y = (v: number, max: number) => padT + (1 - v / max) * (H - padT - padB);
    const poly = (sel: (p: { interacoes: number; alertas: number }) => number, max: number) =>
      s.map((p, i) => `${x(i).toFixed(1)},${y(sel(p), max).toFixed(1)}`).join(' ');
    const fmtD = (iso: string) => {
      const [, m, dd] = iso.split('-');
      return `${dd}/${m}`;
    };
    const temAlerta = s.some((p) => p.alertas > 0);
    return html`
      <div class="card" style=${this.cardStyle('serie_temporal')}>
        <div class="hd">${this.cardHead('serie_temporal', 'Evolução ao longo do tempo')}</div>
        <svg
          viewBox="0 0 ${W} ${H}"
          width="100%"
          preserveAspectRatio="none"
          style="max-height:150px;overflow:visible"
        >
          <polyline
            points="${poly((p) => p.interacoes, maxI)}"
            fill="none"
            stroke="var(--blue)"
            stroke-width="2"
          />
          ${temAlerta
            ? svg`<polyline
                points="${poly((p) => p.alertas, maxA)}"
                fill="none"
                stroke="var(--red)"
                stroke-width="2"
              />`
            : ''}
        </svg>
        <div class="serie-leg">
          <span><i style="background:var(--blue)"></i>Interações</span>
          ${temAlerta
            ? html`<span><i style="background:var(--red)"></i>Alertas de segurança</span>`
            : ''}
          <span class="serie-range">${fmtD(s[0].data)} — ${fmtD(s[n - 1].data)}</span>
        </div>
        <div class="note">Interações e alertas ao longo do período (cada linha na própria escala).</div>
      </div>
    `;
  }

  /** Fase 4 — Uso por período letivo nomeado (bimestre/semestre que a instituição
   * define em Personalizar → Períodos letivos). Oculto sem períodos configurados. */
  /** Refinamento — Evolução dos temas consultados: multi-linha das ~4 temáticas mais
   * consultadas ao longo do tempo (bucket dia/semana), escala compartilhada. */
  private renderEvolucaoTematicas(d: ParceiroData): TemplateResult | '' {
    const series = (d.serie_tematicas ?? []).filter((s) => s.pontos.length >= 2);
    if (!series.length) return '';
    if (!this.cardsAbertos.has('serie_tematicas')) {
      return this.foldedCard(
        'serie_tematicas',
        'Evolução dos temas consultados',
        `${series.length} temas acompanhados (${series
          .map((s) => s.tematica)
          .slice(0, 3)
          .join(', ')}…) — clique para ver o gráfico.`,
      );
    }
    const W = 560;
    const H = 130;
    const padL = 6;
    const padR = 6;
    const padT = 12;
    const padB = 8;
    const dates = Array.from(
      new Set(series.flatMap((s) => s.pontos.map((p) => p.data))),
    ).sort();
    const xi = new Map(dates.map((dt, i) => [dt, i] as const));
    const n = dates.length;
    const maxV = Math.max(1, ...series.flatMap((s) => s.pontos.map((p) => p.total)));
    const x = (i: number) => padL + (n > 1 ? i / (n - 1) : 0.5) * (W - padL - padR);
    const y = (v: number) => padT + (1 - v / maxV) * (H - padT - padB);
    const colors = ['var(--blue)', 'var(--green)', 'var(--amber)', 'var(--red)'];
    const fmtD = (iso: string) => {
      const [, m, dd] = iso.split('-');
      return `${dd}/${m}`;
    };
    return html`
      <div class="card" style=${this.cardStyle('serie_tematicas')}>
        <div class="hd">${this.cardHead('serie_tematicas', 'Evolução dos temas consultados')}</div>
        <svg
          viewBox="0 0 ${W} ${H}"
          width="100%"
          preserveAspectRatio="none"
          style="max-height:150px;overflow:visible"
        >
          ${series.map((s, si) => {
            const pts = s.pontos
              .map((p) => `${x(xi.get(p.data) ?? 0).toFixed(1)},${y(p.total).toFixed(1)}`)
              .join(' ');
            return svg`<polyline
              points="${pts}"
              fill="none"
              stroke="${colors[si % colors.length]}"
              stroke-width="2"
            />`;
          })}
        </svg>
        <div class="serie-leg">
          ${series.map(
            (s, si) =>
              html`<span
                ><i style="background:${colors[si % colors.length]}"></i>${s.tematica}</span
              >`,
          )}
          <span class="serie-range">${fmtD(dates[0])} — ${fmtD(dates[n - 1])}</span>
        </div>
        <div class="note">As temáticas mais consultadas, período a período.</div>
      </div>
    `;
  }

  private renderPeriodoLetivo(d: ParceiroData): TemplateResult | '' {
    const pl = d.uso_por_periodo_letivo ?? [];
    if (!pl.length) return '';
    const maxP = Math.max(1, ...pl.map((p) => p.total));
    return html`
      <div class="card half" style=${this.cardStyle('periodo_letivo')}>
        <div class="hd">${this.cardHead('periodo_letivo', 'Uso por período letivo')}</div>
        ${pl.map(
          (p) => html`
            <div class="trow">
              <span class="tname">${p.nome}</span>
              <span class="ttrack"
                ><span class="tbar" style="width:${Math.round((p.total / maxP) * 100)}%"></span
              ></span>
              <span class="tnum">${p.total}</span>
            </div>
          `,
        )}
        <div class="note">Interações por período letivo do calendário da instituição.</div>
      </div>
    `;
  }

  private renderEmergentes(d: ParceiroData): TemplateResult | '' {
    const em = d.assuntos_emergentes ?? [];
    // Sem dados (janela longa ou nada cresceu): não polui o painel com card vazio.
    if (!em.length) return '';
    return html`
      <div class="card half" style=${this.cardStyle('emergentes')}>
        <div class="hd">${this.cardHead(
          'emergentes',
          this.filtroAluno
            ? 'Temas em crescimento no histórico do estudante'
            : 'Temas em crescimento',
        )}</div>
        ${em.map(
          (e) => html`
            <div class="trow">
              <span class="tname">${e.materia}</span>
              <span class="emg-badge"
                >${e.growth_pct === null
                  ? 'novo'
                  : `▲ ${e.growth_pct >= 0 ? '+' : ''}${e.growth_pct}%`}</span
              >
              <span class="tnum" style="width:auto;min-width:52px"
                >${e.anterior} → ${e.atual}</span
              >
            </div>
          `,
        )}
        <div class="note">
          Disciplinas/temáticas que mais cresceram em relação ao período anterior.
        </div>
      </div>
    `;
  }

  /** v2 §5/crit.6 — Análise INDIVIDUAL: no lugar do ranking coletivo (que mostraria
   * 1 pessoa sozinha), a atividade DO estudante no período. Mesmos dados do snapshot
   * já filtrado pelo aluno — sem chamada extra. */
  private renderAtividadeEstudante(d: ParceiroData): TemplateResult {
    const nome = this.filtroAlunoNome || this.filtroAluno;
    const stats: { l: string; v: string; s: string }[] = [
      { l: 'Sessões de tutoria', v: String(d.sessoes_tutor_7d), s: this.periodLabel() },
      {
        l: 'Dias de utilização',
        v: String(d.recorrencia_media_dias || 0),
        s: 'dias distintos com sessão',
      },
      {
        l: 'Tempo total',
        v: this.fmtDuracao(d.tempo_total_min || 0),
        s: d.tempo_medio_sessao_min ? `~${d.tempo_medio_sessao_min} min/sessão` : '—',
      },
      {
        l: 'Interações',
        v: String(d.mensagens_alunos_7d),
        s: `~${d.media_msgs_por_sessao} por sessão`,
      },
    ];
    // Última utilização + tempo sem atividade (v2 §5): derivados da série temporal
    // já filtrada pelo aluno — último bucket com interação.
    const pontos = (d.serie_temporal ?? []).filter((p) => p.interacoes > 0);
    let ultima = '';
    if (pontos.length) {
      const last = pontos[pontos.length - 1].data;
      const dt = new Date(`${last}T12:00:00`);
      const dias = Math.max(0, Math.round((Date.now() - dt.getTime()) / 86400000));
      ultima = `Última utilização: ${dt.toLocaleDateString('pt-BR')}${
        dias > 0 ? ` · sem atividade há ${dias} dia(s)` : ' · usou hoje'
      }.`;
    }
    return html`<div class="card" style=${this.cardStyle('estudantes_ativos')}>
      <div class="hd">${this.cardHead('estudantes_ativos', 'Atividade do estudante no período')}</div>
      <div class="grid">
        ${stats.map(
          (st) => html`<div class="stat">
            <div class="slabel">${st.l}</div>
            <div class="sval">${st.v}</div>
            <div class="ssub">${st.s}</div>
          </div>`,
        )}
      </div>
      <div class="note">${ultima ? `${ultima} ` : ''}Atividade de ${nome} · ${this.periodLabel()}.</div>
    </div>`;
  }

  /** Fase 3b — Estudantes com maior utilização: top por nº de sessões no período. Clicar num
   * estudante escopa TODO o painel nele (reusa o filtro de aluno). Na análise
   * individual (v2 crit. 6), vira a atividade DO estudante — nunca ranking de 1. */
  private renderEstudantesAtivos(d: ParceiroData): TemplateResult | '' {
    if (this.filtroAluno) return this.renderAtividadeEstudante(d);
    const ativos = d.estudantes_mais_ativos ?? [];
    if (!ativos.length) return '';
    const maxSess = Math.max(1, ...ativos.map((e) => e.sessoes));
    return html`
      <div class="card" style=${this.cardStyle('estudantes_ativos')}>
        <div class="hd">
          ${this.cardHead('estudantes_ativos', 'Estudantes com maior utilização')}
        </div>
        ${ativos.map(
          (e) => html`
            <div
              class="trow clk"
              title="Filtrar o painel por este estudante"
              @click=${() =>
                this.selectAluno({
                  user_id: e.user_id,
                  name: e.name,
                  etapa: null,
                  ano_serie: null,
                })}
            >
              <span class="tname">${e.name ?? e.user_id}</span>
              <span class="ttrack"
                ><span
                  class="tbar"
                  style="width:${Math.round((e.sessoes / maxSess) * 100)}%"
                ></span
              ></span>
              <span class="tnum">${e.sessoes}</span>
            </div>
          `,
        )}
        ${this.renderBaixoUso(d, ativos)}
        <div class="note">Por nº de sessões no período · clique para filtrar.</div>
      </div>
    `;
  }

  /** Seção "Menor utilização" dentro do card de estudantes — os que usaram pouco (que
   * não estão no top). Ajuda a identificar quem precisa de incentivo. */
  private renderBaixoUso(
    d: ParceiroData,
    ativos: EstudanteAtivo[],
  ): TemplateResult | '' {
    const topIds = new Set(ativos.map((e) => e.user_id));
    const baixo = (d.estudantes_baixo_uso ?? []).filter((e) => !topIds.has(e.user_id));
    if (!baixo.length) return '';
    return html`
      <div class="bu-sep">Estudantes com menor utilização</div>
      ${baixo.map(
        (e) => html`<div
          class="trow clk bu-row"
          title="Filtrar o painel por este estudante"
          @click=${() =>
            this.selectAluno({
              user_id: e.user_id,
              name: e.name,
              etapa: null,
              ano_serie: null,
            })}
        >
          <span class="tname">${e.name ?? e.user_id}</span>
          <span class="bu-num">${e.sessoes} ${e.sessoes === 1 ? 'sessão' : 'sessões'}</span>
        </div>`,
      )}
    `;
  }

  /** Uso da Tutoria por etapa/ano (perguntas dos alunos por etapa) — "qual série usa
   * mais o chat". Distinto de "Estudantes por etapa" (que é matrícula). */
  private renderUsoEtapa(d: ParceiroData): TemplateResult | '' {
    // Análise individual (v2 §5): "uso por etapa" de UM aluno não informa nada —
    // a utilização dele já aparece por disciplina, semana e período nos outros cards.
    if (this.filtroAluno) return '';
    const uso = d.perguntas_por_etapa ?? [];
    if (!uso.length) return '';
    const maxUso = Math.max(1, ...uso.map((e) => e.total));
    return html`
      <div class="card half" style=${this.cardStyle('uso_etapa')}>
        <div class="hd">${this.cardHead('uso_etapa', 'Uso da Tutoria por etapa/ano')}</div>
        ${uso.map(
          (e) => html`<div class="trow">
            <span class="tname">${e.etapa ?? '— sem etapa —'}</span>
            <span class="ttrack"
              ><span
                class="tbar"
                style="width:${Math.round((e.total / maxUso) * 100)}%"
              ></span
            ></span>
            <span class="tnum">${e.total}</span>
          </div>`,
        )}
        <div class="note">Interações dos estudantes por etapa/série no período.</div>
      </div>
    `;
  }

  private renderRelatorio(): TemplateResult {
    const rep = this.relatorio?.report;
    return html`
      <div
        class="panel gpanel"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeRelatorio();
        }}
      >
        <div class="gcard rcard">
          <div class="panel-head">
            <span class="panel-title">Relatório do período</span>
            <button class="xbtn" @click=${() => this.closeRelatorio()}>
              ${this.iconClose}
            </button>
          </div>
          <div class="rtoolbar">
            <span class="rperiodo"
              >${rep?.escola ?? this.data?.escola ?? ''}${rep
                ? ` · ${rep.periodo_label}`
                : ''}</span
            >
            <span class="rspacer"></span>
            <button
              class="gtbtn"
              ?disabled=${this.relatorioLoading}
              @click=${() => this.generateRelatorio()}
            >
              ${this.relatorioLoading ? 'Gerando…' : 'Gerar de novo'}
            </button>
            <button
              class="gtbtn primary"
              ?disabled=${!rep || this.relatorioPdfLoading}
              @click=${() => this.downloadRelatorioPdf()}
            >
              ${this.relatorioPdfLoading ? 'Preparando…' : 'Baixar PDF'}
            </button>
          </div>
          <div class="rsec-pick">
            <span class="rsec-pick-l">Seções:</span>
            <button
              class="rsec-chip ${this.relatorioSecoes.length === 0 ? 'on' : ''}"
              @click=${() => this.clearRelatorioSecoes()}
            >
              Todas
            </button>
            ${REPORT_SECTIONS.map(
              (s) => html`<button
                class="rsec-chip ${this.relatorioSecoes.includes(s.key) ? 'on' : ''}"
                @click=${() => this.toggleRelatorioSecao(s.key)}
              >
                ${s.label}
              </button>`,
            )}
            <span class="rsec-pick-hint">
              Escolha e clique <b>Gerar de novo</b>.
            </span>
          </div>
          <div class="rbody">
            ${this.relatorioError
              ? html`<div class="err">${this.relatorioError}</div>`
              : ''}
            ${this.relatorioLoading && !rep
              ? html`<div class="aia-loading">Compilando o relatório do período…</div>`
              : rep
                ? html`
                    ${rep.kpis?.length
                      ? html`<div class="rkpis">
                          ${rep.kpis.map(
                            (k) => html`<div class="rkpi">
                              <div class="rkpi-v">${k.value}</div>
                              <div class="rkpi-l">${k.label}</div>
                            </div>`,
                          )}
                        </div>`
                      : ''}
                    ${rep.sections.map((sec) =>
                      sec.texto || sec.chart
                        ? html`<div class="rsec">
                            <div class="rsec-t">${sec.titulo}</div>
                            ${sec.chart ? this.renderReportChart(sec.chart) : ''}
                            ${sec.texto
                              ? html`<div class="gmd rsec-md">
                                  ${unsafeHTML(mdToHtml(sec.texto))}
                                </div>`
                              : ''}
                          </div>`
                        : '',
                    )}
                  `
                : this.relatorioError
                  ? ''
                  : html`<div class="aia-empty">Sem relatório.</div>`}
          </div>
        </div>
      </div>
    `;
  }

  // ─── Barra de filtros granulares (etapa/ano-série/matéria) ───────────────────
  private renderFiltros(): TemplateResult | '' {
    const f = this.filtros;
    if (!f) return '';
    const hasEtapa = f.etapas.length > 0 || f.tem_sem_etapa;
    const anosAll = f.anos_serie ?? [];
    // Ano-série depende da etapa selecionada (filtro dependente). 'Sem etapa'
    // (__sem__) só lista anos de alunos sem etapa; sem etapa escolhida, lista tudo.
    const anos = !this.filtroEtapa
      ? anosAll
      : this.filtroEtapa === '__sem__'
        ? anosAll.filter((a) => a.etapa === null)
        : anosAll.filter((a) => a.etapa === this.filtroEtapa);
    const anoVals = [...new Set(anos.map((a) => a.ano_serie))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
    const hasAno = anoVals.length > 0 || f.tem_sem_ano;
    const hasMateria = f.materia_disponivel && f.materias.length > 0;
    const anyActive = !!(
      this.filtroEtapa ||
      this.filtroAnoSerie ||
      this.filtroMateria ||
      this.filtroAluno
    );
    return html`
      <div class="filtros" data-tour="filtros">
        <span
          class="scope-l"
          title="Refine o recorte do painel por estudante, etapa, ano/série e matéria. Todos os indicadores respeitam o filtro."
          >Filtrar</span
        >
        ${this.renderAlunoFiltro()}
        ${hasEtapa
          ? html`<select
              class="scope-sel fl-sel"
              title="Filtra o painel por etapa de ensino (ex.: Ensino Fundamental, Ensino Médio)."
              @change=${this.onEtapaChange}
            >
              <option value="" ?selected=${!this.filtroEtapa}>Todas as etapas</option>
              ${f.etapas.map(
                (et) => html`<option value=${et} ?selected=${this.filtroEtapa === et}>
                  ${et}
                </option>`,
              )}
              ${f.tem_sem_etapa
                ? html`<option value="__sem__" ?selected=${this.filtroEtapa === '__sem__'}>
                    Sem etapa
                  </option>`
                : ''}
            </select>`
          : ''}
        ${hasAno
          ? html`<select
              class="scope-sel fl-sel"
              title="Filtra pelo ano ou série. As opções dependem da etapa escolhida."
              @change=${this.onAnoSerieChange}
            >
              <option value="" ?selected=${!this.filtroAnoSerie}>Todos os anos</option>
              ${anoVals.map(
                (a) => html`<option value=${a} ?selected=${this.filtroAnoSerie === a}>
                  ${a}
                </option>`,
              )}
              ${f.tem_sem_ano
                ? html`<option value="__sem__" ?selected=${this.filtroAnoSerie === '__sem__'}>
                    Sem ano
                  </option>`
                : ''}
            </select>`
          : ''}
        ${hasMateria
          ? html`<select
              class="scope-sel fl-sel"
              title="Filtra por disciplina consultada na Tutoria IA. Disponível na visão de uma escola específica."
              @change=${this.onMateriaChange}
            >
              <option value="" ?selected=${!this.filtroMateria}>Todas as matérias</option>
              ${f.materias.map(
                (m) => html`<option value=${m.id} ?selected=${this.filtroMateria === m.id}>
                  ${m.nome}
                </option>`,
              )}
            </select>`
          : ''}
        ${anyActive
          ? html`<button
              class="fl-clear"
              title="Remove todos os filtros e volta ao recorte completo do período."
              @click=${this.clearFiltros}
            >
              Limpar filtros
            </button>`
          : ''}
      </div>
      ${this.alunoSemAtividadeHint()}
    `;
  }

  /** Quando o aluno filtrado existe mas não teve atividade no período, avisa (senão
   *  os blocos zerados parecem bug). Só quando há matrícula mas 0 de atividade. */
  private alunoSemAtividadeHint(): TemplateResult | '' {
    const d = this.data;
    if (!this.filtroAluno || !d) return '';
    const semAtividade =
      d.sessoes_tutor_7d === 0 &&
      d.mensagens_alunos_7d === 0 &&
      d.bloqueios_total === 0;
    if (!semAtividade) return '';
    return html`<div class="fl-hint">
      <b>${this.filtroAlunoNome}</b> não teve atividade no período selecionado —
      troque o período (ex.: “Todo período”) para ver sessões, perguntas e bloqueios
      dele.
    </div>`;
  }

  /** Filtro de ALUNO: chip quando selecionado; senão busca por nome com dropdown. */
  private renderAlunoFiltro(): TemplateResult {
    if (this.filtroAluno) {
      return html`<span class="fl-aluno-chip" title="Aluno filtrado">
        ${this.filtroAlunoNome}
        <button class="fl-aluno-x" @click=${this.clearAluno} title="Remover">×</button>
      </span>`;
    }
    return html`<div class="fl-aluno">
      <input
        class="scope-sel fl-sel fl-aluno-in"
        type="text"
        placeholder="🔎 Aluno…"
        title="Busque um estudante pelo nome para ver o painel apenas dele (análise individual)."
        @focus=${() => {
          this.alunoBuscaAberta = true;
          if (!this.alunoBusca.length) void this.fetchAlunoBusca('');
        }}
        @input=${this.onAlunoSearch}
        @blur=${() => {
          // Deixa o clique na opção acontecer antes de fechar.
          window.setTimeout(() => (this.alunoBuscaAberta = false), 180);
        }}
      />
      ${this.alunoBuscaAberta && this.alunoBusca.length
        ? html`<div class="fl-aluno-menu">
            ${this.alunoBusca.map(
              (a) => html`<button
                class="fl-aluno-opt"
                @click=${() => this.selectAluno(a)}
              >
                <span class="fl-aluno-nome">${a.name ?? a.user_id}</span>
                ${a.etapa || a.ano_serie
                  ? html`<span class="fl-aluno-meta"
                      >${[a.etapa, a.ano_serie].filter(Boolean).join(' · ')}</span
                    >`
                  : ''}
              </button>`,
            )}
          </div>`
        : ''}
    </div>`;
  }

  private clearFiltros(): void {
    this.filtroEtapa = '';
    this.filtroAnoSerie = '';
    this.filtroMateria = '';
    this.filtroAluno = '';
    this.filtroAlunoNome = '';
    this.applyFiltroChange();
  }

  /** Sai da análise individual (v2 §4): limpa SÓ o filtro de aluno — os demais
   * filtros (etapa/ano/matéria) continuam valendo na visão institucional. */
  private voltarVisaoInstitucional(): void {
    this.filtroAluno = '';
    this.filtroAlunoNome = '';
    this.applyFiltroChange();
  }

  // ─── Perfis de coordenador — corpo da aba em Configurações ───────────────────
  private renderCoordBody(): TemplateResult {
    return html`
          <div class="rbody">
            ${this.coordError ? html`<div class="err">${this.coordError}</div>` : ''}
            ${this.coordEditing
              ? this.renderGrantEditor(this.coordEditing)
              : this.renderCoordList()}
          </div>
    `;
  }

  // ─── Coordenador por PESSOA (Fase A) ─────────────────────────────────────────
  private async loadPessoas(): Promise<void> {
    this.pessoaLoading = true;
    this.pessoaError = '';
    try {
      const [disp, assoc] = await Promise.all([
        this.accessFetch<DisponivelRow[]>('/pessoas-disponiveis'),
        this.accessFetch<PersonRow[]>('/pessoas'),
      ]);
      this.disponiveis = disp;
      this.pessoasList = assoc;
    } catch (e) {
      this.pessoaError = (e as Error).message;
    } finally {
      this.pessoaLoading = false;
    }
  }

  private async openPessoaEditor(userId: string): Promise<void> {
    this.pessoaLoading = true;
    this.pessoaError = '';
    try {
      const d = await this.accessFetch<PessoaEditorData>(
        `/pessoa-editor?user_id=${encodeURIComponent(userId)}`,
      );
      const ov = d.association?.overrides ?? {};
      const has = (k: string) => Object.prototype.hasOwnProperty.call(ov, k);
      const asList = (k: OvDim): string[] =>
        Array.isArray(ov[k]) ? (ov[k] as string[]) : []; // null → [] (todos)
      this.pessoaEditing = {
        moodle_user_id: d.moodle_user_id,
        name: d.name,
        roles: d.roles,
        assocId: d.association?.id ?? null,
        profileId: d.association?.profile_id ?? '',
        active: d.association?.active ?? true,
        adj: {
          allowed_tenant_ids: has('allowed_tenant_ids'),
          allowed_etapas: has('allowed_etapas'),
          allowed_ano_series: has('allowed_ano_series'),
          allowed_materias: has('allowed_materias'),
          can_view_reports: has('can_view_reports'),
          can_use_copilot: has('can_use_copilot'),
        },
        lists: {
          allowed_tenant_ids: asList('allowed_tenant_ids'),
          allowed_etapas: asList('allowed_etapas'),
          allowed_ano_series: asList('allowed_ano_series'),
          allowed_materias: asList('allowed_materias'),
        },
        caps: {
          can_view_reports: !!ov['can_view_reports'],
          can_use_copilot: has('can_use_copilot') ? !!ov['can_use_copilot'] : true,
        },
        profiles: d.profiles,
        options: d.options,
      };
    } catch (e) {
      this.pessoaError = (e as Error).message;
    } finally {
      this.pessoaLoading = false;
    }
  }

  private setPessoaProfile(id: string): void {
    if (this.pessoaEditing) this.pessoaEditing = { ...this.pessoaEditing, profileId: id };
  }
  private togglePessoaAdj(k: OvDim | OvCap): void {
    const ed = this.pessoaEditing;
    if (ed) this.pessoaEditing = { ...ed, adj: { ...ed.adj, [k]: !ed.adj[k] } };
  }
  private togglePessoaList(dim: OvDim, value: string): void {
    const ed = this.pessoaEditing;
    if (!ed) return;
    const arr = ed.lists[dim];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    this.pessoaEditing = { ...ed, lists: { ...ed.lists, [dim]: next } };
  }
  private clearPessoaList(dim: OvDim): void {
    const ed = this.pessoaEditing;
    if (ed) this.pessoaEditing = { ...ed, lists: { ...ed.lists, [dim]: [] } };
  }
  private setPessoaCap(cap: OvCap, value: boolean): void {
    const ed = this.pessoaEditing;
    if (ed) this.pessoaEditing = { ...ed, caps: { ...ed.caps, [cap]: value } };
  }
  private setPessoaActive(value: boolean): void {
    const ed = this.pessoaEditing;
    if (ed) this.pessoaEditing = { ...ed, active: value };
  }

  private async savePessoa(): Promise<void> {
    const ed = this.pessoaEditing;
    if (!ed) return;
    if (!ed.profileId) {
      this.pessoaError = 'Escolha um perfil base para o coordenador.';
      return;
    }
    const overrides: Record<string, unknown> = {};
    (
      [
        'allowed_tenant_ids',
        'allowed_etapas',
        'allowed_ano_series',
        'allowed_materias',
      ] as OvDim[]
    ).forEach((k) => {
      if (ed.adj[k]) overrides[k] = ed.lists[k];
    });
    (['can_view_reports', 'can_use_copilot'] as OvCap[]).forEach((k) => {
      if (ed.adj[k]) overrides[k] = ed.caps[k];
    });
    this.pessoaSaving = true;
    this.pessoaError = '';
    try {
      const body = JSON.stringify({
        moodle_user_id: ed.moodle_user_id,
        profile_id: ed.profileId,
        overrides,
        active: ed.active,
      });
      await this.accessFetch(
        ed.assocId ? `/pessoa/${encodeURIComponent(ed.assocId)}` : '/pessoa',
        { method: ed.assocId ? 'PUT' : 'POST', body },
      );
      this.pessoaEditing = undefined;
      await this.loadPessoas();
    } catch (e) {
      this.pessoaError = (e as Error).message;
    } finally {
      this.pessoaSaving = false;
    }
  }

  private async deletePessoa(): Promise<void> {
    const ed = this.pessoaEditing;
    if (!ed?.assocId) return;
    this.pessoaSaving = true;
    this.pessoaError = '';
    try {
      await this.accessFetch(`/pessoa/${encodeURIComponent(ed.assocId)}`, {
        method: 'DELETE',
      });
      this.pessoaEditing = undefined;
      await this.loadPessoas();
    } catch (e) {
      this.pessoaError = (e as Error).message;
    } finally {
      this.pessoaSaving = false;
    }
  }

  /** Remove uma associação órfã (pessoa que saiu do Moodle) direto pela lista —
   * o editor não abre pra quem não está mais no diretório. */
  private async removePessoaAssoc(assocId: string): Promise<void> {
    this.pessoaSaving = true;
    this.pessoaError = '';
    try {
      await this.accessFetch(`/pessoa/${encodeURIComponent(assocId)}`, {
        method: 'DELETE',
      });
      await this.loadPessoas();
    } catch (e) {
      this.pessoaError = (e as Error).message;
    } finally {
      this.pessoaSaving = false;
    }
  }

  private renderPessoasBody(): TemplateResult {
    return html`
          <div class="rbody">
            ${this.pessoaError
              ? html`<div class="err">${this.pessoaError}</div>`
              : ''}
            ${this.pessoaEditing
              ? this.renderPessoaEditor(this.pessoaEditing)
              : this.renderPessoasList()}
          </div>
    `;
  }

  private renderPessoasList(): TemplateResult {
    const dir = this.disponiveis;
    const assocByUser = new Map(
      (this.pessoasList ?? []).map((a) => [a.moodle_user_id, a]),
    );
    return html`
      <div class="acc-intro">
        Defina o <b>acesso no SOS</b> de cada <b>coordenador</b> — a pessoa e o papel
        vêm do <b>Moodle</b> (sincronizados automaticamente); aqui você escolhe o
        <b>perfil</b> e ajustes específicos dela. O SOS não cria papel no Moodle.
      </div>
      ${this.pessoaLoading && !dir
        ? html`<div class="aia-loading">Carregando…</div>`
        : !dir || dir.length === 0
          ? html`<div class="acc-empty">
              Nenhum coordenador sincronizado do Moodle ainda. Assim que alguém receber
              o papel de coordenação lá, ele aparece aqui (a sincronização roda
              periodicamente).
            </div>`
          : html`<table class="acc-tbl">
              <thead>
                <tr>
                  <th>Coordenador</th>
                  <th>Perfil no SOS</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                ${dir.map((p) => {
                  const a = assocByUser.get(p.moodle_user_id);
                  return html`<tr
                    class="acc-row-clk"
                    @click=${() => this.openPessoaEditor(p.moodle_user_id)}
                  >
                    <td>
                      ${p.name ?? p.moodle_user_id}
                      <span class="pp-roles">${p.roles.join(', ')}</span>
                    </td>
                    <td class="co-summary">
                      ${a
                        ? html`${a.profile_name}${a.has_overrides
                            ? html` <span class="acc-badge">ajustado</span>`
                            : ''}`
                        : html`<span class="pp-muted">— sem acesso definido —</span>`}
                    </td>
                    <td>
                      ${!a
                        ? html`<span class="acc-badge">pendente</span>`
                        : a.active
                          ? html`<span class="acc-badge own">Ativo</span>`
                          : html`<span class="acc-badge">Inativo</span>`}
                    </td>
                    <td class="acc-actions">
                      <button
                        class="glink"
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          void this.openPessoaEditor(p.moodle_user_id);
                        }}
                      >
                        ${a ? 'editar' : 'definir'}
                      </button>
                    </td>
                  </tr>`;
                })}
              </tbody>
            </table>`}
      ${this.renderPessoasOrfas()}
    `;
  }

  /** Associações de gente que NÃO está mais no diretório (perdeu o papel no Moodle).
   * O acesso já não vale (a resolução segue o diretório); aqui é só pra limpar. */
  private renderPessoasOrfas(): TemplateResult | '' {
    const dirUsers = new Set(
      (this.disponiveis ?? []).map((p) => p.moodle_user_id),
    );
    const orfas = (this.pessoasList ?? []).filter(
      (a) => !dirUsers.has(a.moodle_user_id),
    );
    if (!orfas.length) return '';
    return html`
      <div class="co-sec-t" style="margin-top:16px">Não estão mais no Moodle</div>
      <div class="co-hint">
        Perderam o papel de coordenação no Moodle — o acesso no SOS já não vale.
        Remova para limpar.
      </div>
      <table class="acc-tbl">
        <tbody>
          ${orfas.map(
            (a) => html`<tr>
              <td>
                ${a.name ?? a.moodle_user_id}
                <span class="pp-muted">(fora do Moodle)</span>
              </td>
              <td class="co-summary">${a.profile_name}</td>
              <td class="acc-actions">
                <button
                  class="glink"
                  ?disabled=${this.pessoaSaving}
                  @click=${() => this.removePessoaAssoc(a.id)}
                >
                  remover
                </button>
              </td>
            </tr>`,
          )}
        </tbody>
      </table>
    `;
  }

  private renderPessoaEditor(ed: PessoaEditing): TemplateResult {
    const o = ed.options;
    // Seção de override de LISTA: checkbox "Personalizar" + chips (só quando ligado).
    const ovSec = (
      dim: OvDim,
      title: string,
      hint: string,
      values: string[],
      label: (v: string) => string,
    ) =>
      values.length
        ? html`<div class="co-sec">
            <label class="acc-chk">
              <input
                type="checkbox"
                .checked=${ed.adj[dim]}
                @change=${() => this.togglePessoaAdj(dim)}
              />
              <b>${title}</b> — personalizar para esta pessoa
            </label>
            ${ed.adj[dim]
              ? html`<div class="co-hint">${hint}</div>
                  <div class="co-chips">
                    <button
                      class="co-chip ${ed.lists[dim].length === 0 ? 'on' : ''}"
                      @click=${() => this.clearPessoaList(dim)}
                    >
                      Todos
                    </button>
                    ${values.map(
                      (v) => html`<button
                        class="co-chip ${ed.lists[dim].includes(v) ? 'on' : ''}"
                        @click=${() => this.togglePessoaList(dim, v)}
                      >
                        ${label(v)}
                      </button>`,
                    )}
                  </div>`
              : ''}
          </div>`
        : '';
    const capSec = (cap: OvCap, title: string) => html`<label class="acc-chk">
      <input
        type="checkbox"
        .checked=${ed.adj[cap]}
        @change=${() => this.togglePessoaAdj(cap)}
      />
      Personalizar “${title}”
      ${ed.adj[cap]
        ? html`<label class="acc-chk pp-inline">
            <input
              type="checkbox"
              .checked=${ed.caps[cap]}
              @change=${(e: Event) =>
                this.setPessoaCap(cap, (e.target as HTMLInputElement).checked)}
            />
            ${title}
          </label>`
        : ''}
    </label>`;
    const anos = Array.from(
      new Set(o.anos_serie.map((a) => a.ano_serie)),
    );
    return html`
      <div class="co-head">
        <button class="glink" @click=${() => (this.pessoaEditing = undefined)}>
          ← voltar
        </button>
        <span class="co-name">${ed.name ?? ed.moodle_user_id}</span>
        ${ed.roles.length
          ? html`<span class="pp-roles">${ed.roles.join(', ')}</span>`
          : ''}
      </div>

      <div class="co-sec">
        <div class="co-sec-t">Perfil base</div>
        <div class="co-hint">
          O acesso-padrão deste coordenador. Crie/edite perfis na aba <b>Perfis</b>.
        </div>
        <select
          class="acc-in"
          @change=${(e: Event) =>
            this.setPessoaProfile((e.target as HTMLSelectElement).value)}
        >
          <option value="" ?selected=${!ed.profileId}>— escolha um perfil —</option>
          ${ed.profiles.map(
            (p) => html`<option value=${p.id} ?selected=${ed.profileId === p.id}>
              ${p.name}
            </option>`,
          )}
        </select>
      </div>

      <div class="co-sec">
        <div class="co-sec-t">Ajustes desta pessoa (opcional)</div>
        <div class="co-hint">
          Por padrão vale o perfil. Marque uma dimensão para sobrepor só para esta
          pessoa (deixar em “Todos” = sem restrição naquela dimensão).
        </div>
      </div>
      ${o.institutions.length > 1
        ? ovSec(
            'allowed_tenant_ids',
            'Instituições',
            'Escolas que este coordenador enxerga.',
            o.institutions.map((i) => i.id),
            (v) => o.institutions.find((i) => i.id === v)?.nome ?? v,
          )
        : ''}
      ${ovSec(
        'allowed_etapas',
        'Etapas',
        'Do catálogo da instituição.',
        o.etapas.map((e) => e.code),
        (v) => o.etapas.find((e) => e.code === v)?.label ?? v,
      )}
      ${ovSec(
        'allowed_ano_series',
        'Anos/série',
        'Restringe a anos/séries específicos.',
        anos,
        (v) => v,
      )}
      ${ovSec(
        'allowed_materias',
        'Matérias',
        'Restringe as métricas de conversa a estas matérias.',
        o.materias,
        (v) => v,
      )}

      <div class="co-sec">
        <div class="co-sec-t">Recursos (ajuste opcional)</div>
        ${capSec('can_view_reports', 'Pode gerar relatórios')}
        ${capSec('can_use_copilot', 'Pode usar o copiloto')}
      </div>

      <div class="co-sec">
        <label class="acc-chk">
          <input
            type="checkbox"
            .checked=${ed.active}
            @change=${(e: Event) =>
              this.setPessoaActive((e.target as HTMLInputElement).checked)}
          />
          Associação ativa
        </label>
      </div>

      <div class="acc-form-btns">
        <button
          class="gtbtn primary"
          ?disabled=${this.pessoaSaving}
          @click=${() => this.savePessoa()}
        >
          ${this.pessoaSaving ? 'Salvando…' : ed.assocId ? 'Salvar' : 'Definir acesso'}
        </button>
        ${ed.assocId
          ? html`<button
              class="gtbtn"
              ?disabled=${this.pessoaSaving}
              @click=${() => this.deletePessoa()}
            >
              Remover acesso
            </button>`
          : ''}
        <button
          class="gtbtn"
          ?disabled=${this.pessoaSaving}
          @click=${() => (this.pessoaEditing = undefined)}
        >
          Cancelar
        </button>
      </div>
    `;
  }

  /** Resumo do envelope de um perfil (chips do card) — ex.: "EF2 · Matemática · Relatórios". */
  private profileSummary(p: ProfileRow): string {
    const parts: string[] = [];
    if (p.n_institutions) parts.push(`${p.n_institutions} instituição(ões)`);
    parts.push(p.n_etapas ? `${p.n_etapas} etapa(s)` : 'todas as etapas');
    if (p.n_materias) parts.push(`${p.n_materias} matéria(s)`);
    if (p.can_view_reports) parts.push('Relatórios');
    return parts.join(' · ');
  }

  private renderCoordList(): TemplateResult {
    const list = this.coordList;
    return html`
      <div class="acc-intro">
        Crie <b>perfis de coordenador</b> (ex.: "Coordenador EF2", "Coordenador
        Geral") — cada um com o acesso que libera: instituições, séries (etapa/ano),
        matérias e recursos. Depois, na aba <b>Acesso</b>, ligue um papel do Moodle a
        um perfil; o admin do Moodle associa cada pessoa a esse papel. No Moodle, o
        papel do coordenador deve começar com <code>coordenador_</code> (ex.:
        <code>coordenador_ef2</code>) pra ter prioridade sobre "professor". Vale no
        <b>próximo login</b>.
      </div>
      ${this.coordLoading && !list
        ? html`<div class="aia-loading">Carregando…</div>`
        : html`
            ${!list || list.length === 0
              ? html`<div class="acc-empty">
                  Nenhum perfil ainda. Crie o primeiro abaixo.
                </div>`
              : html`<table class="acc-tbl">
                  <thead>
                    <tr>
                      <th>Perfil</th>
                      <th>Acesso</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    ${list.map(
                      (c) => html`<tr
                        class="acc-row-clk"
                        @click=${() => this.openCoordGrant(c.id)}
                      >
                        <td>${c.name}</td>
                        <td class="co-summary">${this.profileSummary(c)}</td>
                        <td>
                          ${c.active
                            ? html`<span class="acc-badge own">Ativo</span>`
                            : html`<span class="acc-badge">Inativo</span>`}
                        </td>
                        <td
                          class="acc-actions"
                          @click=${(e: Event) => e.stopPropagation()}
                        >
                          <button
                            class="ic-btn"
                            title="Editar perfil"
                            @click=${() => void this.openCoordGrant(c.id)}
                          >
                            ${this.iconEdit}
                          </button>
                          ${c.active
                            ? html`<button
                                class="ic-btn danger"
                                title="Excluir (inativar) o perfil"
                                @click=${() => void this.setCoordActive(c, false)}
                              >
                                ${this.iconTrash}
                              </button>`
                            : html`<button
                                class="glink co-restore-row"
                                title="Restaurar (reativar) o perfil"
                                @click=${() => void this.setCoordActive(c, true)}
                              >
                                Restaurar
                              </button>`}
                        </td>
                      </tr>`,
                    )}
                  </tbody>
                </table>`}
            <div class="co-toolbar">
              <button class="gtbtn" @click=${() => this.openCoordGrant()}>
                + Novo perfil
              </button>
              <button
                class="gtbtn ghost co-restore-orig"
                title="Recria o perfil-base 'Coordenador' com acesso total (todas as instituições, etapas, matérias e recursos)"
                @click=${() => void this.restoreOriginalCoord()}
              >
                ↩︎ Voltar configuração original
              </button>
            </div>
          `}
    `;
  }

  /** Chips de ano/série agrupados por etapa (ano/série é texto livre sem catálogo). */
  private renderAnoGroups(o: GrantOptions, selected: string[]): TemplateResult {
    const byEtapa = new Map<string, string[]>();
    for (const p of o.anos_serie) {
      const key = p.etapa ?? '__none__';
      const arr = byEtapa.get(key) ?? [];
      if (!arr.includes(p.ano_serie)) arr.push(p.ano_serie);
      byEtapa.set(key, arr);
    }
    const etapaLabel = (code: string) =>
      code === '__none__'
        ? 'Sem etapa'
        : (o.etapas.find((e) => e.code === code)?.label ?? code);
    return html`${[...byEtapa.entries()].map(
      ([etapa, anos]) => html`<div class="co-anogrp">
        <span class="co-anogrp-t">${etapaLabel(etapa)}</span>
        <div class="co-chips">
          ${[...anos]
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map(
              (a) => html`<button
                class="co-chip ${selected.includes(a) ? 'on' : ''}"
                @click=${() => this.toggleGrantList('allowed_ano_series', a)}
              >
                ${a}
              </button>`,
            )}
        </div>
      </div>`,
    )}`;
  }

  private renderGrantEditor(d: CoordinatorDetail): TemplateResult {
    const g = d.profile;
    const o = d.options;
    const tabsRestricted = g.allowed_tabs !== null;
    // Cada dimensão tem um chip explícito "Todos/Todas" (aceso quando nada está
    // selecionado = sem restrição); clicar nele limpa a seleção. O usuário nunca
    // precisa saber que "vazio = todos".
    const chipSec = (
      title: string,
      hint: string,
      allLabel: string,
      allOn: boolean,
      onAll: () => void,
      values: string[],
      selected: (v: string) => boolean,
      label: (v: string) => string,
      onToggle: (v: string) => void,
    ) =>
      values.length
        ? html`<div class="co-sec">
            <div class="co-sec-t">${title}</div>
            ${hint ? html`<div class="co-hint">${hint}</div>` : ''}
            <div class="co-chips">
              <button class="co-chip ${allOn ? 'on' : ''}" @click=${onAll}>
                ${allLabel}
              </button>
              ${values.map(
                (v) => html`<button
                  class="co-chip ${selected(v) ? 'on' : ''}"
                  @click=${() => onToggle(v)}
                >
                  ${label(v)}
                </button>`,
              )}
            </div>
          </div>`
        : '';
    return html`
      <div class="co-head">
        <button class="glink" @click=${() => this.backToCoordList()}>← voltar</button>
        <span class="co-name">${g.id ? g.name : 'Novo perfil'}</span>
      </div>
      <div class="co-sec">
        <div class="co-sec-t">Nome do perfil</div>
        <div class="co-hint">
          Como aparece na lista e no de-para (ex.: "Coordenador EF2").
        </div>
        <input
          class="acc-in co-name-in"
          .value=${g.name}
          placeholder="ex.: Coordenador EF2"
          @input=${(e: Event) =>
            this.setGrantName((e.target as HTMLInputElement).value)}
        />
      </div>
      ${o.institutions.length > 1
        ? chipSec(
            'Instituições',
            '',
            'Padrão (própria escola)',
            g.allowed_tenant_ids.length === 0,
            () => this.clearGrantList('allowed_tenant_ids'),
            o.institutions.map((i) => i.id),
            (v) => g.allowed_tenant_ids.includes(v),
            (v) => o.institutions.find((i) => i.id === v)?.nome ?? v,
            (v) => this.toggleGrantList('allowed_tenant_ids', v),
          )
        : ''}
      ${chipSec(
        'Etapas',
        'Do catálogo da instituição (tela Etapas).',
        'Todas',
        g.allowed_etapas.length === 0,
        () => this.clearGrantList('allowed_etapas'),
        o.etapas.map((e) => e.code),
        (v) => g.allowed_etapas.includes(v),
        (v) => o.etapas.find((e) => e.code === v)?.label ?? v,
        (v) => this.toggleGrantList('allowed_etapas', v),
      )}
      ${o.anos_serie.length
        ? html`<div class="co-sec">
            <div class="co-sec-t">Ano / série</div>
            <div class="co-hint">Agrupado por etapa.</div>
            <div class="co-chips">
              <button
                class="co-chip ${g.allowed_ano_series.length === 0 ? 'on' : ''}"
                @click=${() => this.clearGrantList('allowed_ano_series')}
              >
                Todos
              </button>
            </div>
            ${this.renderAnoGroups(o, g.allowed_ano_series)}
          </div>`
        : ''}
      ${chipSec(
        'Matérias',
        'Restringe as métricas de conversa (assunto das perguntas).',
        'Todas',
        g.allowed_materias.length === 0,
        () => this.clearGrantList('allowed_materias'),
        o.materias,
        (v) => g.allowed_materias.includes(v),
        (v) => v,
        (v) => this.toggleGrantList('allowed_materias', v),
      )}
      <div class="co-sec">
        <div class="co-sec-t">Recursos</div>
        <label class="acc-chk">
          <input
            type="checkbox"
            .checked=${g.can_view_reports}
            @change=${(e: Event) =>
              this.setGrantFlag('can_view_reports', (e.target as HTMLInputElement).checked)}
          />
          Pode gerar relatórios
        </label>
        <label class="acc-chk">
          <input
            type="checkbox"
            .checked=${g.can_use_copilot}
            @change=${(e: Event) =>
              this.setGrantFlag('can_use_copilot', (e.target as HTMLInputElement).checked)}
          />
          Pode usar o copiloto
        </label>
      </div>
      ${o.tabs.length
        ? html`<div class="co-sec">
            <div class="co-sec-t">Abas de IA</div>
            <div class="co-chips">
              <button
                class="co-chip ${!tabsRestricted ? 'on' : ''}"
                @click=${() => {
                  const ed2 = this.coordEditing;
                  if (ed2)
                    this.coordEditing = {
                      ...ed2,
                      profile: { ...ed2.profile, allowed_tabs: null },
                    };
                }}
              >
                Todas
              </button>
              ${o.tabs.map(
                (t) => html`<button
                  class="co-chip ${tabsRestricted &&
                  (g.allowed_tabs ?? []).includes(t.id)
                    ? 'on'
                    : ''}"
                  @click=${() => this.toggleGrantTab(t.id)}
                >
                  ${t.nome}
                </button>`,
              )}
            </div>
          </div>`
        : ''}
      <div class="co-sec">
        <label class="acc-chk">
          <input
            type="checkbox"
            .checked=${g.active}
            @change=${(e: Event) =>
              this.setGrantFlag('active', (e.target as HTMLInputElement).checked)}
          />
          Perfil ativo
        </label>
        <div class="co-hint">
          Desativar faz os papéis ligados a este perfil caírem no acesso
          <b>padrão</b> (própria escola, copiloto ligado, sem relatório) — não é um
          bloqueio de recursos. Para tirar um recurso, desmarque-o acima com o perfil
          ativo.
        </div>
      </div>
      <div class="acc-form-btns">
        <button
          class="gtbtn primary"
          ?disabled=${this.coordSaving}
          @click=${() => this.saveGrant()}
        >
          ${this.coordSaving ? 'Salvando…' : g.id ? 'Salvar' : 'Criar perfil'}
        </button>
        ${g.id
          ? html`<button
              class="gtbtn"
              ?disabled=${this.coordSaving}
              @click=${() => this.deleteGrant()}
            >
              Excluir perfil
            </button>`
          : ''}
        <button
          class="gtbtn"
          ?disabled=${this.coordSaving}
          @click=${() => this.backToCoordList()}
        >
          Cancelar
        </button>
      </div>
    `;
  }

  // ─── Acesso (de-para papel→divisão) — corpo da aba em Configurações ──────────
  private renderAcessoBody(): TemplateResult {
    const data = this.acesso;
    const ed = this.acessoEditing;
    const canEdit = data?.can_edit ?? this.isNetwork;
    return html`
          <div class="rbody">
            <div class="acc-intro">
              Defina qual <b>divisão de acesso</b> cada papel do Moodle recebe na
              sua instituição. O padrão é herdado da Otzma; personalize se o seu
              Moodle usa outros nomes de papel. As mudanças valem no
              <b>próximo login</b> (não afetam sessões já abertas).
            </div>
            ${this.acessoError ? html`<div class="err">${this.acessoError}</div>` : ''}
            ${this.acessoLoading && !data
              ? html`<div class="aia-loading">Carregando…</div>`
              : data
                ? html`
                    <table class="acc-tbl">
                      <thead>
                        <tr>
                          <th>Papel (Moodle)</th>
                          <th>Divisão</th>
                          <th>Escopo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        ${data.role_map.length === 0
                          ? html`<tr>
                              <td colspan="4" class="acc-empty">
                                Nenhum papel mapeado ainda.
                              </td>
                            </tr>`
                          : data.role_map.map((r) => this.renderAccRow(r, canEdit))}
                      </tbody>
                    </table>
                    ${canEdit && !ed
                      ? html`<button class="gtbtn" @click=${() => this.startNovoPapel()}>
                          + Novo papel
                        </button>`
                      : ''}
                    ${ed ? this.renderAccForm(ed, data) : ''}
                    ${this.renderAccLegend(data)}
                  `
                : ''}
          </div>
    `;
  }

  /** Legenda: o que cada divisão de acesso permite. Monta a partir das divisões
   *  vindas do backend (usa o `label` editável de cada uma) e descreve pela `key`
   *  estável. Divisão sem descrição conhecida é omitida da legenda. */
  private renderAccLegend(data: AccessOverview): TemplateResult {
    const items = data.divisions.filter((d) => DIVISION_HELP[d.key]);
    if (items.length === 0) return html``;
    return html`
      <div class="acc-legend">
        <div class="acc-legend-t">O que cada divisão permite</div>
        ${items.map(
          (d) => html`<div class="acc-legend-item">
            <span class="acc-legend-name">${d.label}</span>
            <span class="acc-legend-desc">${DIVISION_HELP[d.key]}</span>
          </div>`,
        )}
      </div>
    `;
  }

  private renderAccRow(r: AccessRoleMapRow, canEdit: boolean): TemplateResult {
    const badge =
      r.scope === 'personalizado'
        ? html`<span class="acc-badge own">Personalizado</span>`
        : html`<span class="acc-badge">Herdado</span>`;
    return html`
      <tr class=${r.active ? '' : 'acc-off'}>
        <td class="acc-role">${r.moodle_role}</td>
        <td>
          ${r.profile_name
            ? html`<span class="acc-prof">${r.profile_name}</span>
                <span class="acc-inactive"> · perfil</span>`
            : r.division_label}${r.active
            ? ''
            : html` <span class="acc-inactive">(inativo)</span>`}
        </td>
        <td>${badge}</td>
        <td class="acc-actions">
          ${canEdit
            ? r.scope === 'personalizado'
              ? html`
                  <button class="glink" @click=${() => this.startEditPapel(r)}>
                    editar
                  </button>
                  <button class="glink del" @click=${() => this.deleteRoleMap(r)}>
                    remover
                  </button>
                `
              : html`<button class="glink" @click=${() => this.startEditPapel(r)}>
                  personalizar
                </button>`
            : ''}
        </td>
      </tr>
    `;
  }

  private renderAccForm(ed: AccessEditing, data: AccessOverview): TemplateResult {
    const editing = !!ed.id;
    return html`
      <div class="acc-form">
        <div class="acc-form-t">
          ${editing ? 'Editar mapeamento' : 'Novo mapeamento (personalizado)'}
        </div>
        <div class="acc-form-row">
          <label class="acc-fld">
            <span>Papel (shortname do Moodle)</span>
            <input
              class="acc-in"
              .value=${ed.moodle_role}
              placeholder="ex.: coordinator"
              @input=${(e: Event) =>
                (this.acessoEditing = {
                  ...ed,
                  moodle_role: (e.target as HTMLInputElement).value,
                })}
            />
          </label>
          <label class="acc-fld">
            <span>Divisão de acesso</span>
            <select
              class="acc-in"
              @change=${(e: Event) =>
                this.setAccTarget((e.target as HTMLSelectElement).value)}
            >
              ${data.divisions.map(
                (d) => html`<option
                  value="d:${d.id}"
                  ?selected=${!ed.profile_id && d.id === ed.division_id}
                >
                  ${d.label}
                </option>`,
              )}
              ${data.profiles.length
                ? html`<optgroup label="Perfis de coordenador">
                    ${data.profiles.map(
                      (p) => html`<option
                        value="p:${p.id}"
                        ?selected=${ed.profile_id === p.id}
                      >
                        ${p.name}
                      </option>`,
                    )}
                  </optgroup>`
                : ''}
            </select>
          </label>
          <label class="acc-chk">
            <input
              type="checkbox"
              .checked=${ed.active}
              @change=${(e: Event) =>
                (this.acessoEditing = {
                  ...ed,
                  active: (e.target as HTMLInputElement).checked,
                })}
            />
            Ativo
          </label>
        </div>
        <div class="acc-form-btns">
          <button
            class="gtbtn primary"
            ?disabled=${this.acessoSaving}
            @click=${() => this.saveRoleMap()}
          >
            ${this.acessoSaving ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            class="gtbtn"
            ?disabled=${this.acessoSaving}
            @click=${() => this.cancelEditPapel()}
          >
            Cancelar
          </button>
        </div>
      </div>
    `;
  }

  // ─── Conteúdo do Conect: markdown + gráficos (```chart) intercalados ─────────
  private renderGestorContent(content: string): TemplateResult[] {
    const out: TemplateResult[] = [];
    const re = /```chart\s*([\s\S]*?)```/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      const text = content.slice(last, m.index);
      if (text.trim()) out.push(html`${unsafeHTML(mdToHtml(text))}`);
      const chart = this.renderChartSpec(m[1]);
      if (chart) out.push(chart);
      last = re.lastIndex;
    }
    // Esconde um bloco ```chart AINDA aberto (streaming) — os completos já foram
    // consumidos acima, então isso só remove o JSON cru enquanto o gráfico não fecha.
    const tail = content.slice(last).replace(/```chart[\s\S]*$/, '');
    if (tail.trim() || out.length === 0) out.push(html`${unsafeHTML(mdToHtml(tail))}`);
    return out;
  }

  /** Renderiza um gráfico do snapshot (dados REAIS de this.data; a IA só escolhe
   * a série e o tipo). Inválido/vazio → null (não desenha). */
  private renderChartSpec(raw: string): TemplateResult | null {
    let spec: { data?: string; type?: string; title?: string };
    try {
      spec = JSON.parse(raw.trim());
    } catch {
      return null;
    }
    const series = this.chartSeries(spec?.data);
    if (!series || !series.length) return null;
    const title = typeof spec.title === 'string' ? spec.title : '';
    const nonZero = series.filter((s) => s.value > 0);
    const t = spec.type;
    // Composição circular: 1 categoria = 100% (círculo cheio) — útil ao filtrar por
    // uma disciplina. Só cai pra barra se NENHUM valor (nada pra proporcionar).
    if ((t === 'pie' || t === 'donut') && nonZero.length >= 1) {
      return this.chartPie(title, series, t === 'donut');
    }
    if (t === 'stacked' && nonZero.length >= 1) return this.chartStacked(title, series);
    // Radar precisa de ≥3 eixos p/ formar polígono; com menos, cai p/ colunas (nunca
    // pra barra horizontal silenciosa, que confundia com o tipo 'bar' pedido).
    if (t === 'radar') {
      return series.length >= 3
        ? this.chartRadar(title, series)
        : this.chartColumns(title, series);
    }
    if (t === 'gauge') return this.chartGauge(title, series);
    if (t === 'column') return this.chartColumns(title, series);
    if (t === 'area') return this.chartLine(title, series, true);
    if (t === 'line') return this.chartLine(title, series);
    return this.chartBar(title, series);
  }

  /** Exemplo didático de um tipo de gráfico (dados fictícios) — usado no editor
   * Personalizar → Gráficos pra mostrar como cada tipo marcado aparece no Conect. */
  private chartExample(t: string): TemplateResult | null {
    const s = [
      { label: 'Matemática', value: 42 },
      { label: 'Português', value: 28 },
      { label: 'Ciências', value: 18 },
      { label: 'História', value: 12 },
    ];
    const title = CHART_TYPE_LABELS[t] ?? t;
    switch (t) {
      case 'pie':
        return this.chartPie(title, s);
      case 'donut':
        return this.chartPie(title, s, true);
      case 'stacked':
        return this.chartStacked(title, s);
      case 'radar':
        return this.chartRadar(title, s);
      case 'gauge':
        // Medidor lê valor ÷ total: 42 de 100 = 42%.
        return this.chartGauge(title, [
          { label: 'Ativos no Tutor IA', value: 42 },
          { label: 'Demais alunos', value: 58 },
        ]);
      case 'column':
        return this.chartColumns(title, s);
      case 'area':
        return this.chartLine(title, s, true);
      case 'line':
        return this.chartLine(title, s);
      case 'bar':
        return this.chartBar(title, s);
      default:
        return null;
    }
  }

  private chartLine(
    title: string,
    series: { label: string; value: number }[],
    area = false,
  ): TemplateResult {
    const max = Math.max(1, ...series.map((s) => s.value));
    const W = 300;
    const H = 90;
    const n = series.length;
    // 1 ponto NÃO forma linha: em vez do "ponto solto no topo", desenha o nível como
    // linha tracejada (uma amostra) + o ponto rotulado no meio da altura.
    if (n === 1) {
      const s0 = series[0];
      const yc = H * 0.45;
      return html`<div class="gchart">
        ${title ? html`<div class="gchart-t">${title}</div>` : ''}
        <svg viewBox="0 0 ${W} ${H}" width="100%" height="100" preserveAspectRatio="none">
          <line x1="8" y1="${yc}" x2="${W - 8}" y2="${yc}"
            stroke="var(--sos-accent, #378add)" stroke-width="2" stroke-dasharray="5 4"></line>
          <circle cx="${W / 2}" cy="${yc}" r="4" fill="var(--sos-accent, #378add)"></circle>
          <text x="${W / 2}" y="${yc - 8}" text-anchor="middle" font-size="11" fill="var(--muted, #888)">${s0.value}</text>
        </svg>
        <div class="gchart-xaxis">
          <span title=${s0.label}>${s0.label}: ${s0.value}</span>
        </div>
      </div>`;
    }
    const x = (i: number) => (n <= 1 ? W / 2 : (i / (n - 1)) * (W - 16) + 8);
    const y = (v: number) => H - 12 - (v / max) * (H - 24);
    const pts = series.map((s, i) => `${x(i)},${y(s.value)}`).join(' ');
    // Área = a mesma linha, fechada até a base e preenchida.
    const areaPts = `${x(0)},${H - 12} ${pts} ${x(n - 1)},${H - 12}`;
    const dots = series.map(
      (s, i) => svg`<circle cx="${x(i)}" cy="${y(s.value)}" r="3" fill="var(--sos-accent, #378add)"></circle>`,
    );
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <svg viewBox="0 0 ${W} ${H}" width="100%" height="100" preserveAspectRatio="none">
        ${area
          ? svg`<polygon points=${areaPts} fill="var(--sos-accent, #378add)" fill-opacity="0.18" stroke="none"></polygon>`
          : ''}
        <polyline
          points=${pts}
          fill="none"
          stroke="var(--sos-accent, #378add)"
          stroke-width="2"
        ></polyline>
        ${dots}
      </svg>
      <div class="gchart-xaxis">
        ${series.map((s) => html`<span title=${s.label}>${s.label}: ${s.value}</span>`)}
      </div>
    </div>`;
  }

  private chartSeries(key?: string): { label: string; value: number }[] | null {
    const d = this.data;
    if (!d || !key) return null;
    switch (key) {
      case 'por_materia':
        return d.por_materia.map((m) => ({ label: m.materia, value: m.total }));
      case 'por_etapa':
        return d.por_etapa.map((e) => ({ label: e.etapa ?? 'sem etapa', value: e.total }));
      case 'uso_por_etapa':
        return (d.perguntas_por_etapa ?? []).map((e) => ({
          label: e.etapa ?? 'sem etapa',
          value: e.total,
        }));
      case 'seguranca':
        return d.por_categoria_bloqueio.map((c) => ({ label: c.rotulo, value: c.total }));
      case 'matricula':
        return [
          { label: 'Matriculados', value: d.matriculados },
          { label: 'Desmatriculados', value: d.desmatriculados },
          { label: 'Pendentes de carga', value: d.pendentes_carga },
        ];
      case 'engajamento':
        return [
          { label: 'Ativos', value: d.ativos_7d },
          { label: 'Inativos', value: d.inativos_7d },
        ];
      case 'classificacao':
        return [
          { label: 'Classificadas', value: d.perguntas_classificadas },
          { label: 'Sem matéria', value: d.perguntas_sem_materia },
          { label: 'Fora de conteúdo', value: d.perguntas_fora_conteudo },
          { label: 'Aguardando', value: d.perguntas_pendentes },
        ];
      case 'conversa':
        return [
          { label: 'Sessões', value: d.sessoes_tutor_7d },
          { label: 'Perguntas', value: d.mensagens_alunos_7d },
        ];
      case 'kpis':
        return [
          { label: 'Matriculados', value: d.matriculados },
          { label: 'Ativos', value: d.ativos_7d },
          { label: 'Sessões', value: d.sessoes_tutor_7d },
          { label: 'Perguntas', value: d.mensagens_alunos_7d },
          { label: 'Barrados', value: d.bloqueios_total },
        ];
      default:
        return null;
    }
  }

  private chartBar(
    title: string,
    series: { label: string; value: number }[],
  ): TemplateResult {
    const max = Math.max(1, ...series.map((s) => s.value));
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      ${series.map(
        (s) => html`<div class="gchart-row">
          <span class="gchart-l" title=${s.label}>${s.label}</span>
          <span class="gchart-track"
            ><span class="gchart-bar" style="width:${Math.round((s.value / max) * 100)}%"></span
          ></span>
          <span class="gchart-v">${s.value}</span>
        </div>`,
      )}
    </div>`;
  }

  private chartLegend(
    series: { label: string; value: number }[],
    pct = false,
  ): TemplateResult {
    const total = series.reduce((a, s) => a + s.value, 0);
    return html`<div class="gchart-leg">
      ${series.map(
        (s, i) => html`<div class="gchart-li">
          <span class="gchart-dot" style="background:${CHART_PALETTE[i % CHART_PALETTE.length]}"></span>
          <span class="gchart-l" title=${s.label}>${s.label}</span>
          <span class="gchart-v">${s.value}</span>
          ${pct && total > 0
            ? html`<span class="gchart-pct">${Math.round((s.value / total) * 100)}%</span>`
            : ''}
        </div>`,
      )}
    </div>`;
  }

  // pizza (donut=false) = setores cheios; rosca (donut=true) = anel.
  private chartPie(
    title: string,
    series: { label: string; value: number }[],
    donut = true,
  ): TemplateResult {
    const total = series.reduce((a, s) => a + s.value, 0) || 1;
    const cx = 60;
    const cy = 60;
    let shapes: unknown[];
    if (donut) {
      const R = 42;
      const C = 2 * Math.PI * R;
      let acc = 0;
      shapes = series.map((s, i) => {
        const dash = (s.value / total) * C;
        const c = svg`<circle
          r="${R}" cx="${cx}" cy="${cy}" fill="none"
          stroke="${CHART_PALETTE[i % CHART_PALETTE.length]}" stroke-width="16"
          stroke-dasharray="${dash} ${C - dash}" stroke-dashoffset="${-acc}"
          transform="rotate(-90 ${cx} ${cy})"
        ></circle>`;
        acc += dash;
        return c;
      });
    } else {
      const R = 52;
      let a0 = -Math.PI / 2;
      const p = (a: number) =>
        `${(cx + R * Math.cos(a)).toFixed(2)} ${(cy + R * Math.sin(a)).toFixed(2)}`;
      shapes = series.map((s, i) => {
        const frac = s.value / total;
        const color = CHART_PALETTE[i % CHART_PALETTE.length];
        if (frac <= 0) return ''; // fatia zerada não desenha (e não consome ângulo)
        // Fatia de 100%: um arco de 360° é degenerado (início = fim) → não pinta.
        // Desenha o círculo cheio no lugar (ex.: 1 aluno, tudo numa categoria).
        if (frac >= 0.9999) {
          return svg`<circle cx="${cx}" cy="${cy}" r="${R}" fill="${color}"></circle>`;
        }
        const a1 = a0 + frac * 2 * Math.PI;
        const large = a1 - a0 > Math.PI ? 1 : 0;
        const d = `M${cx} ${cy} L${p(a0)} A${R} ${R} 0 ${large} 1 ${p(a1)} Z`;
        a0 = a1;
        return svg`<path d="${d}" fill="${color}"></path>`;
      });
    }
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <div class="gchart-pie">
        <svg viewBox="0 0 120 120" width="116" height="116">${shapes}</svg>
        ${this.chartLegend(series, true)}
      </div>
    </div>`;
  }

  // Colunas (barras verticais).
  private chartColumns(
    title: string,
    series: { label: string; value: number }[],
  ): TemplateResult {
    const max = Math.max(1, ...series.map((s) => s.value));
    const W = 300;
    const H = 120;
    const base = H - 20;
    const n = series.length;
    const gap = 8;
    const bw = Math.max(6, (W - gap * (n + 1)) / n);
    const bars = series.map((s, i) => {
      // reserva ~18px no topo pro rótulo do valor não ser cortado no viewBox.
      const h = (s.value / max) * (base - 18);
      const bx = gap + i * (bw + gap);
      const by = base - h;
      const ly = Math.max(9, by - 4);
      return svg`<g>
        <rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="3" fill="var(--sos-accent, #378add)"></rect>
        <text x="${(bx + bw / 2).toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="middle" font-size="9" fill="var(--muted, #888)">${s.value}</text>
      </g>`;
    });
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <svg viewBox="0 0 ${W} ${H}" width="100%" height="130">
        <line x1="0" y1="${base}" x2="${W}" y2="${base}" stroke="var(--border, #ddd)" stroke-width="1"></line>
        ${bars}
      </svg>
      <div class="gchart-xaxis">
        ${series.map((s) => html`<span title=${s.label}>${s.label}</span>`)}
      </div>
    </div>`;
  }

  // Barra empilhada (composição num só traço).
  private chartStacked(
    title: string,
    series: { label: string; value: number }[],
  ): TemplateResult {
    const total = series.reduce((a, s) => a + s.value, 0) || 1;
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <div class="gchart-stack">
        ${series.map((s, i) => {
          const pct = (s.value / total) * 100;
          return pct > 0
            ? html`<span
                class="gchart-seg"
                title="${s.label}: ${s.value}"
                style="width:${pct}%;background:${CHART_PALETTE[i % CHART_PALETTE.length]}"
              ></span>`
            : '';
        })}
      </div>
      ${this.chartLegend(series)}
    </div>`;
  }

  // Medidor (gauge): 1º valor como % do total (ex.: ativos vs total).
  private chartGauge(
    title: string,
    series: { label: string; value: number }[],
  ): TemplateResult {
    const total = series.reduce((a, s) => a + s.value, 0) || 1;
    const val = series[0]?.value ?? 0;
    const pct = Math.round((val / total) * 100);
    const R = 46;
    const cx = 60;
    const cy = 56;
    const at = (f: number) => {
      const a = Math.PI - f * Math.PI;
      return `${(cx + R * Math.cos(a)).toFixed(2)} ${(cy - R * Math.sin(a)).toFixed(2)}`;
    };
    const bg = `M${cx - R} ${cy} A${R} ${R} 0 0 1 ${cx + R} ${cy}`;
    const fill = `M${cx - R} ${cy} A${R} ${R} 0 0 1 ${at(pct / 100)}`;
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <div class="gchart-pie">
        <svg viewBox="0 0 120 72" width="150" height="90">
          <path d="${bg}" fill="none" stroke="var(--track-bg, #eee)" stroke-width="12" stroke-linecap="round"></path>
          ${pct > 0
            ? svg`<path d="${fill}" fill="none" stroke="var(--sos-accent, #378add)" stroke-width="12" stroke-linecap="round"></path>`
            : ''}
          <text x="${cx}" y="${cy - 4}" text-anchor="middle" font-size="18" font-weight="700" fill="var(--sos-text, #222)">${pct}%</text>
        </svg>
        <div class="gchart-leg">
          <div class="gchart-li">
            <span class="gchart-dot" style="background:var(--sos-accent, #378add)"></span>
            <span class="gchart-l" title=${series[0]?.label ?? ''}>${series[0]?.label ?? ''}</span>
            <span class="gchart-v">${val}</span>
          </div>
          <div class="gchart-li" style="color:var(--muted)">
            <span class="gchart-dot" style="background:var(--track-bg, #eee)"></span>
            <span class="gchart-l">Total</span>
            <span class="gchart-v">${total}</span>
          </div>
        </div>
      </div>
    </div>`;
  }

  // Radar (compara N dimensões de uma vez; precisa de 3+).
  private chartRadar(
    title: string,
    series: { label: string; value: number }[],
  ): TemplateResult {
    const max = Math.max(1, ...series.map((s) => s.value));
    const n = series.length;
    const cx = 70;
    const cy = 70;
    const R = 50;
    const pt = (i: number, r: number) => {
      const a = -Math.PI / 2 + (i / n) * 2 * Math.PI;
      return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
    };
    const rings = [0.25, 0.5, 0.75, 1].map(
      (f) =>
        svg`<polygon points="${Array.from({ length: n }, (_, i) => pt(i, R * f)).join(' ')}" fill="none" stroke="var(--border, #ddd)" stroke-width="0.5"></polygon>`,
    );
    const spokes = Array.from(
      { length: n },
      (_, i) =>
        svg`<line x1="${cx}" y1="${cy}" x2="${pt(i, R).split(',')[0]}" y2="${pt(i, R).split(',')[1]}" stroke="var(--border, #ddd)" stroke-width="0.5"></line>`,
    );
    const poly = series.map((s, i) => pt(i, (s.value / max) * R)).join(' ');
    return html`<div class="gchart">
      ${title ? html`<div class="gchart-t">${title}</div>` : ''}
      <div class="gchart-pie">
        <svg viewBox="0 0 140 140" width="150" height="150">
          ${rings}${spokes}
          <polygon points="${poly}" fill="var(--sos-accent, #378add)" fill-opacity="0.25" stroke="var(--sos-accent, #378add)" stroke-width="1.5"></polygon>
        </svg>
        ${this.chartLegend(series)}
      </div>
    </div>`;
  }

  private renderGestor(): TemplateResult {
    const vazio = this.gestorMsgs.length === 0 && !this.gestorStreaming;
    return html`
      <div class="panel gpanel" @click=${(e: Event) => {
        if (e.target === e.currentTarget) this.showGestor = false;
      }}>
        <div class="gcard">
        <div class="panel-head">
          <span
            ><span class="panel-title"
              >Analisar com o ${this.data?.tutor_nome || 'analista'}</span
            ></span
          >
          <button class="xbtn" @click=${() => (this.showGestor = false)}>
            ${this.iconClose}
          </button>
        </div>
        <div class="gtoolbar">
          <button class="gtbtn" @click=${() => this.novaGestorConversa()}>+ Nova conversa</button>
          <button
            class="gtbtn"
            @click=${() => {
              this.gestorShowList = !this.gestorShowList;
              if (this.gestorShowList) void this.loadGestorSessions();
            }}
          >
            Histórico${this.gestorSessions.length ? ` (${this.gestorSessions.length})` : ''} ▾
          </button>
          ${this.gestorMsgs.length
            ? html`<button
                class="gtbtn"
                title="Baixar esta conversa (.md)"
                ?disabled=${this.gestorStreaming}
                @click=${() => this.exportGestorConversa()}
              >
                ⬇ Exportar
              </button>`
            : ''}
        </div>
        ${this.gestorShowList ? this.renderGestorList() : ''}
        <div class="gchat">
          <div class="gmsgs">
            ${vazio
              ? html`
                  <div class="gintro">
                    ${this.data?.copiloto_intro ||
                    'Assistente Inteligente de Gestão Educacional — interpreta os indicadores da sua instituição para apoiar a análise pedagógica e a tomada de decisão.'}
                  </div>
                  <div class="gsug">
                    ${SUGESTOES.map(
                      (s) => html`<button class="gchip" @click=${() => this.askGestor(s)}>
                        ${s}
                      </button>`,
                    )}
                  </div>
                  <div class="gsug-h">Peça um tipo de gráfico:</div>
                  <div class="gsug">
                    ${SUGESTOES_GRAFICOS.map(
                      (s) => html`<button
                        class="gchip gchip-chart"
                        @click=${() => this.askGestor(s)}
                      >
                        ${s}
                      </button>`,
                    )}
                  </div>
                `
              : ''}
            ${this.gestorMsgs.map((m) =>
              m.role === 'assistant'
                ? html`<div class="gbubble assistant gmd">
                    ${this.renderGestorContent(m.content)}
                  </div>`
                : html`<div class="gbubble user">${m.content}</div>`,
            )}
            ${this.gestorStreaming
              ? this.gestorStreamingText
                ? html`<div class="gbubble assistant gmd">${this.renderGestorContent(this.gestorStreamingText)}</div>`
                : html`<div class="gbubble assistant gtyping"><span></span><span></span><span></span></div>`
              : ''}
            ${this.gestorError
              ? html`<div class="gerr">
                  <span>${this.gestorError}</span>
                  ${this.gestorLastQ
                    ? html`<button class="gretry" @click=${() => this.retryGestor()}>
                        Tentar de novo
                      </button>`
                    : ''}
                </div>`
              : ''}
          </div>
          <form
            class="ginput"
            @submit=${(e: Event) => {
              e.preventDefault();
              void this.sendGestor();
            }}
          >
            <input
              type="text"
              placeholder=${`Analisar com o ${this.data?.tutor_nome || 'analista'}…`}
              .value=${this.gestorInput}
              @input=${this.onGestorInput}
              ?disabled=${this.gestorStreaming}
            />
            <button
              type="submit"
              ?disabled=${this.gestorStreaming || !this.gestorInput.trim()}
            >
              Enviar
            </button>
          </form>
          ${this.gestorUso
            ? html`<div class="note">
                Você já fez ${this.gestorUso.consultas_total} consulta(s) à IA de análise.
              </div>`
            : ''}
        </div>
        </div>
      </div>
    `;
  }

  private renderInfo(): TemplateResult {
    const e = this.escopo;
    return html`
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">Como o Tutor IA funciona</span>
          <button class="xbtn" @click=${() => (this.showInfo = false)}>
            ${this.iconClose}
          </button>
        </div>
        <div class="panel-body">
          <p class="erow-m" style="font-size:13px;margin:0 0 14px">
            O Tutor IA é um <b>tutor de estudos</b>: ele ajuda o aluno a entender a
            matéria com explicações no nível da etapa dele, faz perguntas e dá
            caminhos — <b>nunca entrega a resposta pronta</b> de prova/trabalho.
            Mantém o foco em conteúdo escolar e tem <b>barreiras de segurança</b>
            para temas impróprios.
          </p>

          ${this.escopoLoading
            ? html`<div class="loading">Carregando o escopo…</div>`
            : this.escopoError
              ? html`<div class="err">${this.escopoError}</div>`
              : e
                ? html`
                    <div class="esec-h">
                      <span class="dot" style="background:var(--red)"></span>
                      Bloqueado (o tutor recusa)
                    </div>
                    ${e.blacklist.length
                      ? e.blacklist.map(
                          (r) => html`
                            <div class="erow">
                              <div class="erow-t">${r.rotulo}</div>
                              ${r.mensagem
                                ? html`<div class="erow-m">"${r.mensagem}"</div>`
                                : ''}
                            </div>
                          `,
                        )
                      : html`<div class="empty">Sem regras de bloqueio.</div>`}

                    <div class="esec-h">
                      <span class="dot" style="background:var(--amber)"></span>
                      Tratado com cuidado (redireciona)
                    </div>
                    ${e.graylist.length
                      ? e.graylist.map(
                          (r) => html`
                            <div class="erow">
                              <div class="erow-t">${r.rotulo}</div>
                              ${r.mensagem
                                ? html`<div class="erow-m">"${r.mensagem}"</div>`
                                : ''}
                            </div>
                          `,
                        )
                      : html`<div class="empty">Sem regras de cuidado.</div>`}

                    <div class="note">
                      ${e.regras_customizadas
                        ? 'Inclui regras específicas da sua instituição.'
                        : 'Regras de segurança padrão da plataforma.'}
                    </div>
                  `
                : ''}
        </div>
      </div>
    `;
  }

  private renderDrill(d: DrillState): TemplateResult {
    return html`
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">${d.titulo}</span>
          <button class="xbtn" @click=${() => (this.drill = undefined)}>
            ${this.iconClose}
          </button>
        </div>
        <div class="panel-body">
          ${d.loading
            ? html`<div class="loading">Carregando…</div>`
            : d.error
              ? html`<div class="err">${d.error}</div>`
              : !d.items.length
                ? html`<div class="empty">Nada para mostrar aqui.</div>`
                : d.tipo === 'alunos'
                  ? (d.items as AlunoRow[]).map((a) => this.rowAluno(a))
                  : d.tipo === 'sessoes'
                    ? (d.items as SessaoRow[]).map((s) => this.rowSessao(s))
                    : d.tipo === 'bloqueios'
                      ? (d.items as BloqueioRow[]).map((b) => this.rowBloqueio(b))
                      : (d.items as PerguntaRow[]).map((p) => this.rowPergunta(p))}
        </div>
      </div>
    `;
  }

  private rowAluno(a: AlunoRow): TemplateResult {
    const etapa = [a.etapa, a.ano_serie].filter(Boolean).join(' · ') || '—';
    return html`
      <div class="lrow">
        <div class="lmain">
          <div class="lname">${a.name ?? a.user_id}</div>
          <div class="lsub">${etapa} · último acesso: ${this.fmtDate(a.last_seen_at)}</div>
        </div>
        ${this.statusBadge(a.enrollment_status)}
      </div>
    `;
  }

  private rowSessao(s: SessaoRow): TemplateResult {
    const sub = [s.componente, `${s.mensagens} msg`].filter(Boolean).join(' · ');
    // Clicar abre o detalhe pergunta-a-pergunta da sessão (com status de matéria).
    return html`
      <div
        class="lrow clk"
        @click=${() =>
          this.openDrill(
            `Sessão · ${s.aluno ?? s.user_id}`,
            'perguntas',
            `/parceiro/perguntas?session_id=${encodeURIComponent(s.id)}&limit=200`,
          )}
      >
        <div class="lmain">
          <div class="lname">${s.titulo || s.aluno || s.user_id}</div>
          <div class="lsub">${s.aluno ?? s.user_id} · ${sub}</div>
          ${s.materias.length || s.perguntas_sem_materia
            ? html`<div class="ltags">
                ${s.materias.map((m) => html`<span class="mtag">${m}</span>`)}
                ${s.perguntas_sem_materia
                  ? html`<span class="mtag sem"
                      >${s.perguntas_sem_materia} sem matéria</span
                    >`
                  : ''}
              </div>`
            : ''}
        </div>
        <div class="lright">${this.fmtDateTime(s.created_at)}</div>
      </div>
    `;
  }

  private materiaTag(p: PerguntaRow): TemplateResult {
    if (p.materia_status === 'classificada') {
      return html`<span class="mtag">${p.materia}</span>`;
    }
    if (p.materia_status === 'sem_materia') {
      return html`<span class="mtag sem">sem matéria</span>`;
    }
    return html`<span class="mtag pend">aguardando</span>`;
  }

  private rowPergunta(p: PerguntaRow): TemplateResult {
    const serie = [p.etapa, p.ano_serie].filter(Boolean).join(' · ') || '—';
    const quem = [p.aluno ?? p.user_id, serie].filter(Boolean).join(' · ');
    return html`
      <div class="lrow">
        <div class="lmain">
          <div class="lname">${p.pergunta}</div>
          <div class="lsub">${quem} ${this.materiaTag(p)}</div>
        </div>
        <div class="lright">${this.fmtDateTime(p.created_at)}</div>
      </div>
    `;
  }

  private rowBloqueio(b: BloqueioRow): TemplateResult {
    const serie = [b.etapa, b.ano_serie].filter(Boolean).join(' · ') || 'série não informada';
    // `rotulo` = o QUE barrou (categoria); `detalhe` = o sub-motivo (ex.: categoria
    // do anexo fora de escopo). Mostra o detalhe como um pill ao lado do rótulo.
    return html`
      <div class="lrow">
        <div class="lmain">
          <div class="lname">
            ${b.aluno ?? b.user_id}
            ${b.detalhe
              ? html`<span class="blk-tag">${b.detalhe}</span>`
              : ''}
          </div>
          <div class="lsub">${serie} · ${b.rotulo} · ${b.action}</div>
        </div>
        <div class="lright">${this.fmtDateTime(b.created_at)}</div>
      </div>
    `;
  }
}
